import React, { useState, useEffect } from 'react';
import { 
  Settings, Users, Calendar, LayoutDashboard, Save, Plus, Trash2, Copy, 
  Check, X, Store, Clock, Smartphone, Send, Database, Activity, 
  RefreshCw, Sparkles, Building, BarChart2, DollarSign, ShieldCheck,
  ChevronRight, ChevronLeft, ArrowRight, UserCheck, QrCode, LogOut, Upload, FileText,
  Link as LinkIcon, CheckCircle, UserCircle2, HelpCircle, Edit2,
  ChevronDown, ChevronUp, Search, Filter, Lock, ShieldAlert, Globe, ShoppingBag, Bot, CalendarDays, Package, Award, Megaphone, Gift, BookOpen, Bell, BellRing, KeyRound
} from 'lucide-react';

import { supabase } from './supabaseClient';
import { lookupBusinessForLogin } from './utils/publicBusiness';
import { DebouncedInput } from './components/ui/DebouncedInput';
import BusinessLoginPanel from './components/BusinessLoginPanel';
import { setBusinessLogin, validateBusinessLogin } from './utils/businessLogin';
import { AppointmentsView } from './components/AppointmentsView';
import KanbanBoard from './components/KanbanBoard';
import FinancialReports from './components/FinancialReports';
import InventoryManager from './components/InventoryManager';
import StaffCommissions from './components/StaffCommissions';
import WhatsAppCampaigns from './components/WhatsAppCampaigns';
import LoyaltyCoupons from './components/LoyaltyCoupons';
import ClientHelpCenter from './components/ClientHelpCenter';
import NotificationCenter, { playNotificationSound, triggerSystemNotification } from './components/NotificationCenter';
import AdminCredentialsVault from './components/AdminCredentialsVault';
import ContextualHelpDrawer from './components/ContextualHelpDrawer';
import { DEFAULT_ADMIN_EMAILS, getStoredAdminEmails, saveStoredAdminEmails } from './utils/adminConstants';
import { startInTestMode } from './utils/botTestMode';
import { buildManualOrderRow, isMissingColumnError, isPaidOrder } from './utils/orders';
import { getAllBusinessFeatures } from './utils/allBusinessFeatures';

// Webhook proxy de n8n para la provisión autónoma de WhatsApp (Auto-QR)
const WA_PROVISION_WEBHOOK = "https://flow.seryiads.com/webhook/02c73d5f-816f-475a-9696-6a40b53c97d8";

const TooltipIcon = ({ text }) => (
  <div className="relative group inline-block ml-2 align-middle">
    <HelpCircle size={16} className="text-slate-500 hover:text-seryi-green transition-colors cursor-help" />
    <div className="absolute z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 bg-slate-800 text-xs text-white rounded shadow-lg border border-slate-700 pointer-events-none">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
    </div>
  </div>
);

// Características disponibles por módulo, y sus valores recomendados según el tipo de negocio.
// El usuario puede sobreescribir manualmente cualquiera de estas casillas al crear o editar un negocio.
const getDefaultFeatures = (tipoNegocio) => {
  if (tipoNegocio === 'Restaurante') {
    return { 
      feature_staff: false, feature_services: true, feature_appointments: false, feature_orders: true, feature_menu_media: true, feature_identidad_ia: true, feature_finanzas: true,
      feature_inventory: true, feature_commissions: false, feature_campaigns: true, feature_loyalty: true, feature_prospecting: false, feature_catalogo: false, feature_cotizaciones: false,
      feature_bot_aprende: true, feature_reportes: false, feature_seguimiento: false, feature_contenido: false, feature_ads: false
    };
  }
  return { 
    feature_staff: true, feature_services: true, feature_appointments: true, feature_orders: false, feature_menu_media: true, feature_identidad_ia: true, feature_finanzas: true,
    feature_inventory: true, feature_commissions: true, feature_campaigns: true, feature_loyalty: true, feature_prospecting: false, feature_catalogo: false, feature_cotizaciones: false,
    feature_bot_aprende: true, feature_reportes: false, feature_seguimiento: false, feature_contenido: false, feature_ads: false
  };
};

// Lista maestra de módulos del sistema. Se usa tanto para editar los Planes como para
// habilitar/deshabilitar características por negocio. Para agregar un módulo nuevo en el
// futuro, basta con añadir una entrada aquí (con su columna feature_* en Supabase).
const MODULE_LIST = [
  { key: 'feature_appointments', label: 'Agendador de Citas' },
  { key: 'feature_services', label: 'Catálogo y Precios' },
  { key: 'feature_menu_media', label: 'Menú Multimedia (PDF/Fotos por el bot)', premium: true },
  { key: 'feature_identidad_ia', label: 'Identidad IA' },
  { key: 'feature_staff', label: 'Equipo' },
  { key: 'feature_finanzas', label: 'Finanzas y Reportes' },
  { key: 'feature_orders', label: 'Pedidos' },
  { key: 'feature_inventory', label: 'Inventario & Stock' },
  { key: 'feature_commissions', label: 'Comisiones / Nómina' },
  { key: 'feature_campaigns', label: 'Campañas WhatsApp', premium: true },
  { key: 'feature_loyalty', label: 'Fidelización & Cupones', premium: true },
  // Módulo en prueba: se enciende negocio por negocio; no forma parte de ningún plan todavía.
  { key: 'feature_prospecting', label: 'Prospección Google Maps', premium: true, internal: true },
  { key: 'feature_catalogo', label: 'Catálogo con listas de precios', premium: true, internal: true },
  { key: 'feature_cotizaciones', label: 'Cotizaciones y ventas (PDF, WhatsApp, abonos y cartera)', premium: true, internal: true },
  // Módulos de crecimiento (Marketor): el bot que aprende va en todos los planes; reportes desde PRO; el resto son complementos.
  { key: 'feature_bot_aprende', label: 'Bot que pregunta al dueño y aprende' },
  { key: 'feature_reportes', label: 'Reporte semanal y competencia', premium: true },
  { key: 'feature_seguimiento', label: 'Seguimiento automático de clientes', premium: true },
  { key: 'feature_contenido', label: 'Contenido con IA (créditos)', premium: true },
  { key: 'feature_ads', label: 'Anuncios en Meta (Click-to-WhatsApp)', premium: true }
];

const DEFAULT_MASTER_DB = [];
const DEFAULT_PLANS_DB = [
  { 
    id: 'p1', 
    name: 'Emprendedor', 
    price: 29, 
    price_cop: '119.000 COP',
    max_specialists: 1, 
    max_services: 5, 
    features: 'WhatsApp Básico, Recordatorios, 500 Chats IA/mes', 
    description: 'Ideal para profesionales independientes, barberos o esteticistas individuales.', 
    feature_appointments: true, 
    feature_services: true, 
    feature_menu_media: false, 
    feature_identidad_ia: true, 
    feature_staff: false, 
    feature_finanzas: false, 
    feature_orders: false,
    feature_inventory: true,
    feature_commissions: false,
    feature_campaigns: false,
    feature_loyalty: false,
    feature_bot_aprende: true,
    feature_reportes: false,
    feature_seguimiento: false,
    feature_contenido: false,
    feature_ads: false
  },
  { 
    id: 'p2', 
    name: 'Pro (Recomendado)', 
    price: 59, 
    price_cop: '239.000 COP',
    max_specialists: 5, 
    max_services: 15, 
    features: 'WhatsApp IA, Comisiones Staff, Calculadora Domicilios, 2.000 Chats IA/mes', 
    description: 'Para locales en crecimiento, barberías, restaurantes y salones de belleza.', 
    feature_appointments: true, 
    feature_services: true, 
    feature_menu_media: false, 
    feature_identidad_ia: true, 
    feature_staff: true, 
    feature_finanzas: true, 
    feature_orders: true,
    feature_inventory: true,
    feature_commissions: true,
    feature_campaigns: true,
    feature_loyalty: true,
    feature_bot_aprende: true,
    feature_reportes: true,
    feature_seguimiento: false,
    feature_contenido: false,
    feature_ads: false
  },
  { 
    id: 'p3', 
    name: 'Avanzado / VIP', 
    price: 99, 
    price_cop: '399.000 COP',
    max_specialists: 99, 
    max_services: 99, 
    features: 'Menú Multimedia PDF/Fotos, Conversaciones Ilimitadas, Multi-Sede & API', 
    description: 'Para clínicas médicas, cadenas de restaurantes y franquicias establecidas.', 
    feature_appointments: true, 
    feature_services: true, 
    feature_menu_media: true, 
    feature_identidad_ia: true, 
    feature_staff: true, 
    feature_finanzas: true, 
    feature_orders: true,
    feature_inventory: true,
    feature_commissions: true,
    feature_campaigns: true,
    feature_loyalty: true,
    feature_bot_aprende: true,
    feature_reportes: true,
    feature_seguimiento: false,
    feature_contenido: false,
    feature_ads: false
  }
];

const UNIVERSAL_MULTI_SELECTION_RULE = `
======================================================================
REGLAS UNIVERSALES DE PREGUNTAS PRECISAS Y CAPTURA DE CANTIDADES (TODOS LOS NICHOS)
======================================================================
1. Presenta siempre los catálogos o menús en listas numeradas limpias y continuas (1 a N).

2. Si el cliente escribe SOLO UN NÚMERO (Ej: '3' o '1'):
   - Identifica inmediatamente el NOMBRE EXACTO del producto, servicio o consulta vinculado a ese número.
   - Haz la pregunta directa confirmando el nombre del ítem seleccionado:
     '¡Excelente elección! Elegiste: [NOMBRE DEL PRODUCTO/SERVICIO] ($[PRECIO]). 
     ¿Cuántas cantidades / cupos de este servicio deseas?'

3. Cuando el cliente responde la cantidad (Ej: '1', '2' o 'quiero 2'):
   - Asigna la cantidad, actualiza el resumen del pedido o cita y pregunta el siguiente paso:
     '¿Deseas agregar otro servicio/producto a tu solicitud?
     [1] ➕ Sí, agregar otro servicio
     [2] 🚀 No, continuar con los datos de reserva / pago'

4. Si el cliente escribe desde el inicio combinaciones explícitas (Ej: '3 del 1' o '2 cortes de cabello'):
   - Asume directamente las cantidades, genera el desglose consolidado con total/duración acumulada y pasa a la confirmación de datos de envío o agendamiento.`;

const UNIVERSAL_NO_HALLUCINATION_RULE = `
======================================================================
REGLA UNIVERSAL ESTRICTA DE NO INVENCIÓN Y RECOLECCIÓN DE DATOS:
======================================================================
1. NUNCA inventes, asumas ni autocompletes información del cliente que no haya sido proporcionada explícitamente en el chat (como direcciones 'Calle Falsa 123', nombres ficticios, placas, correos o métodos de pago por defecto).

2. Cuando el flujo avance a la fase de cierre o confirmación (Ej: 'Continuar con los datos' o seleccionar opciones finales):
   - Detén el resumen automático y SOLICITA PRIMERO los datos obligatorios según el tipo de servicio:
     • Si es Pedido/Domicilio: Pide Dirección exacta/Ubicación y Método de Pago.
     • Si es Cita/Reserva: Pide Nombre Completo, Documento/Placa/Mascota (según aplique) y Fecha/Hora deseada.

3. Solo cuando el cliente haya respondido con sus datos REALES:
   - Presenta el resumen consolidado final con todos sus campos completos.
   - Solicita la confirmación explícita ([1] ✅ Confirmar / [2] ✏️ Modificar).

4. EJECUCIÓN OBLIGATORIA DE HERRAMIENTA:
   Para que el pedido o cita sea válido en el sistema, NO BASTA con despedirte del cliente.
   Una vez el cliente responda con '[1]' confirmando los datos, ES OBLIGATORIO Y ESTRICTAMENTE NECESARIO que ejecutes la función / tool del sistema ('Herramienta_Pedido' o 'Herramienta_Agendar').
   ¡NUNCA cierres la conversación asumiendo que el pedido está listo si no has invocado explícitamente la herramienta!

5. REGLA DE DISPONIBILIDAD (MUY IMPORTANTE):
   NUNCA le digas al cliente que "no tienes acceso al sistema de disponibilidad" o que "el equipo debe confirmar". Asume que tienes acceso total. Tú simplemente pide la fecha y hora deseada al cliente, recolecta sus datos y ejecuta la Herramienta_Agendar. El sistema backend se encargará de cruzar los horarios reales y responderá si hay conflicto.`;

// Galería de plantillas de prompts e identidad de marca por nicho comercial (Vinculadas con módulos y productos del sistema)
const PROMPT_TEMPLATES = [
  {
    id: 'fast-food',
    category: 'Restaurante / Comida Rápida',
    icon: '🍔',
    badge: 'Popular',
    tono: 'Cálido, cordial, eficiente y enfocado en ventas.',
    getEstilo: (nombre) => `Eres Keyla, la asistente virtual de ${nombre}. Tu objetivo es tomar pedidos de comida de forma rápida, clara, profesional y sin fricción.

REGLAS DE ATENCIÓN Y FLUJO COMPLETO:
1. Saluda con calidez y pregunta el nombre del cliente si es nuevo.
2. Si piden el menú, ofrece las opciones enumeradas con 1️⃣, 2️⃣, 3️⃣...
3. Si el cliente escribe SOLO UN NÚMERO (Ej: '3'):
   Identifica inmediatamente el NOMBRE y PRECIO del producto y RESPONDE MANDATORIAMENTE:
   "¡Excelente elección! 🍔 Elegiste: [NOMBRE DE PRODUCTO] ($[PRECIO]). ¿Cuántas cantidades deseas de este producto?"
4. Cuando el cliente responda la cantidad (Ej: '1' o '2'), agrégalo al pedido y pregunta si desea agregar otro producto o proceden con los datos de entrega.
5. Solicita dirección y método de pago (Efectivo, Nequi, Daviplata).
6. INVOCA la herramienta 'Herramienta_Pedido'.

${UNIVERSAL_NO_HALLUCINATION_RULE}`,
    featuresSummary: ['Menú y Precios', 'Menú Web / Foto 💎', 'Control de Pedidos'],
    sampleServices: [
      { id: 's_ff1', nombre: 'Hamburguesa Sencilla + Papas', precio: 18900, duracion_minutos: '15', activo: true },
      { id: 's_ff2', nombre: 'Hamburguesa Doble Queso & Tocineta + Papas', precio: 25000, duracion_minutos: '15', activo: true },
      { id: 's_ff3', nombre: 'Hamburguesa Monster Triple Carne', precio: 32000, duracion_minutos: '20', activo: true },
      { id: 's_ff4', nombre: 'Perro Caliente Especial', precio: 14000, duracion_minutos: '10', activo: true },
      { id: 's_ff5', nombre: 'Perro Caliente Suizo con Queso', precio: 18000, duracion_minutos: '12', activo: true },
      { id: 's_ff6', nombre: 'Salchipapa Especial para Compartir', precio: 28000, duracion_minutos: '20', activo: true },
      { id: 's_ff7', nombre: 'Salchipapa Personal', precio: 16000, duracion_minutos: '12', activo: true },
      { id: 's_ff8', nombre: 'Mazorcada Desgranada Especial', precio: 24000, duracion_minutos: '18', activo: true },
      { id: 's_ff9', nombre: 'Alitas BBQ / Picantes x6 Unidades', precio: 22000, duracion_minutos: '15', activo: true },
      { id: 's_ff10', nombre: 'Alitas BBQ / Picantes x12 Unidades', precio: 38000, duracion_minutos: '20', activo: true },
      { id: 's_ff11', nombre: 'Gaseosa / Bebida Personal 400ml', precio: 4500, duracion_minutos: '5', activo: true },
      { id: 's_ff12', nombre: 'Gaseosa Familiar 1.5L', precio: 8500, duracion_minutos: '5', activo: true },
      { id: 's_ff13', nombre: 'Jugo Natural en Agua', precio: 6000, duracion_minutos: '5', activo: true },
      { id: 's_ff14', nombre: 'Jugo Natural en Leche', precio: 7500, duracion_minutos: '5', activo: true },
      { id: 's_ff15', nombre: 'Porción de Papas a la Francesa', precio: 6000, duracion_minutos: '10', activo: true }
    ]
  },
  {
    id: 'barber-shop',
    category: 'Barbería / Grooming Masculino',
    icon: '✂️',
    badge: 'Recomendado',
    tono: 'Moderno, fresco, masculino y atento.',
    getEstilo: (nombre) => `Eres Keyla, la asistente virtual de ${nombre}. Tu objetivo es agendar citas de corte y barbería de forma ordenada y eficiente.

REGLAS DE ATENCIÓN Y FLUJO COMPLETO:
1. Saluda con entusiasmo y tono cercano, preguntando cómo puedes ayudarle.
2. Si el cliente pregunta por servicios, muéstralos numerados (1️⃣, 2️⃣...).
3. Si el cliente elige un servicio (Ej: escribe '1'):
   RESPONDE MANDATORIAMENTE: "¡Excelente elección! 💈 Elegiste: [NOMBRE DEL SERVICIO]. ¿Para qué día y hora te gustaría agendar tu cita?"
4. Si el cliente indica el día y la hora, pregunta si tiene preferencia por algún barbero o especialista.
5. Captura su Nombre completo y haz un resumen de la cita (Servicio, Fecha, Hora, Especialista) para confirmar.
6. Apenas el cliente confirme, INVOCA EXCLUSIVAMENTE la herramienta 'Herramienta_Agendar'.

${UNIVERSAL_NO_HALLUCINATION_RULE}`,
    featuresSummary: ['Catálogo y Precios', 'Agendador de Citas', 'Gestión de Barberos'],
    sampleServices: [
      { id: 's_bs1', nombre: 'Corte de Cabello Tradicional', precio: 25000, duracion_minutos: '30', activo: true },
      { id: 's_bs2', nombre: 'Corte Degradado / Fade Especial', precio: 28000, duracion_minutos: '35', activo: true },
      { id: 's_bs3', nombre: 'Arreglo de Barba Ritual (Toalla Caliente)', precio: 18000, duracion_minutos: '20', activo: true },
      { id: 's_bs4', nombre: 'Combo: Corte + Barba', precio: 38000, duracion_minutos: '45', activo: true },
      { id: 's_bs5', nombre: 'Combo VIP: Corte + Barba + Exfoliación', precio: 48000, duracion_minutos: '60', activo: true },
      { id: 's_bs6', nombre: 'Corte Infantil', precio: 20000, duracion_minutos: '30', activo: true },
      { id: 's_bs7', nombre: 'Pigmentación de Barba', precio: 15000, duracion_minutos: '20', activo: true },
      { id: 's_bs8', nombre: 'Tintura / Colorimetría Masculina', precio: 45000, duracion_minutos: '45', activo: true },
      { id: 's_bs9', nombre: 'Alisado / Keratina Capilar Masculina', precio: 50000, duracion_minutos: '60', activo: true },
      { id: 's_bs10', nombre: 'Mascarilla Negra Facial / Exfoliación', precio: 12000, duracion_minutos: '15', activo: true },
      { id: 's_bs11', nombre: 'Depilación Facial con Cera', precio: 10000, duracion_minutos: '10', activo: true },
      { id: 's_bs12', nombre: 'Diseño de Cejas Masculinas', precio: 8000, duracion_minutos: '10', activo: true },
      { id: 's_bs13', nombre: 'Lavado Capilar + Masaje Capilar', precio: 10000, duracion_minutos: '10', activo: true },
      { id: 's_bs14', nombre: 'Cera Moldeadora / Pomada Capilar', precio: 25000, duracion_minutos: '5', activo: true },
      { id: 's_bs15', nombre: 'Aceite Hidratante para Barba', precio: 30000, duracion_minutos: '5', activo: true }
    ]
  },
  {
    id: 'spa-beauty',
    category: 'Estética / Salón / Spa',
    icon: '💅',
    badge: 'Elegante',
    tono: 'Dulce, atento, pulcro y sumamente servicial.',
    getEstilo: (nombre) => `Eres Keyla, la asistente de servicio de ${nombre}. Tu objetivo es agendar servicios de belleza y spa.

REGLAS DE ATENCIÓN Y FLUJO COMPLETO:
1. Saluda con mucha amabilidad, dulzura y tono cálido.
2. Si la clienta desea conocer los tratamientos, muéstralos numerados.
3. Al elegir un tratamiento (Ej: escribe un número):
   RESPONDE MANDATORIAMENTE: "¡Me encanta esa elección! 💅 Elegiste: [NOMBRE DEL TRATAMIENTO]. ¿Para qué fecha y en qué horario te gustaría tu espacio de relajación?"
4. Tras definir el horario, pregunta si tiene preferencia por alguna de nuestras especialistas.
5. Captura su Nombre completo y haz un resumen de la cita (Tratamiento, Fecha, Hora, Especialista) para que confirme.
6. Apenas la clienta confirme, INVOCA EXCLUSIVAMENTE la herramienta 'Herramienta_Agendar'.

${UNIVERSAL_NO_HALLUCINATION_RULE}`,
    featuresSummary: ['Catálogo de Servicios', 'Galería de Trabajos', 'Agendador de Citas'],
    sampleServices: [
      { id: 's_sb1', nombre: 'Manicura Semi-permanente', precio: 45000, duracion_minutos: '60', activo: true },
      { id: 's_sb2', nombre: 'Pedicura Spa Profunda', precio: 40000, duracion_minutos: '50', activo: true },
      { id: 's_sb3', nombre: 'Combo Manicura + Pedicura Tradicional', precio: 45000, duracion_minutos: '60', activo: true },
      { id: 's_sb4', nombre: 'Uñas en Acrílico / Esculpidas', precio: 90000, duracion_minutos: '90', activo: true },
      { id: 's_sb5', nombre: 'Retoque de Uñas Acrílicas', precio: 60000, duracion_minutos: '60', activo: true },
      { id: 's_sb6', nombre: 'Diseño y Depilación de Cejas con Hilo/Cera', precio: 20000, duracion_minutos: '25', activo: true },
      { id: 's_sb7', nombre: 'Pigmentación / Henna de Cejas', precio: 30000, duracion_minutos: '30', activo: true },
      { id: 's_sb8', nombre: 'Extensión de Pestañas Punto a Punto', precio: 70000, duracion_minutos: '90', activo: true },
      { id: 's_sb9', nombre: 'Extensión de Pestañas Pelo a Pelo', precio: 110000, duracion_minutos: '120', activo: true },
      { id: 's_sb10', nombre: 'Lifting y Laminado de Pestañas', precio: 65000, duracion_minutos: '45', activo: true },
      { id: 's_sb11', nombre: 'Limpieza Facial Profunda con Hidratación', precio: 85000, duracion_minutos: '60', activo: true },
      { id: 's_sb12', nombre: 'Masaje Relajante Corporal (60 min)', precio: 90000, duracion_minutos: '60', activo: true },
      { id: 's_sb13', nombre: 'Masaje Reductivo / Moldeador Sesión', precio: 75000, duracion_minutos: '45', activo: true },
      { id: 's_sb14', nombre: 'Cepillado y Planchado de Cabello', precio: 35000, duracion_minutos: '40', activo: true },
      { id: 's_sb15', nombre: 'Hidratación Capilar Profunda / Repolarización', precio: 60000, duracion_minutos: '50', activo: true }
    ]
  },
  {
    id: 'clinic-health',
    category: 'Clínica / Consultorio Médico',
    icon: '🩺',
    badge: 'Formal',
    tono: 'Respetuoso, profesional, empático y formal.',
    getEstilo: (nombre) => `Eres Keyla, la asistente de atención al paciente de ${nombre}. Tu objetivo es agendar consultas médicas.

REGLAS DE ATENCIÓN Y FLUJO COMPLETO:
1. Saluda con alto respeto, profesionalismo y empatía.
2. Muestra las consultas o especialidades médicas numeradas de forma clara.
3. Cuando el paciente elija la especialidad (Ej: escribe '2'):
   RESPONDE MANDATORIAMENTE: "Comprendido. 🩺 Ha seleccionado: [NOMBRE DE CONSULTA]. ¿Para qué fecha y jornada (mañana o tarde) le gustaría programar su cita?"
4. Solicite el Nombre completo del paciente y su Número de Identificación o Cédula (datos obligatorios).
5. Realice un resumen formal de la cita (Especialidad, Fecha, Hora, Paciente, Documento) y pida su confirmación.
6. Con la confirmación del paciente, INVOCA EXCLUSIVAMENTE la herramienta 'Herramienta_Agendar'.

${UNIVERSAL_NO_HALLUCINATION_RULE}`,
    featuresSummary: ['Catálogo de Procedimientos', 'Agendador de Citas', 'Google Calendar Sync'],
    sampleServices: [
      { id: 's_ch1', nombre: 'Consulta Médica General', precio: 80000, duracion_minutos: '30', activo: true },
      { id: 's_ch2', nombre: 'Consulta Médica de Urgencia', precio: 110000, duracion_minutos: '30', activo: true },
      { id: 's_ch3', nombre: 'Valoración Odontológica', precio: 50000, duracion_minutos: '30', activo: true },
      { id: 's_ch4', nombre: 'Profilaxis y Limpieza Dental Ultrasónica', precio: 120000, duracion_minutos: '45', activo: true },
      { id: 's_ch5', nombre: 'Consulta de Especialista Pediatría', precio: 150000, duracion_minutos: '40', activo: true },
      { id: 's_ch6', nombre: 'Consulta de Especialista Dermatología', precio: 160000, duracion_minutos: '40', activo: true },
      { id: 's_ch7', nombre: 'Consulta de Especialista Ginecología', precio: 160000, duracion_minutos: '40', activo: true },
      { id: 's_ch8', nombre: 'Toma de Muestras / Laboratorio Clínico', precio: 35000, duracion_minutos: '15', activo: true },
      { id: 's_ch9', nombre: 'Electrocardiograma de Control', precio: 70000, duracion_minutos: '20', activo: true },
      { id: 's_ch10', nombre: 'Curación y Procedimiento Menor', precio: 60000, duracion_minutos: '30', activo: true },
      { id: 's_ch11', nombre: 'Retiro de Puntos / Sutura', precio: 30000, duracion_minutos: '20', activo: true },
      { id: 's_ch12', nombre: 'Lavado Otológico / Oídos', precio: 45000, duracion_minutos: '25', activo: true },
      { id: 's_ch13', nombre: 'Citología Vaginal de Control', precio: 40000, duracion_minutos: '20', activo: true },
      { id: 's_ch14', nombre: 'Inyectología / Aplicación de Medicamento', precio: 15000, duracion_minutos: '10', activo: true },
      { id: 's_ch15', nombre: 'Certificado Médico Ocupacional / Salud', precio: 40000, duracion_minutos: '20', activo: true }
    ]
  },
  {
    id: 'dental-care',
    category: 'Odontología / Salud Oral',
    icon: '🦷',
    badge: 'Salud',
    tono: 'Empático, cuidadoso, profesional y claro.',
    getEstilo: (nombre) => `Eres Keyla, la asistente de atención al paciente de ${nombre}. Tu objetivo es agendar citas odontológicas.

REGLAS DE ATENCIÓN Y FLUJO COMPLETO:
1. Saluda con amabilidad, cuidado y tono altamente profesional.
2. Si el paciente desea conocer procedimientos, muéstralos numerados.
3. Cuando el paciente seleccione el procedimiento:
   RESPONDE MANDATORIAMENTE: "Perfecto. 🦷 Ha elegido: [NOMBRE DEL PROCEDIMIENTO]. ¿Para qué día y en qué horario le gustaría ser atendido?"
4. Solicite el Nombre completo y Número de Documento de Identidad del paciente.
5. Envíe un resumen claro de la cita (Procedimiento, Fecha, Hora, Paciente) para que el paciente lo verifique y confirme.
6. Tras recibir la confirmación, INVOCA EXCLUSIVAMENTE la herramienta 'Herramienta_Agendar'.

${UNIVERSAL_NO_HALLUCINATION_RULE}`,
    featuresSummary: ['Procedimientos Orales', 'Agendador de Citas', 'Recordatorios SMS'],
    sampleServices: [
      { id: 's_den1', nombre: 'Valoración Odontológica + Rayos X Diagnóstico', precio: 50000, duracion_minutos: '30', activo: true },
      { id: 's_den2', nombre: 'Limpieza Dental Ultrasónica + Profilaxis', precio: 120000, duracion_minutos: '45', activo: true },
      { id: 's_den3', nombre: 'Blanqueamiento Dental Láser', precio: 250000, duracion_minutos: '60', activo: true },
      { id: 's_den4', nombre: 'Blanqueamiento Dental Casero con Cubetas', precio: 180000, duracion_minutos: '30', activo: true },
      { id: 's_den5', nombre: 'Calza / Obturación en Resina Estética', precio: 90000, duracion_minutos: '40', activo: true },
      { id: 's_den6', nombre: 'Valoración de Ortodoncia / Diseño de Sonrisa', precio: 80000, duracion_minutos: '30', activo: true },
      { id: 's_den7', nombre: 'Control Mensual de Ortodoncia', precio: 70000, duracion_minutos: '30', activo: true },
      { id: 's_den8', nombre: 'Extracción Dental Simple', precio: 90000, duracion_minutos: '40', activo: true },
      { id: 's_den9', nombre: 'Extracción de Cordal / Muela del Juicio', precio: 220000, duracion_minutos: '60', activo: true },
      { id: 's_den10', nombre: 'Endodoncia Unirradicular', precio: 280000, duracion_minutos: '60', activo: true },
      { id: 's_den11', nombre: 'Endodoncia Multirradicular', precio: 380000, duracion_minutos: '90', activo: true },
      { id: 's_den12', nombre: 'Placa Miorelajante para Bruxismo', precio: 160000, duracion_minutos: '30', activo: true },
      { id: 's_den13', nombre: 'Carilla de Resina de Alta Estética', precio: 150000, duracion_minutos: '45', activo: true },
      { id: 's_den14', nombre: 'Prótesis Dental Removible', precio: 450000, duracion_minutos: '60', activo: true },
      { id: 's_den15', nombre: 'Mantenimiento y Pulido de Carillas', precio: 80000, duracion_minutos: '30', activo: true }
    ]
  },
  {
    id: 'veterinary-pet',
    category: 'Veterinaria / Pet Shop',
    icon: '🐾',
    badge: 'Mascotas',
    tono: 'Cálido, alegre y apasionado por los animales.',
    getEstilo: (nombre) => `Eres Keyla, la asistente virtual de ${nombre}. Tu objetivo es agendar citas médicas veterinarias y grooming.

REGLAS DE ATENCIÓN Y FLUJO COMPLETO:
1. Saluda con mucha alegría y demuestra amor por las mascotas.
2. Si preguntan por servicios o grooming, preséntalos de forma numerada.
3. Cuando el cliente seleccione el servicio:
   RESPONDE MANDATORIAMENTE: "¡Genial! 🐾 Has elegido: [NOMBRE DEL SERVICIO]. Para agendar, ¿cómo se llama el peludito y de qué raza es?"
4. Luego de saber el nombre de la mascota, pregunta qué día y hora les queda mejor para visitarnos.
5. Solicita el Nombre del propietario y haz un breve resumen de la cita (Mascota, Servicio, Fecha, Hora) para que confirmen.
6. Apenas te den el 'ok', INVOCA EXCLUSIVAMENTE la herramienta 'Herramienta_Agendar'.

${UNIVERSAL_NO_HALLUCINATION_RULE}`,
    featuresSummary: ['Consultas y Grooming', 'Agendamiento Canino', 'Productos Pet'],
    sampleServices: [
      { id: 's_vet1', nombre: 'Consulta Veterinaria General', precio: 60000, duracion_minutos: '30', activo: true },
      { id: 's_vet2', nombre: 'Consulta Veterinaria de Urgencia', precio: 90000, duracion_minutos: '30', activo: true },
      { id: 's_vet3', nombre: 'Baño y Peluquería Canina Raza Pequeña', precio: 45000, duracion_minutos: '60', activo: true },
      { id: 's_vet4', nombre: 'Baño y Peluquería Canina Raza Mediana/Grande', precio: 65000, duracion_minutos: '90', activo: true },
      { id: 's_vet5', nombre: 'Baño y Peluquería Felina', precio: 55000, duracion_minutos: '60', activo: true },
      { id: 's_vet6', nombre: 'Vacunación Pentavalente Canina', precio: 45000, duracion_minutos: '20', activo: true },
      { id: 's_vet7', nombre: 'Vacunación Rabia Canina / Felina', precio: 30000, duracion_minutos: '15', activo: true },
      { id: 's_vet8', nombre: 'Desparasitación Interna y Externa', precio: 25000, duracion_minutos: '15', activo: true },
      { id: 's_vet9', nombre: 'Limpieza Dental Sin Anestesia', precio: 80000, duracion_minutos: '45', activo: true },
      { id: 's_vet10', nombre: 'Profilaxis Dental Veterinaria con Anestesia', precio: 220000, duracion_minutos: '90', activo: true },
      { id: 's_vet11', nombre: 'Corte de Uñas + Limpieza de Oídos', precio: 15000, duracion_minutos: '15', activo: true },
      { id: 's_vet12', nombre: 'Guardería Canina por Día', precio: 30000, duracion_minutos: '60', activo: true },
      { id: 's_vet13', nombre: 'Hospedaje Canino por Noche', precio: 50000, duracion_minutos: '60', activo: true },
      { id: 's_vet14', nombre: 'Examen de Sangre Cuadro Hemicos', precio: 55000, duracion_minutos: '20', activo: true },
      { id: 's_vet15', nombre: 'Alimento Bulto Premium 3kg', precio: 75000, duracion_minutos: '5', activo: true }
    ]
  },
  {
    id: 'auto-mechanic',
    category: 'Taller Mecánico / Autolavado',
    icon: '🚗',
    badge: 'Técnico',
    tono: 'Directo, técnico, transparente y eficiente.',
    getEstilo: (nombre) => `Eres Keyla, la asesora de servicio de ${nombre}. Tu objetivo es agendar mantenimientos y lavados de vehículos.

REGLAS DE ATENCIÓN Y FLUJO COMPLETO:
1. Saluda de forma directa, técnica y transparente.
2. Presenta los mantenimientos, lavados o servicios numerados.
3. Cuando el cliente elija el servicio (Ej: escribe '1'):
   RESPONDE MANDATORIAMENTE: "¡Entendido! 🚗 Has seleccionado: [NOMBRE DEL SERVICIO]. Por favor, indícame la marca, modelo y placa del vehículo."
4. Luego de registrar los datos del vehículo, pregunta qué día y a qué hora desea traerlo al taller.
5. Solicita el Nombre del propietario y confirma el agendamiento (Vehículo, Placa, Servicio, Fecha y Hora).
6. Al obtener la confirmación del cliente, INVOCA EXCLUSIVAMENTE la herramienta 'Herramienta_Agendar'.

${UNIVERSAL_NO_HALLUCINATION_RULE}`,
    featuresSummary: ['Servicios Vehiculares', 'Agendamiento de Turnos', 'Cotizaciones'],
    sampleServices: [
      { id: 's_aut1', nombre: 'Cambio de Aceite + Filtro Sintético', precio: 110000, duracion_minutos: '45', activo: true },
      { id: 's_aut2', nombre: 'Mantenimiento Preventivo de Frenos', precio: 80000, duracion_minutos: '60', activo: true },
      { id: 's_aut3', nombre: 'Alineación Computarizada y Balanceo', precio: 60000, duracion_minutos: '40', activo: true },
      { id: 's_aut4', nombre: 'Diagnóstico con Escáner Computarizado', precio: 50000, duracion_minutos: '30', activo: true },
      { id: 's_aut5', nombre: 'Sincronización de Motor', precio: 140000, duracion_minutos: '90', activo: true },
      { id: 's_aut6', nombre: 'Cambio de Pastillas de Freno', precio: 70000, duracion_minutos: '45', activo: true },
      { id: 's_aut7', nombre: 'Mantenimiento de Suspensión y Amortiguadores', precio: 120000, duracion_minutos: '90', activo: true },
      { id: 's_aut8', nombre: 'Recarga de Aire Acondicionado Automotriz', precio: 90000, duracion_minutos: '45', activo: true },
      { id: 's_aut9', nombre: 'Lavado General + Encerado Automóvil', precio: 35000, duracion_minutos: '40', activo: true },
      { id: 's_aut10', nombre: 'Lavado General + Encerado Camioneta/SUV', precio: 45000, duracion_minutos: '50', activo: true },
      { id: 's_aut11', nombre: 'Lavado Técnico de Motor', precio: 30000, duracion_minutos: '30', activo: true },
      { id: 's_aut12', nombre: 'Polichado y Brillo de Carrocería', precio: 130000, duracion_minutos: '120', activo: true },
      { id: 's_aut13', nombre: 'Lavado de Cojinería e Interiores', precio: 110000, duracion_minutos: '120', activo: true },
      { id: 's_aut14', nombre: 'Restauración y Pulido de Farolas', precio: 40000, duracion_minutos: '45', activo: true },
      { id: 's_aut15', nombre: 'Peritaje Diagnóstico para Compraventa', precio: 150000, duracion_minutos: '90', activo: true }
    ]
  },
  {
    id: 'fitness-gym',
    category: 'Fitness / Gimnasio / Personalizado',
    icon: '🏋️‍♂️',
    badge: 'Energía',
    tono: 'Motivador, enérgico, disciplinado y proactivo.',
    getEstilo: (nombre) => `Eres Keyla, la asesora de entrenamiento de ${nombre}. Tu objetivo es agendar valoraciones físicas y coordinar clases.

REGLAS DE ATENCIÓN Y FLUJO COMPLETO:
1. Saluda con altísima energía, actitud motivadora y disciplinada.
2. Presenta los planes de entrenamiento, mensualidades o clases numerados.
3. Cuando el usuario elija su plan o clase (Ej: escribe '2'):
   RESPONDE MANDATORIAMENTE: "¡Excelente decisión para tu salud! 🏋️‍♂️ Elegiste: [NOMBRE DE PLAN/CLASE]. ¿Para qué día y hora te gustaría agendar tu primera sesión o valoración?"
4. Pregunta por el Nombre completo de la persona que asistirá y si tiene alguna restricción médica básica.
5. Resume los datos (Clase, Fecha, Hora, Nombre) y pide que confirme su asistencia.
6. Al recibir el 'sí' del usuario, INVOCA EXCLUSIVAMENTE la herramienta 'Herramienta_Agendar'.

${UNIVERSAL_NO_HALLUCINATION_RULE}`,
    featuresSummary: ['Planes y Clases', 'Agendamiento 1 a 1', 'Membresías'],
    sampleServices: [
      { id: 's_fit1', nombre: 'Mensualidad Gimnasio Libre', precio: 90000, duracion_minutos: '60', activo: true },
      { id: 's_fit2', nombre: 'Trimestre Gimnasio Libre', precio: 240000, duracion_minutos: '60', activo: true },
      { id: 's_fit3', nombre: 'Semestre Gimnasio Libre', precio: 420000, duracion_minutos: '60', activo: true },
      { id: 's_fit4', nombre: 'Anualidad Gimnasio Libre', precio: 750000, duracion_minutos: '60', activo: true },
      { id: 's_fit5', nombre: 'Clase Personalizada 1 a 1', precio: 40000, duracion_minutos: '60', activo: true },
      { id: 's_fit6', nombre: 'Paquete x12 Clases Personalizadas', precio: 400000, duracion_minutos: '60', activo: true },
      { id: 's_fit7', nombre: 'Valoración Antropométrica InBody', precio: 40000, duracion_minutos: '30', activo: true },
      { id: 's_fit8', nombre: 'Plan Nutricional Deportivo Personalizado', precio: 80000, duracion_minutos: '45', activo: true },
      { id: 's_fit9', nombre: 'Tiquete X10 Clases Semipersonalizadas', precio: 220000, duracion_minutos: '60', activo: true },
      { id: 's_fit10', nombre: 'Pase por Día / Tiquete Individual', precio: 15000, duracion_minutos: '60', activo: true },
      { id: 's_fit11', nombre: 'Clase de Funcional / Crossfit', precio: 20000, duracion_minutos: '60', activo: true },
      { id: 's_fit12', nombre: 'Clase de Spinning / Indoor Cycling', precio: 20000, duracion_minutos: '50', activo: true },
      { id: 's_fit13', nombre: 'Clase de Yoga / Pilates', precio: 25000, duracion_minutos: '60', activo: true },
      { id: 's_fit14', nombre: 'Sesión de Masaje Deportivo / Descarga', precio: 70000, duracion_minutos: '45', activo: true },
      { id: 's_fit15', nombre: 'Batido de Proteína Post-Entreno', precio: 10000, duracion_minutos: '5', activo: true }
    ]
  },
  {
    id: 'laundry-dry',
    category: 'Lavandería / Tintorería / Calzado',
    icon: '🧺',
    badge: 'Servicio',
    tono: 'Amable, limpio, organizado y rápido.',
    getEstilo: (nombre) => `Eres Keyla, la asistente de servicio de ${nombre}. Tu objetivo es coordinar la recolección de prendas a domicilio.

REGLAS DE ATENCIÓN Y FLUJO COMPLETO:
1. Saluda amablemente, con un enfoque en limpieza y organización.
2. Presenta las tarifas y servicios de recolección numerados.
3. Al seleccionar un servicio (Ej: escribe '1'):
   RESPONDE MANDATORIAMENTE: "¡Entendido! 🧺 Elegiste: [NOMBRE DE SERVICIO]. Por favor, indícame cuántas cargas, pares o prendas vas a enviar."
4. Luego de saber la cantidad, solicita la Dirección exacta de recolección y a qué hora prefieres que pasemos.
5. Pregunta por el método de pago preferido (Nequi, Efectivo) y confirma el total estimado.
6. Tras la confirmación final de los datos de recolección, INVOCA EXCLUSIVAMENTE la herramienta 'Herramienta_Pedido'.

${UNIVERSAL_NO_HALLUCINATION_RULE}`,
    featuresSummary: ['Tarifario por Carga', 'Domicilios', 'Pedidos de Prendas'],
    sampleServices: [
      { id: 's_lau1', nombre: 'Lavado y Secado por Carga (Hasta 8kg)', precio: 22000, duracion_minutos: '30', activo: true },
      { id: 's_lau2', nombre: 'Lavado y Secado por Carga Doble (Hasta 16kg)', precio: 38000, duracion_minutos: '45', activo: true },
      { id: 's_lau3', nombre: 'Lavado en Seco Traje Completo 2 Piezas', precio: 25000, duracion_minutos: '20', activo: true },
      { id: 's_lau4', nombre: 'Lavado en Seco Vestido de Gala / Fiesta', precio: 35000, duracion_minutos: '25', activo: true },
      { id: 's_lau5', nombre: 'Lavado y Restauración de Tenis / Sneakers', precio: 18000, duracion_minutos: '20', activo: true },
      { id: 's_lau6', nombre: 'Lavado de Tenis Gamuza / Cuero', precio: 25000, duracion_minutos: '25', activo: true },
      { id: 's_lau7', nombre: 'Lavado de Edredón Sencillo / Semidoble', precio: 24000, duracion_minutos: '30', activo: true },
      { id: 's_lau8', nombre: 'Lavado de Edredón Doble / King Size', precio: 32000, duracion_minutos: '30', activo: true },
      { id: 's_lau9', nombre: 'Planchado por Docena de Prendas', precio: 30000, duracion_minutos: '40', activo: true },
      { id: 's_lau10', nombre: 'Lavado de Chaqueta de Plumas / Térmica', precio: 22000, duracion_minutos: '20', activo: true },
      { id: 's_lau11', nombre: 'Lavado de Chaqueta de Cuero / Gamuza', precio: 45000, duracion_minutos: '30', activo: true },
      { id: 's_lau12', nombre: 'Lavado de Cortinas por Metro', precio: 15000, duracion_minutos: '20', activo: true },
      { id: 's_lau13', nombre: 'Tinturado y Renovación de Color en Prendas', precio: 35000, duracion_minutos: '30', activo: true },
      { id: 's_lau14', nombre: 'Servicio de Costura / Dobladillo de Pantalón', precio: 12000, duracion_minutos: '15', activo: true },
      { id: 's_lau15', nombre: 'Domicilio Recolección y Entrega', precio: 6000, duracion_minutos: '30', activo: true }
    ]
  },
  {
    id: 'real-estate',
    category: 'Inmobiliaria / Agencias de Arriendo',
    icon: '🏠',
    badge: 'Bienes Raíces',
    tono: 'Corporativo, seguro, confiable y formal.',
    getEstilo: (nombre) => `Eres Keyla, la asesora inmobiliaria de ${nombre}. Tu objetivo es cualificar interesados y agendar visitas presenciales.

REGLAS DE ATENCIÓN Y FLUJO COMPLETO:
1. Saluda de manera corporativa, segura y confiable.
2. Presenta los servicios inmobiliarios, avalúos o opciones de visita numerados.
3. Cuando el cliente elija el servicio (Ej: escribe '1'):
   RESPONDE MANDATORIAMENTE: "¡Excelente! 🏠 Ha seleccionado: [NOMBRE DE SERVICIO]. ¿Qué día y en qué horario le gustaría que agendemos la cita o reunión?"
4. Solicita el Nombre completo del interesado y su Número de Cédula.
5. Haz un breve resumen de la visita/reunión (Servicio, Fecha, Hora, Nombre) para pedir su validación.
6. Una vez que el cliente confirme, INVOCA EXCLUSIVAMENTE la herramienta 'Herramienta_Agendar'.

${UNIVERSAL_NO_HALLUCINATION_RULE}`,
    featuresSummary: ['Visitas Inmobiliarias', 'Avalúos', 'Agendamiento'],
    sampleServices: [
      { id: 's_re1', nombre: 'Agendamiento de Visita a Inmueble', precio: 0, duracion_minutos: '45', activo: true },
      { id: 's_re2', nombre: 'Avalúo Comercial Urbano de Propiedad', precio: 180000, duracion_minutos: '60', activo: true },
      { id: 's_re3', nombre: 'Avalúo Comercial Rural / Campestre', precio: 250000, duracion_minutos: '90', activo: true },
      { id: 's_re4', nombre: 'Asesoría Consignación en Arriendo', precio: 0, duracion_minutos: '30', activo: true },
      { id: 's_re5', nombre: 'Asesoría Consignación en Venta', precio: 0, duracion_minutos: '30', activo: true },
      { id: 's_re6', nombre: 'Elaboración de Contrato de Arrendamiento', precio: 120000, duracion_minutos: '45', activo: true },
      { id: 's_re7', nombre: 'Estudio de Póliza / Siniestros Inmobiliarios', precio: 80000, duracion_minutos: '30', activo: true },
      { id: 's_re8', nombre: 'Fotografía Profesional e Inmueble 360°', precio: 150000, duracion_minutos: '60', activo: true },
      { id: 's_re9', nombre: 'Video Promocional HD de Inmueble', precio: 200000, duracion_minutos: '60', activo: true },
      { id: 's_re10', nombre: 'Publicación VIP en Portales Inmobiliarios', precio: 90000, duracion_minutos: '30', activo: true },
      { id: 's_re11', nombre: 'Asesoría Crédito Hipotecario / Leasing', precio: 0, duracion_minutos: '40', activo: true },
      { id: 's_re12', nombre: 'Levantamiento de Planos de Inmueble', precio: 160000, duracion_minutos: '60', activo: true },
      { id: 's_re13', nombre: 'Inspección y Entrega de Llaves con Inventario', precio: 70000, duracion_minutos: '45', activo: true },
      { id: 's_re14', nombre: 'Asesoría Jurídica Inmobiliaria', precio: 130000, duracion_minutos: '45', activo: true },
      { id: 's_re15', nombre: 'Administración Mensual de Inmueble', precio: 0, duracion_minutos: '30', activo: true }
    ]
  },
  {
    id: 'services-general',
    category: 'General / Asesoría / Servicios',
    icon: '💼',
    badge: 'Corporativo',
    tono: 'Ejecutivo, enfocado en solución de problemas y orientado a resultados.',
    getEstilo: (nombre) => `Eres Keyla, la asesora comercial virtual de ${nombre}. Tu objetivo es cualificar prospectos y agendar sesiones de consultoría o reunión inicial.

REGLAS DE ATENCIÓN Y FLUJO COMPLETO:
1. Saluda de forma ejecutiva, enfocándote en la solución de problemas.
2. Pregunta en qué área o proyecto requiere apoyo y presenta los paquetes de consultoría.
3. Cuando el prospecto elija un paquete (Ej: escribe '1'):
   RESPONDE MANDATORIAMENTE: "Perfecto. 💼 Ha elegido: [NOMBRE DEL SERVICIO]. Para avanzar, ¿para qué día y a qué hora le gustaría agendar nuestra reunión inicial o llamada de diagnóstico?"
4. Haz 1 o 2 preguntas de cualificación (Ej: "¿Cuál es el tamaño de la empresa o tipo de proyecto?").
5. Solicita Nombre completo y Empresa, y confirma los datos de la reunión.
6. Apenas el prospecto confirme la cita, INVOCA EXCLUSIVAMENTE la herramienta 'Herramienta_Agendar'.`,
    featuresSummary: ['Servicios y Paquetes', 'Agendador de Sesiones', 'Finanzas y Reportes'],
    sampleServices: [
      { id: 's_sg1', nombre: 'Sesión de Diagnóstico / Asesoría (1 Hora)', precio: 120000, duracion_minutos: '60', activo: true },
      { id: 's_sg2', nombre: 'Asesoría Estratégica Mensual Base', precio: 350000, duracion_minutos: '60', activo: true },
      { id: 's_sg3', nombre: 'Servicio Técnico / Revisión Inicial', precio: 50000, duracion_minutos: '45', activo: true },
      { id: 's_sg4', nombre: 'Mantenimiento Preventivo / Soporte Estándar', precio: 80000, duracion_minutos: '60', activo: true },
      { id: 's_sg5', nombre: 'Soporte Técnico Correctivo Hora', precio: 70000, duracion_minutos: '60', activo: true },
      { id: 's_sg6', nombre: 'Auditoría y Diagnóstico Operativo', precio: 250000, duracion_minutos: '90', activo: true },
      { id: 's_sg7', nombre: 'Capacitación Presencial / Virtual Equipo (2 Horas)', precio: 300000, duracion_minutos: '120', activo: true },
      { id: 's_sg8', nombre: 'Configuración e Implementación de Software', precio: 200000, duracion_minutos: '90', activo: true },
      { id: 's_sg9', nombre: 'Diseño de Propuesta / Cotización Especializada', precio: 0, duracion_minutos: '30', activo: true },
      { id: 's_sg10', nombre: 'Elaboración de Documento / Reporte Técnico', precio: 150000, duracion_minutos: '60', activo: true },
      { id: 's_sg11', nombre: 'Asesoría Legal / Financiera Inicial', precio: 140000, duracion_minutos: '60', activo: true },
      { id: 's_sg12', nombre: 'Revisión de Contratos / Términos', precio: 110000, duracion_minutos: '45', activo: true },
      { id: 's_sg13', nombre: 'Visita Técnica de Inspección en Sitio', precio: 90000, duracion_minutos: '60', activo: true },
      { id: 's_sg14', nombre: 'Plan de Acompañamiento VIP Trimestral', precio: 950000, duracion_minutos: '60', activo: true },
      { id: 's_sg15', nombre: 'Horas de Consultoría Adicional', precio: 100000, duracion_minutos: '60', activo: true }
    ]
  }
];

// Funciones auxiliares para guardar la activación de módulos por negocio de forma infalible en localStorage y Postgres
const getStoredFeatureOverrides = () => {
  try {
    const raw = localStorage.getItem('keylacrm_feature_overrides');
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

const safeSetLocalStorage = (key, value) => {
  try {
    let cleanedValue = value;
    if (key === 'keylacrm_master_db' && Array.isArray(value)) {
      cleanedValue = value.map(b => {
        if (b.menu_pdf_url && typeof b.menu_pdf_url === 'string' && b.menu_pdf_url.startsWith('data:')) {
          return { ...b, menu_pdf_url: '' };
        }
        return b;
      });
    }
    localStorage.setItem(key, typeof cleanedValue === 'string' ? cleanedValue : JSON.stringify(cleanedValue));
  } catch (err) {
    console.warn(`[LocalStorage Safe Warning] Se omitió guardar ${key} en caché local por límite de memoria del navegador.`, err.message);
  }
};

const setStoredFeatureOverride = (businessId, field, value) => {
  try {
    // Si es un data URL gigante (>300KB), se omite de localStorage.
    // Las imágenes comprimidas con canvas (~100KB) Sí se preservan.
    if (typeof value === 'string' && value.startsWith('data:') && value.length > 300000) {
      return;
    }
    const overrides = getStoredFeatureOverrides();
    const strId = String(businessId);
    if (!overrides[strId]) overrides[strId] = {};
    overrides[strId][field] = value;
    safeSetLocalStorage('keylacrm_feature_overrides', overrides);
  } catch (e) {
    console.warn("Error en setStoredFeatureOverride:", e);
  }
};

const parseFeaturesFromBusiness = (b) => {
  if (!b) return {};
  let parsed = {};
  if (b.estilo && typeof b.estilo === 'string' && b.estilo.includes('__FEATURES__:')) {
    try {
      const parts = b.estilo.split('__FEATURES__:');
      parsed = JSON.parse(parts[1].trim());
    } catch (e) {
      console.warn("Error parsing features from estilo", e);
    }
  }
  return {
    ...parsed,
    ...(b.feature_menu_media !== undefined ? { feature_menu_media: b.feature_menu_media } : {}),
    ...(b.feature_staff !== undefined ? { feature_staff: b.feature_staff } : {}),
    ...(b.feature_services !== undefined ? { feature_services: b.feature_services } : {}),
    ...(b.feature_appointments !== undefined ? { feature_appointments: b.feature_appointments } : {}),
    ...(b.feature_orders !== undefined ? { feature_orders: b.feature_orders } : {}),
    ...(b.feature_identidad_ia !== undefined ? { feature_identidad_ia: b.feature_identidad_ia } : {}),
    ...(b.feature_finanzas !== undefined ? { feature_finanzas: b.feature_finanzas } : {}),
    ...(b.feature_prospecting !== undefined ? { feature_prospecting: b.feature_prospecting } : {}),
    ...(b.feature_catalogo !== undefined ? { feature_catalogo: b.feature_catalogo } : {}),
    ...(b.feature_cotizaciones !== undefined ? { feature_cotizaciones: b.feature_cotizaciones } : {}),
    ...(b.feature_bot_aprende !== undefined ? { feature_bot_aprende: b.feature_bot_aprende } : {}),
    ...(b.feature_reportes !== undefined ? { feature_reportes: b.feature_reportes } : {}),
    ...(b.feature_seguimiento !== undefined ? { feature_seguimiento: b.feature_seguimiento } : {}),
    ...(b.feature_contenido !== undefined ? { feature_contenido: b.feature_contenido } : {}),
    ...(b.feature_ads !== undefined ? { feature_ads: b.feature_ads } : {}),
    ...(b.bot_activo !== undefined ? { bot_activo: b.bot_activo } : {}),
    menu_pdf_url: parsed.menu_pdf_url !== undefined ? parsed.menu_pdf_url : (b.menu_pdf_url || ""),
    menu_link_url: parsed.menu_link_url !== undefined ? parsed.menu_link_url : "",
    menu_text_list: parsed.menu_text_list !== undefined ? parsed.menu_text_list : "",
    muted_phones: Array.isArray(parsed.muted_phones) ? parsed.muted_phones : [],
    muted_phones_metadata: parsed.muted_phones_metadata || {}
  };
};

const getCleanEstilo = (estiloText) => {
  if (!estiloText || typeof estiloText !== 'string') return '';
  return estiloText.split('__FEATURES__:')[0].split('[REGLAS ADICIONALES DEL NEGOCIO]')[0].trim();
};

const getDirectMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (trimmed.startsWith('data:')) return trimmed;
  const match = trimmed.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (match && match[1]) {
    return `https://lh3.googleusercontent.com/d/${match[1]}`;
  }
  return trimmed;
};

const generateTextListFromServices = (services) => {
  if (!services || !Array.isArray(services) || services.length === 0) return "";
  return services.map(s => {
    let icon = "🍔";
    const nameLower = (s.nombre || '').toLowerCase();
    if (nameLower.includes('coca') || nameLower.includes('bebida') || nameLower.includes('jugo') || nameLower.includes('agua')) icon = "🥤";
    else if (nameLower.includes('perro') || nameLower.includes('hot dog')) icon = "🌭";
    else if (nameLower.includes('papa') || nameLower.includes('fries')) icon = "🍟";
    else if (nameLower.includes('pizza')) icon = "🍕";
    return `${icon} ${s.nombre} - $${Number(s.precio || 0).toLocaleString('es-CO')}`;
  }).join('\n');
};

const openMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return;
  const targetUrl = getDirectMediaUrl(url);
  if (targetUrl.startsWith('data:')) {
    const win = window.open('', '_blank');
    if (win) {
      if (targetUrl.startsWith('data:image/')) {
        win.document.write(`
          <!DOCTYPE html>
          <html>
            <head><title>Vista previa de la Carta - Keyla CRM</title></head>
            <body style="margin:0; background:#0f172a; display:flex; justify-content:center; align-items:center; min-height:100vh;">
              <img src="${targetUrl}" style="max-width:95%; max-height:95vh; border-radius:16px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.7);" />
            </body>
          </html>
        `);
      } else if (targetUrl.startsWith('data:application/pdf')) {
        win.document.write(`
          <!DOCTYPE html>
          <html>
            <head><title>Vista previa de PDF - Keyla CRM</title></head>
            <body style="margin:0; background:#0f172a;">
              <iframe src="${targetUrl}" style="width:100vw; height:100vh; border:none;"></iframe>
            </body>
          </html>
        `);
      } else {
        win.document.write(`
          <!DOCTYPE html>
          <html>
            <head><title>Archivo del Menú - Keyla CRM</title></head>
            <body style="margin:0; background:#0f172a; color:white; font-family:sans-serif; padding:40px; text-align:center;">
              <h2>Archivo del Menú Cargado</h2>
              <a href="${targetUrl}" download="menu_digital" style="display:inline-block; padding:12px 24px; background:#4f46e5; color:white; border-radius:12px; text-decoration:none; font-weight:bold;">Descargar Documento</a>
            </body>
          </html>
        `);
      }
    }
  } else {
    window.open(targetUrl, '_blank');
  }
};

const compressImageFile = (file) => {
  return new Promise((resolve) => {
    if (!file || !file.type.startsWith('image/')) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const maxDim = 1200;

        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
        resolve(dataUrl);
      };
      img.onerror = () => resolve(null);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
};

const buildEstiloWithFeatures = (cleanEstiloText, featuresObj) => {
  const clean = (cleanEstiloText || '').split('__FEATURES__:')[0].split('[REGLAS ADICIONALES DEL NEGOCIO]')[0].trim();
  let result = clean;
  if (featuresObj.custom_instructions && featuresObj.custom_instructions.trim() !== '') {
    result += `\n\n[REGLAS ADICIONALES DEL NEGOCIO]\n${featuresObj.custom_instructions.trim()}\n\n`;
  }
  return `${result}__FEATURES__:${JSON.stringify(featuresObj)}`;
};

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSignUp, setIsSignUp] = useState(window.location.pathname.startsWith('/signup'));
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [signupData, setSignupData] = useState({ name: '', businessName: '', email: '', phone: '', password: '', acceptsTerms: false });
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  
  // Configuración de Perfil: 'master' (Sergio como dueño del SaaS) o 'client' (Ver un local específico)
  const [userRole, setUserRole] = useState('master'); 
  const initialTab = window.location.pathname.startsWith('/master/') 
    ? window.location.pathname.replace('/master/', 'master-')
    : 'master-dashboard';
  const [activeTab, setActiveTab] = useState(initialTab);

  const [masterDb, setMasterDb] = useState(DEFAULT_MASTER_DB);
  const [adminEmails, setAdminEmails] = useState(() => getStoredAdminEmails());

  useEffect(() => {
    saveStoredAdminEmails(adminEmails);
  }, [adminEmails]);

  const isSuperAdmin = adminEmails.some(e => e.toLowerCase().trim() === currentUserEmail);
  const isDemoAccount = currentUserEmail && currentUserEmail.endsWith('demo@seryiads.com') && !isSuperAdmin;

  // ID del negocio que estamos viendo/editando actualmente
  const [activeBusinessId, setActiveBusinessId] = useState("1");
  
  const activeBusinessMatch = activeBusinessId ? masterDb.find(b => String(b.id) === String(activeBusinessId)) : null;
  const isTrialAccount = activeBusinessMatch && (activeBusinessMatch.plan === 'free' || activeBusinessMatch.subscription_status === 'trial') && !isSuperAdmin;


  const [isTemplatesModalOpen, setIsTemplatesModalOpen] = useState(false);
  const [isManualOpen, setIsManualOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);

  const [isImpersonating, setIsImpersonating] = useState(false);
  
  const [newAdminEmailInput, setNewAdminEmailInput] = useState('');
  const [newMasterPasswordInput, setNewMasterPasswordInput] = useState('');
  const [paymentProofUrlInput, setPaymentProofUrlInput] = useState('');

  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const [activeTemplates, setActiveTemplates] = useState(() => {
    // Las funciones (getEstilo) no sobreviven un round-trip por JSON.stringify/localStorage,
    // así que NUNCA confiamos en el array completo guardado. En vez de eso, partimos siempre
    // de las plantillas reales del código (con su getEstilo intacto) y solo aplicamos el texto
    // personalizado que un admin haya editado explícitamente alguna vez para esa plantilla.
    let customOverrides = {};
    const saved = localStorage.getItem('keylacrm_custom_templates');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          parsed.forEach(t => {
            if (t && t.id && typeof t.estiloText === 'string' && t.estiloText.trim()) {
              customOverrides[t.id] = t.estiloText;
            }
          });
        }
      } catch (e) {}
    }
    return PROMPT_TEMPLATES.map(base => {
      const customText = customOverrides[base.id];
      if (customText) {
        return { ...base, estiloText: customText, getEstilo: () => customText };
      }
      return base;
    });
  });

  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDraftText, setTemplateDraftText] = useState('');

  useEffect(() => {
    localStorage.setItem('keylacrm_custom_templates', JSON.stringify(activeTemplates));
  }, [activeTemplates]);
  const [plansDb, setPlansDb] = useState(DEFAULT_PLANS_DB);
  const [isLoadingDb, setIsLoadingDb] = useState(true);

  // Respaldo de seguridad anti-bloqueo: si después de 5 segundos sigue en "Cargando Sistema...", liberar interfaz
  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      setIsCheckingAuth(prev => {
        if (prev) console.warn("[Safety Check] Liberando isCheckingAuth por timeout de 5s.");
        return false;
      });
      setIsLoadingDb(prev => {
        if (prev) console.warn("[Safety Check] Liberando isLoadingDb por timeout de 5s.");
        return false;
      });
    }, 5000);
    return () => clearTimeout(safetyTimeout);
  }, []);
  const [isUploadingMenu, setIsUploadingMenu] = useState(false);

  const handleMenuFileUpload = async (file) => {
    if (!file) return;
    if (file.type && file.type === 'application/pdf') {
      showNotification("La Opción 1 es solo para fotos de la carta (JPG, PNG). Para archivos PDF o catálogos web, utiliza la Opción 2.", "warning");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showNotification("La foto supera el límite máximo de 10 MB.", "error");
      return;
    }
    setIsUploadingMenu(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `menu_${activeBusinessId}_${Date.now()}.${fileExt}`;
      const filePath = `menus/${fileName}`;

      // 1. Intentar subir a Supabase Storage CDN
      let { error: uploadError } = await supabase.storage
        .from('menu-files')
        .upload(filePath, file, { upsert: true });

      if (!uploadError) {
        const { data: { publicUrl } } = supabase.storage
          .from('menu-files')
          .getPublicUrl(filePath);

        updateActiveBusinessField('menu_pdf_url', publicUrl);
        await saveBusinessSettings({ menu_pdf_url: publicUrl });
        showNotification("¡Foto de la carta subida y guardada exitosamente en la nube!", "success");
        return;
      }

      console.warn("Storage CDN no disponible. Usando compresión Canvas:", uploadError.message);

      // 2. Compresión ultra rápida con Canvas HTML5 (pasa de 5MB a ~100KB JPEG)
      if (file.type && file.type.startsWith('image/')) {
        const compressedDataUrl = await compressImageFile(file);
        if (compressedDataUrl) {
          updateActiveBusinessField('menu_pdf_url', compressedDataUrl);
          await saveBusinessSettings({ menu_pdf_url: compressedDataUrl });
          showNotification("¡Foto de la carta optimizada y guardada exitosamente!", "success");
          return;
        }
      }

      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (e) => reject(e);
        reader.readAsDataURL(file);
      });

      updateActiveBusinessField('menu_pdf_url', dataUrl);
      await saveBusinessSettings({ menu_pdf_url: dataUrl });
      showNotification("¡Foto de la carta guardada exitosamente!", "success");
    } catch (err) {
      console.error("Error al guardar foto del menú:", err);
      showNotification("Error al procesar la foto. Te sugerimos pegar un enlace en la Opción 2.", "error");
    } finally {
      setIsUploadingMenu(false);
    }
  };

  const fetchDb = async () => {
    try {
      const { data: bData, error: bErr } = await supabase.from('businesses').select('*, staff(*), services(*), appointments(*), orders(*)');
      const { data: pData, error: pErr } = await supabase.from('plans').select('*');
      const overrides = getStoredFeatureOverrides();

      if (bData && !bErr && bData.length > 0) {
        const mergedBusinesses = bData.map(b => {
          const bOverrides = overrides[String(b.id)] || overrides[b.id] || {};
          const featuresFromEstilo = parseFeaturesFromBusiness(b);
          return {
            ...b,
            estilo_clean: getCleanEstilo(b.estilo),
            ...featuresFromEstilo,
            ...bOverrides,
            // Las colecciones relacionales siempre provienen de Supabase Postgres
            services: Array.isArray(b.services) ? b.services : [],
            staff: Array.isArray(b.staff) ? b.staff : [],
            appointments: Array.isArray(b.appointments) ? b.appointments : [],
            orders: Array.isArray(b.orders) ? b.orders : []
          };
        });
        mergedBusinesses.sort((a, b) => {
          const isA = (a.nombre_negocio || '').toLowerCase().includes('total emprender');
          const isB = (b.nombre_negocio || '').toLowerCase().includes('total emprender');
          if (isA && !isB) return -1;
          if (!isA && isB) return 1;
          return (a.nombre_negocio || '').localeCompare(b.nombre_negocio || '');
        });
        setMasterDb(mergedBusinesses);

        // Mantenimiento seguro del negocio activo sin reseteos bruscos
        setActiveBusinessId(prev => {
          if (!prev || prev === '1' || !bData.some(b => String(b.id) === String(prev))) {
            return String(bData[0].id);
          }
          return String(prev);
        });
      }
      if (pData && !pErr) setPlansDb(pData);
    } catch (err) {
      console.error("Error fetching DB", err);
    } finally {
      setIsLoadingDb(false);
    }
  };

  useEffect(() => {
    fetchDb();
  }, []);

  // Polling silencioso en segundo plano solo para refrescar pedidos y citas nuevas,
  // totalmente inofensivo para la pestaña o menú seleccionado.
  useEffect(() => {
    // Si el usuario está editando formularios o en pestañas con borradores, no pollear
    const tabsWithDrafts = ['config', 'services', 'staff', 'master-plans', 'master-profile', 'menu-media'];
    if (tabsWithDrafts.includes(activeTab)) return;
    const interval = setInterval(() => { fetchDb(); }, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Obtener negocio activo
  const activeBusiness = masterDb.find(b => String(b.id) === String(activeBusinessId)) || masterDb[0] || { id: 'dummy', nombre_negocio: 'Sin negocios', tipo_negocio: '', id_whatsapp: '', horario: '', tono: '', estilo: '', webhook_n8n: '', google_calendar_id: '', plan: '', monto_suscripcion: 0, activo: false, staff: [], services: [], appointments: [], orders: [] };
  const isRestaurant = activeBusiness.tipo_negocio === 'Restaurante';
  // Si las columnas feature_* aún no existen en la BD (undefined), usamos el comportamiento anterior por tipo de negocio como respaldo.
  const showStaffTab = activeBusiness.feature_staff !== undefined ? activeBusiness.feature_staff : !isRestaurant;
  const showServicesTab = activeBusiness.feature_services !== undefined ? activeBusiness.feature_services : true;
  const showAppointmentsTab = activeBusiness.feature_appointments !== undefined ? activeBusiness.feature_appointments : !isRestaurant;
  const showOrdersTab = activeBusiness.feature_orders !== undefined ? activeBusiness.feature_orders : isRestaurant;
  const showIdentidadTab = activeBusiness.feature_identidad_ia !== undefined ? activeBusiness.feature_identidad_ia : true;
  const showFinanzasTab = activeBusiness.feature_finanzas !== undefined ? activeBusiness.feature_finanzas : true;
  const showMenuMediaTab = activeBusiness.feature_menu_media !== undefined ? activeBusiness.feature_menu_media : true;
  const showInventoryTab = activeBusiness.feature_inventory !== undefined ? activeBusiness.feature_inventory : true;
  const showCommissionsTab = activeBusiness.feature_commissions !== undefined ? activeBusiness.feature_commissions : showStaffTab;
  const showCampaignsTab = activeBusiness.feature_campaigns !== undefined ? activeBusiness.feature_campaigns : true;
  const showLoyaltyTab = activeBusiness.feature_loyalty !== undefined ? activeBusiness.feature_loyalty : true;
  const showBotEnabled = activeBusiness.bot_activo !== undefined ? activeBusiness.bot_activo : true;

  // States para nuevos registros en el negocio activo
  const [newStaff, setNewStaff] = useState({ name: "", specialty: "", google_calendar_id: "", pin_code: "", telefono: "", comision_porcentaje: 50, available: true });
  const [newAppt, setNewAppt] = useState({ client: "", phone: "", service: "Corte de Cabello", time: "", date: "", staffAssigned: "" });
  const [newService, setNewService] = useState({ nombre: "", precio: "", duracion_minutos: "30", activo: true });
  const [manualMutePhone, setManualMutePhone] = useState("");
  const [manualMuteName, setManualMuteName] = useState("");
  const [editingMutedPhone, setEditingMutedPhone] = useState(null);
  const [editMutedPhoneName, setEditMutedPhoneName] = useState("");

  const [mutedPhoneNames, setMutedPhoneNames] = useState({});
  const [mutedPhoneSearch, setMutedPhoneSearch] = useState("");

  const [selectedCountryPrefix, setSelectedCountryPrefix] = useState("57");
  const [estiloTextDraft, setEstiloTextDraft] = useState("");

  const [templateConfirmModal, setTemplateConfirmModal] = useState(null);

  // ESTADO Y LÓGICA DE CENTRO DE NOTIFICACIONES Y ALERTAS PUSH (KEYLA v12)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [showHelpDrawer, setShowHelpDrawer] = useState(false);
  const [notifications, setNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem('seryi_notifications_history');
      return saved
        ? JSON.parse(saved)
        : [
            {
              id: 1,
              type: 'system',
              title: '⚠️¡ Keyla CRM Conectado',
              body: 'Centro de Alertas Push en tiempo real y sintetizador de sonido activo.',
              timestamp: 'Hoy',
              read: true
            }
          ];
    } catch {
      return [];
    }
  });

  const handleAddNotification = (newNotif) => {
    setNotifications((prev) => {
      const updated = [newNotif, ...prev].slice(0, 30);
      try {
        localStorage.setItem('seryi_notifications_history', JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  };

  const handleMarkAllRead = () => {
    setNotifications((prev) => {
      const updated = prev.map((n) => ({ ...n, read: true }));
      try {
        localStorage.setItem('seryi_notifications_history', JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  };

  const handleClearAllNotifications = () => {
    setNotifications([]);
    try {
      localStorage.removeItem('seryi_notifications_history');
    } catch (e) {}
  };

  const handleNotificationClick = (item) => {
    setNotifications((prev) => prev.map((n) => (n.id === item.id ? { ...n, read: true } : n)));
    if (item.type === 'appointment') {
      setActiveTab('citas');
    } else if (item.type === 'order') {
      setActiveTab('cocina');
    } else if (item.type === 'payment') {
      setActiveTab('client_account');
    }
    setIsNotificationsOpen(false);
  };

  // Suscripción Realtime a nuevas citas y pedidos en Supabase para emitir alerta sonora y push en vivo
  useEffect(() => {
    if (!activeBusinessId || activeBusinessId === 'dummy') return;

    const appointmentsChannel = supabase
      .channel(`public:appointments:${activeBusinessId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'appointments',
          filter: `business_id=eq.${activeBusinessId}`
        },
        (payload) => {
          const newAppt = payload.new;
          const title = '🗓️ ¡Nueva Cita Agendada!';
          const body = `Cliente ${newAppt.client_name || 'Nuevo'} • ${newAppt.service_name || 'Servicio'} para ${newAppt.date || 'hoy'} a las ${newAppt.time || ''}`;
          handleAddNotification({
            id: Date.now() + Math.random(),
            type: 'appointment',
            title,
            body,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            read: false,
            actionLabel: 'Ver en Agenda'
          });
          triggerSystemNotification(title, body, 'appointment');
          fetchDb();
        }
      )
      .subscribe();

    const ordersChannel = supabase
      .channel(`public:orders:${activeBusinessId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders',
          filter: `business_id=eq.${activeBusinessId}`
        },
        (payload) => {
          const newOrd = payload.new;
          const title = '🍔 ¡Nuevo Pedido de WhatsApp!';
          const body = `Orden #${newOrd.order_number || ''} • Total: $${Number(newOrd.total || 0).toLocaleString('es-CO')} COP`;
          handleAddNotification({
            id: Date.now() + Math.random(),
            type: 'order',
            title,
            body,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            read: false,
            actionLabel: 'Ver en Cocina'
          });
          triggerSystemNotification(title, body, 'order');
          fetchDb();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(appointmentsChannel);
      supabase.removeChannel(ordersChannel);
    };
  }, [activeBusinessId]);

  // Estados y manejador para Filtros y Pedidos Manuales (Req #7)
  const [orderFilterTab, setOrderFilterTab] = useState('all');
  const [showManualOrderModal, setShowManualOrderModal] = useState(false);
  const [manualOrderForm, setManualOrderForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_address: '',
    items_text: '',
    total: '0',
    source: 'manual'
  });

  const handleCreateManualOrder = async (e) => {
    e.preventDefault();
    if (!manualOrderForm.customer_name || !manualOrderForm.items_text) {
      showNotification("Por favor completa el nombre del cliente y el detalle del pedido", "warning");
      return;
    }
    const newOrder = buildManualOrderRow(manualOrderForm, activeBusiness.id);
    try {
      let { error } = await supabase.from('orders').insert([{ ...newOrder, source: 'manual' }]);
      if (isMissingColumnError(error, 'source')) ({ error } = await supabase.from('orders').insert([newOrder]));
      if (error) {
        showNotification("Error al guardar pedido manual: " + error.message, "error");
      } else {
        showNotification("✅ Pedido manual registrado exitosamente", "success");
        setShowManualOrderModal(false);
        setManualOrderForm({ customer_name: '', customer_phone: '', customer_address: '', items_text: '', total: '0', source: 'manual' });
        fetchDb();
      }
    } catch (err) {
      showNotification("Error de red o base de datos al crear pedido", "error");
    }
  };

  // Efecto para cargar los nombres de los teléfonos silenciados
  useEffect(() => {
    if (activeBusiness && activeBusiness.muted_phones && activeBusiness.muted_phones.length > 0) {
      const fetchNames = async () => {
        try {
          const { data, error } = await supabase
            .from('demo_sessions')
            .select('phone, prospect_name')
            .in('phone', activeBusiness.muted_phones);
          if (data && !error) {
            const namesMap = {};
            data.forEach(session => {
              namesMap[session.phone] = session.prospect_name || 'Desconocido';
            });
            setMutedPhoneNames(namesMap);
          }
        } catch (err) {
          console.warn("Error fetching muted phone names", err);
        }
      };
      fetchNames();
    }
  }, [activeBusiness?.muted_phones]);

  const executeTemplateApply = async (tmpl, shouldReplaceCatalog) => {
    const nombre = activeBusiness.nombre_negocio || 'Mi Negocio';
    const newTono = tmpl.tono;
    const baseTemplate = PROMPT_TEMPLATES.find(t => t.id === tmpl.id);
    const baseFn = baseTemplate?.getEstilo || tmpl.getEstilo;
    const newEstilo = typeof baseFn === 'function' ? baseFn(nombre) : tmpl.estiloText;

    setEstiloTextDraft(newEstilo);
    const el = document.getElementById('reglas-de-oro-textarea');
    if (el) el.value = newEstilo;

    updateActiveBusinessField('tipo_negocio', tmpl.category);
    
    // Auto-mapear el flujo operativo basado en la categoría comercial
    const categoryLower = tmpl.category.toLowerCase();
    const newFlujoOperativo = (categoryLower.includes('restaurante') || categoryLower.includes('comida') || categoryLower.includes('lavandería')) 
      ? 'pedidos' 
      : 'citas';
    updateActiveBusinessField('flujo_operativo', newFlujoOperativo);

    updateActiveBusinessField('tono', newTono);
    updateActiveBusinessField('estilo', newEstilo);
    updateActiveBusinessField('estilo_clean', newEstilo);
    setStoredFeatureOverride(activeBusinessId, 'estilo_clean', newEstilo);
    setStoredFeatureOverride(activeBusinessId, 'tono', newTono);
    
    if (shouldReplaceCatalog && tmpl.sampleServices && Array.isArray(tmpl.sampleServices)) {
      const servicesToInsert = tmpl.sampleServices.map((s, sIdx) => ({
          id: String(Date.now() + sIdx + 1),
          business_id: activeBusinessId,
          nombre: s.nombre,
          precio: s.precio,
          duracion_minutos: s.duracion_minutos || '30',
          activo: true
      }));

      updateActiveBusinessField('services', servicesToInsert);

      try {
        const { error: delErr } = await supabase.from('services').delete().eq('business_id', activeBusinessId);
        if (delErr) throw delErr;

        if (servicesToInsert.length > 0) {
          const { error: insErr } = await supabase.from('services').insert(servicesToInsert);
          if (insErr) throw insErr;
        }
      } catch(e) {
        console.error("Error al reemplazar el catálogo de servicios en Supabase:", e);
        showNotification("Error de red al reemplazar el catálogo. Revisa la consola.", "error");
        return; // Don't show success if it failed
      }
    }
    showNotification("Plantilla aplicada exitosamente", "success");
    setTemplateConfirmModal(null);
  };

  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editServiceName, setEditServiceName] = useState('');
  const [editServicePrice, setEditServicePrice] = useState('');

  const handleStartEditService = (service) => {
    setEditingServiceId(service.id);
    setEditServiceName(service.nombre || '');
    setEditServicePrice(service.precio || '');
  };

  const handleSaveEditService = async (serviceId) => {
    if (!editServiceName.trim()) {
      showNotification("El nombre del producto no puede estar vacío.", "warning");
      return;
    }
    const newPrice = parseFloat(editServicePrice) || 0;
    
    const updatedServices = (activeBusiness.services || []).map(s => 
      String(s.id) === String(serviceId) ? { ...s, nombre: editServiceName.trim(), precio: newPrice } : s
    );
    
    updateActiveBusinessField('services', updatedServices);
    setEditingServiceId(null);
    
    try {
      await supabase.from('services').update({ nombre: editServiceName.trim(), precio: newPrice }).eq('id', serviceId);
      showNotification("¡Producto / Platillo actualizado exitosamente!", "success");
    } catch (err) {
      console.error("Error al actualizar servicio en Postgres:", err);
    }
  };

  const handleToggleMutePhone = async (phoneInput, nameInput = '', overridePrefix = null) => {
    if (!phoneInput) return;
    let cleanPhone = String(phoneInput).replace(/[^0-9]/g, '');
    if (!cleanPhone) return;

    // Si el usuario seleccionó un prefijo y el número ingresado parece ser número local (sin código de país)
    const prefixToUse = overridePrefix !== null ? overridePrefix : selectedCountryPrefix;
    if (prefixToUse && cleanPhone.length <= 10 && !cleanPhone.startsWith(prefixToUse)) {
      cleanPhone = `${prefixToUse}${cleanPhone}`;
    }

    const currentMuted = Array.isArray(activeBusiness.muted_phones) ? activeBusiness.muted_phones : [];
    const currentMetadata = activeBusiness.muted_phones_metadata || {};
    
    // Comparación inteligente usando los últimos 10 dígitos para evitar inconsistencias de prefijo
    const phoneSuffix = cleanPhone.length >= 10 ? cleanPhone.slice(-10) : cleanPhone;

    const isMuted = currentMuted.some(p => {
      const pClean = String(p).replace(/[^0-9]/g, '');
      const pSuffix = pClean.length >= 10 ? pClean.slice(-10) : pClean;
      return pSuffix === phoneSuffix;
    });

    let newMuted = [];
    let newMetadata = { ...currentMetadata };

    if (isMuted) {
      newMuted = currentMuted.filter(p => {
        const pClean = String(p).replace(/[^0-9]/g, '');
        const pSuffix = pClean.length >= 10 ? pClean.slice(-10) : pClean;
        if (pSuffix === phoneSuffix) {
          delete newMetadata[p]; // Eliminar el nombre del diccionario si se reactiva
          return false;
        }
        return true;
      });
    } else {
      newMuted = [...currentMuted, cleanPhone];
      if (nameInput && nameInput.trim() !== '') {
        newMetadata[cleanPhone] = nameInput.trim();
      }
    }

    const currentFeatures = getAllBusinessFeatures(activeBusiness, { muted_phones: newMuted, muted_phones_metadata: newMetadata });

    const updatedEstilo = buildEstiloWithFeatures(activeBusiness.estilo_clean || activeBusiness.estilo, currentFeatures);

    // Guardar estado previo para revertir si falla
    const previousEstilo = activeBusiness.estilo;
    const previousMuted = currentMuted;
    const previousMetadata = currentMetadata;

    // Guardar en override local para consistencia inmediata entre renders
    setStoredFeatureOverride(activeBusinessId, 'muted_phones', newMuted);
    setStoredFeatureOverride(activeBusinessId, 'muted_phones_metadata', newMetadata);

    // Actualización optimista local
    setMasterDb(prev => prev.map(b => String(b.id) === String(activeBusinessId) ? {
      ...b,
      muted_phones: newMuted,
      muted_phones_metadata: newMetadata,
      estilo: updatedEstilo,
      estilo_clean: getCleanEstilo(updatedEstilo)
    } : b));

    try {
      // Actualizar estrictamente el negocio activo en Supabase
      const { error } = await supabase.from('businesses').update({ estilo: updatedEstilo }).eq('id', activeBusinessId);

      if (error) {
        console.error("Error Supabase al actualizar muted_phones:", error.message);
        showNotification(`Error al guardar: ${error.message}`, "error");
        // Revertir estado local
        setStoredFeatureOverride(activeBusinessId, 'muted_phones', previousMuted);
        setStoredFeatureOverride(activeBusinessId, 'muted_phones_metadata', previousMetadata);
        setMasterDb(prev => prev.map(b => String(b.id) === String(activeBusinessId) ? {
          ...b,
          muted_phones: previousMuted,
          muted_phones_metadata: previousMetadata,
          estilo: previousEstilo,
          estilo_clean: getCleanEstilo(previousEstilo)
        } : b));
        return;
      }
      showNotification(isMuted ? `Bot reactivado para +${cleanPhone}` : `Bot pausado para +${cleanPhone}`, isMuted ? "success" : "warning");
    } catch (e) {
      console.error("Error de red al actualizar muted_phones:", e);
      showNotification("Error de conexión al guardar", "error");
      // Revertir estado local
      setStoredFeatureOverride(activeBusinessId, 'muted_phones', previousMuted);
      setStoredFeatureOverride(activeBusinessId, 'muted_phones_metadata', previousMetadata);
      setMasterDb(prev => prev.map(b => String(b.id) === String(activeBusinessId) ? {
        ...b,
        muted_phones: previousMuted,
        muted_phones_metadata: previousMetadata,
        estilo: previousEstilo,
        estilo_clean: getCleanEstilo(previousEstilo)
      } : b));
    }
  };

  const handleUpdateMutedPhoneName = async (phone, newName) => {
    if (!phone) return;
    const currentMetadata = activeBusiness.muted_phones_metadata || {};
    
    let newMetadata = { ...currentMetadata };
    if (newName && newName.trim() !== '') {
      newMetadata[phone] = newName.trim();
    } else {
      delete newMetadata[phone];
    }

    const currentFeatures = getAllBusinessFeatures(activeBusiness, { muted_phones_metadata: newMetadata });
    const updatedEstilo = buildEstiloWithFeatures(activeBusiness.estilo_clean || activeBusiness.estilo, currentFeatures);

    const previousEstilo = activeBusiness.estilo;
    const previousMetadata = currentMetadata;

    setStoredFeatureOverride(activeBusinessId, 'muted_phones_metadata', newMetadata);
    setMasterDb(prev => prev.map(b => String(b.id) === String(activeBusinessId) ? {
      ...b,
      muted_phones_metadata: newMetadata,
      estilo: updatedEstilo,
      estilo_clean: getCleanEstilo(updatedEstilo)
    } : b));
    setEditingMutedPhone(null);
    setEditMutedPhoneName("");

    try {
      const { error } = await supabase.from('businesses').update({ estilo: updatedEstilo }).eq('id', activeBusinessId);
      if (error) throw error;
      showNotification("Nombre actualizado exitosamente", "success");
    } catch (error) {
      console.error("Error al actualizar muted_phones_metadata:", error.message);
      showNotification(`Error al guardar nombre: ${error.message}`, "error");
      setStoredFeatureOverride(activeBusinessId, 'muted_phones_metadata', previousMetadata);
      setMasterDb(prev => prev.map(b => String(b.id) === String(activeBusinessId) ? {
        ...b,
        muted_phones_metadata: previousMetadata,
        estilo: previousEstilo,
        estilo_clean: getCleanEstilo(previousEstilo)
      } : b));
    }
  };
  
  // Contraseña del nuevo cliente: va aparte de newBusiness para que nunca quede en masterDb ni en el respaldo exportado.
  const [newBusinessPassword, setNewBusinessPassword] = useState('');
  // State para nuevo negocio (SaaS Master)
  const [newBusiness, setNewBusiness] = useState({
    nombre_negocio: "",
    tipo_negocio: "Barbería",
    id_whatsapp: "",
    google_calendar_id: "",
    plan: "Emprendedor", // Ahora guardaremos el nombre del plan para legibilidad
    monto_suscripcion: 29,
    ...getDefaultFeatures("Barbería")
  });

  // State para la gestión de Planes en Master
  const [activeMasterTab, setActiveMasterTab] = useState('clientes');
  const [masterSearchQuery, setMasterSearchQuery] = useState('');
  const [masterFilterStatus, setMasterFilterStatus] = useState('todos');
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [masterCurrentPage, setMasterCurrentPage] = useState(1);
  
  const [newPlan, setNewPlan] = useState({
    name: "",
    price: "",
    max_specialists: 1,
    max_services: 5,
    features: "",
    description: "",
    feature_appointments: true,
    feature_services: true,
    feature_menu_media: false,
    feature_identidad_ia: true,
    feature_staff: true,
    feature_finanzas: true,
    feature_orders: false
  });

  // Feedback Visual (Toasts)
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [isSyncing, setIsSyncing] = useState(false);

  // Estados para provisión y conexión de WhatsApp (Auto-QR)
  const [waStatus, setWaStatus] = useState('disconnected'); // 'disconnected' | 'loading' | 'qr_ready' | 'connected'
  const [qrCode, setQrCode] = useState('');
  const [pollingInterval, setPollingInterval] = useState(null);

  // Persistir base de datos global en localStorage de forma segura (sin exceder la cuota del navegador)
  useEffect(() => {
    safeSetLocalStorage('keylacrm_master_db', masterDb);
  }, [masterDb]);



  // Limpieza del temporizador de sondeo de estado al desmontar
  useEffect(() => {
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [pollingInterval]);



  // Sincronizar estado visual de WhatsApp al cambiar de local
  useEffect(() => {
    if (activeBusiness.instance_name && activeBusiness.instance_token) {
      setWaStatus('connected');
    } else {
      setWaStatus('disconnected');
      setQrCode('');
    }
  }, [activeBusinessId]);



  useEffect(() => {
    localStorage.setItem('keylacrm_plans_db', JSON.stringify(plansDb));
  }, [plansDb]);



  const showNotification = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3000);
  };

  // --- MÉTODOS DEL PORTAL MASTER (SERGIO) ---
  const handleCreatePlan = async () => {
    if (!newPlan.name.trim() || !newPlan.price.toString().trim()) {
      showNotification("Por favor completa el nombre del plan y el precio", "error");
      return;
    }
    const createdPlan = {
      ...newPlan,
      id: 'p-' + Date.now().toString(),
      price: parseFloat(newPlan.price) || 0
    };
    
    // 1. Actualización optimista del estado local e inmediato guardado en localStorage
    setPlansDb(prev => [...prev, createdPlan]);
    setNewPlan({
      name: "", price: "", max_specialists: 1, max_services: 5, features: "", description: "",
      feature_appointments: true, feature_services: true, feature_menu_media: false,
      feature_identidad_ia: true, feature_staff: true, feature_finanzas: true, feature_orders: false
    });

    // 2. Intento de persistencia en Supabase Postgres
    try {
      const { error } = await supabase.from('plans').insert([createdPlan]);
      if (error) {
        console.warn("[Supabase] Nota al guardar plan en Postgres:", error.message);
        showNotification("¡Plan creado y guardado localmente!", "info");
      } else {
        showNotification("¡Plan creado y sincronizado en BD!");
      }
    } catch (err) {
      console.error(err);
      showNotification("¡Plan creado exitosamente!", "info");
    }
  };

  // Activa/desactiva un módulo dentro de la definición de un plan (se guarda en Supabase).
  const updatePlanFeature = async (planId, field, value) => {
    setPlansDb(prev => prev.map(p => p.id === planId ? { ...p, [field]: value } : p));
    try {
      const { error } = await supabase.from('plans').update({ [field]: value }).eq('id', planId);
      if (error) {
        console.warn(`[Supabase] Nota al actualizar columna '${field}' en 'plans':`, error.message);
      } else {
        showNotification("Módulo del plan actualizado en BD.");
        return;
      }
    } catch (err) {
      console.error(err);
    }
    showNotification("Módulo del plan guardado localmente.", "info");
  };



  const handleDeletePlan = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este plan? Los negocios existentes no se verán afectados.")) return;
    setPlansDb(prev => prev.filter(p => p.id !== id));
    showNotification("Plan eliminado.", "warning");
    try {
      const { error } = await supabase.from('plans').delete().eq('id', id);
      if (error) console.warn("[Supabase] Nota al eliminar plan en BD:", error.message);
    } catch (err) {
      console.error(err);
    }
  };

  const handleExportDB = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(masterDb, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "keylacrm_masterdb_backup.json");
    document.body.appendChild(downloadAnchorNode); // required for firefox
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
    showNotification("Backup descargado con éxito");
  };

  const handleCreateBusiness = async () => {
    if (!newBusiness.nombre_negocio.trim() || !newBusiness.id_whatsapp.trim()) {
      showNotification("Por favor completa el nombre del negocio y su WhatsApp", "error");
      return;
    }

    // Si se escribió una contraseña, hace falta un correo válido: con él entra el negocio.
    if (newBusinessPassword) {
      const loginCheck = validateBusinessLogin({ email: newBusiness.google_calendar_id, password: newBusinessPassword });
      if (!loginCheck.ok) { showNotification(loginCheck.error, "error"); return; }
    }

    const sanitizedInstanceName = `${newBusiness.nombre_negocio.toLowerCase().replace(/[^a-z0-9]/g, '')}inst`;

    // Bloqueo de duplicados: Verificar si ya existe un negocio con el mismo nombre o instancia
    const exists = masterDb.some(b => 
      b.nombre_negocio.toLowerCase().trim() === newBusiness.nombre_negocio.toLowerCase().trim() ||
      b.instance_name === sanitizedInstanceName
    );

    if (exists) {
      showNotification("Ya existe un negocio registrado con este nombre o instancia de WhatsApp.", "error");
      return;
    }

    const businessId = Date.now().toString();

    // Mapear plantilla correspondiente al tipo_negocio seleccionado
    const matchingTmpl = PROMPT_TEMPLATES.find(t => 
      t.category.toLowerCase().includes(newBusiness.tipo_negocio.toLowerCase()) || 
      newBusiness.tipo_negocio.toLowerCase().includes(t.category.toLowerCase())
    ) || PROMPT_TEMPLATES[0];

    const initialEstilo = matchingTmpl.getEstilo(newBusiness.nombre_negocio);
    const initialTono = matchingTmpl.tono;
    const initialFeatures = getAllBusinessFeatures(newBusiness);
    const estiloWithFeatures = buildEstiloWithFeatures(initialEstilo, initialFeatures);

    const created = {
      ...newBusiness,
      id: businessId,
      instance_name: sanitizedInstanceName,
      horario: "Lunes a Sábado de 8:00 AM a 8:00 PM",
      tono: initialTono,
      estilo: estiloWithFeatures,
      estilo_clean: initialEstilo,
      webhook_n8n: "https://flow.seryiads.com/webhook/keylacrm-sync",
      activo: true
    };

    // Preparar los servicios semilla predefinidos del nicho para Supabase
    const seedServicesList = (matchingTmpl.sampleServices || []).map(s => ({
      business_id: businessId,
      nombre: s.nombre,
      precio: s.precio,
      duracion_minutos: s.duracion_minutos || '30',
      activo: true
    }));

    const dbPayload = {
      id: created.id,
      nombre_negocio: created.nombre_negocio,
      tipo_negocio: created.tipo_negocio,
      id_whatsapp: created.id_whatsapp,
      instance_name: sanitizedInstanceName,
      google_calendar_id: created.google_calendar_id || null,
      horario: created.horario,
      tono: created.tono,
      estilo: created.estilo,
      activo: true
    };

    // 1. Actualización optimista del estado local con servicios semilla
    setMasterDb(prev => [...prev, { ...created, staff: [], appointments: [], services: seedServicesList, orders: [] }]);
    const loginToCreate = newBusinessPassword ? { email: newBusiness.google_calendar_id, password: newBusinessPassword } : null;
    setNewBusiness({ nombre_negocio: "", tipo_negocio: "Barbería", id_whatsapp: "", google_calendar_id: "", plan: "Emprendedor", monto_suscripcion: 29, ...getDefaultFeatures("Barbería") });
    setNewBusinessPassword('');

    // 2. Persistencia real en Supabase Postgres (Negocio + Servicios Semilla)
    try {
      const { error } = await supabase.from('businesses').insert([dbPayload]);
      if (error) {
        console.error("[Supabase Error al crear negocio]:", error);
        showNotification(`Atención: Error al guardar en Postgres (${error.message})`, "error");
      } else {
        await startInTestMode(supabase, dbPayload.id, null);
        // Insertar servicios semilla en public.services
        if (seedServicesList.length > 0) {
          try {
            await supabase.from('services').insert(seedServicesList);
          } catch (sErr) {
            console.warn("Aviso al insertar servicios semilla:", sErr);
          }
        }

        showNotification(`¡SaaS Creado! Bienvenido ${created.nombre_negocio} a Keyla CRM`, "success");
        if (loginToCreate) {
          const loginResult = await setBusinessLogin(created.id, loginToCreate);
          if (!loginResult.ok) showNotification(`El negocio se creó, pero no el acceso: ${loginResult.error} Créalo desde su ficha.`, "warning");
          else showNotification(`Acceso creado: ${loginResult.email}. Compártele la contraseña que escribiste.`, "success");
        }
        setActiveBusinessId(created.id);
        fetchDb();
      }
    } catch (err) {
      console.error("Error al crear negocio:", err);
      showNotification(`Error inesperado al registrar el negocio.`, "error");
    }
  };

  const toggleBusinessStatus = async (id) => {
    const businessToToggle = masterDb.find(b => b.id === id);
    if (!businessToToggle) return;

    const isSuspending = businessToToggle.activo;
    const confirmMsg = isSuspending 
      ? "¿Estás seguro de SUSPENDER esta cuenta? El cliente perderá el acceso a su panel y se apagará su bot de WhatsApp." 
      : "¿Estás seguro de REACTIVAR esta cuenta?";
      
    if (!window.confirm(confirmMsg)) return;

    // Si vamos a suspender y tenía una instancia conectada, mandamos la señal a n8n para apagarla
    if (isSuspending && businessToToggle.instance_name) {
      try {
        showNotification("Desconectando bot de WhatsApp...", "info");
        await fetch(WA_PROVISION_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'delete_instance',
            instance_name: businessToToggle.instance_name
          })
        });
        console.log(`[Keyla CRM] Instancia ${businessToToggle.instance_name} eliminada en Evolution API.`);
      } catch (err) {
        console.warn("Error enviando desconexión a n8n:", err);
      }
    }

    setMasterDb(prev => prev.map(b => b.id === id ? { ...b, activo: !isSuspending } : b));
    showNotification(isSuspending ? "Cuenta suspendida exitosamente." : "Cuenta reactivada exitosamente.", "success");

    // Limpiamos la vista si estábamos viendo la empresa recién suspendida
    if (isSuspending && activeBusinessId === id) {
      setActiveBusinessId(null);
    }
  };

  const handleApprovePayment = async (id) => {
    if (!window.confirm("¿Aprobar el pago de este negocio? Se actualizará el estado a 'aprobado'.")) return;
    try {
      showNotification("Aprobando pago...", "info");
      const { error } = await supabase.from('businesses').update({
        payment_status: 'aprobado',
        payment_approved_at: new Date().toISOString()
      }).eq('id', id);
      
      if (error) throw error;
      
      setMasterDb(prev => prev.map(b => b.id === id ? { ...b, payment_status: 'aprobado', payment_approved_at: new Date().toISOString() } : b));
      showNotification("Pago aprobado exitosamente.", "success");
    } catch (err) {
      console.error("Error al aprobar pago:", err);
      showNotification(`Error al aprobar el pago: ${err.message}`, "error");
    }
  };

  const handleSubmitPaymentProof = async () => {
    if (!paymentProofUrlInput.trim()) {
      showNotification("Por favor ingresa la URL del comprobante.", "error");
      return;
    }
    try {
      showNotification("Enviando comprobante...", "info");
      const { error } = await supabase.from('businesses').update({
        payment_proof_url: paymentProofUrlInput.trim(),
        payment_status: 'pendiente'
      }).eq('id', activeBusinessId);
      
      if (error) throw error;
      
      setMasterDb(prev => prev.map(b => b.id === activeBusinessId ? { ...b, payment_proof_url: paymentProofUrlInput.trim(), payment_status: 'pendiente' } : b));
      showNotification("Comprobante enviado. En breve lo revisaremos.", "success");
      setPaymentProofUrlInput('');
    } catch (err) {
      console.error("Error al enviar comprobante:", err);
      showNotification(`Error: ${err.message}`, "error");
    }
  };

  const handleDeleteBusiness = async (id) => {
    const businessToDelete = masterDb.find(b => String(b.id) === String(id));
    if (!businessToDelete) return;


    if (!window.confirm(`¿Estás SEGURO de ELIMINAR COMPLETAMENTE a ${businessToDelete.nombre_negocio}? Esto borrará la instancia de WhatsApp, sus citas y configuración de la base de datos.`)) return;

    const pwd = window.prompt("Introduce la clave maestra para confirmar la eliminación de la base de datos:");
    if (pwd !== "Indext.2026") {
      showNotification("Clave incorrecta. Eliminación cancelada.", "error");
      return;
    }

    // 1. Eliminar instancia de Evolution API si existe
    if (businessToDelete.instance_name) {
      try {
        showNotification("Desconectando bot de WhatsApp...", "info");
        await fetch(WA_PROVISION_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'delete_instance',
            instance_name: businessToDelete.instance_name
          })
        });
      } catch (err) {
        console.warn("Error enviando desconexión a n8n:", err);
      }
    }

    // 2. Eliminar de Supabase (tablas hijas primero, luego la tabla principal)
    try {
      await supabase.from('orders').delete().eq('business_id', String(id));
      await supabase.from('appointments').delete().eq('business_id', String(id));
      await supabase.from('staff').delete().eq('business_id', String(id));
      await supabase.from('services').delete().eq('business_id', String(id));
      
      const { error } = await supabase.from('businesses').delete().eq('id', String(id));
      if (error) throw error;
      
      // Actualización reactiva inmediata en pantalla (sin recargar)
      setMasterDb(prev => {
        const remaining = prev.filter(b => String(b.id) !== String(id));
        if (String(activeBusinessId) === String(id)) {
          setActiveBusinessId(remaining.length > 0 ? String(remaining[0].id) : null);
        }
        return remaining;
      });
      
      showNotification(`Negocio "${businessToDelete.nombre_negocio}" eliminado exitosamente en la BD.`, "success");
    } catch (error) {
      console.error("[Supabase Delete Error]:", error);
      showNotification(`Error al eliminar de la base de datos: ${error.message}`, "error");
    }
  };



  // --- MÉTODOS DE APROVISIONAMIENTO WHATSAPP (AUTO-QR) ---
  const handleConnectWhatsApp = async () => {
    if (isDemoAccount) {
      showNotification("Por seguridad, la vinculación de WhatsApp está bloqueada en cuentas Demo.", "error");
      return;
    }

    if (!activeBusiness.id_whatsapp) {
      showNotification("Por favor configura primero el ID Celular WhatsApp en los ajustes.", "error");
      return;
    }

    setWaStatus('loading');
    setQrCode('');

    // Evolution API v2 requiere nombres 100% alfanuméricos (sin guiones ni guión bajo)
    // Generamos: <nombre_negocio_sanitizado>inst  (ej: "seryiadsainst")
    const rawName = activeBusiness.instance_name || 
      `${activeBusiness.nombre_negocio.toLowerCase().replace(/[^a-z0-9]/g, '')}inst`;
    // Sanitizamos también por si el nombre guardado tenía underscore de versión anterior
    const instanceName = rawName.replace(/[^a-z0-9]/g, '');

    // Si no tiene instance_name guardada (o era inválida), la asignamos
    if (!activeBusiness.instance_name || activeBusiness.instance_name !== instanceName) {
      updateActiveBusinessField('instance_name', instanceName);
    }

    try {
      const response = await fetch(WA_PROVISION_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create_instance',
          business_id: activeBusiness.id,
          id_whatsapp: activeBusiness.id_whatsapp,
          instance_name: instanceName
        })
      });

      // Leer siempre el body, incluso en errores 4xx/5xx
      const data = await response.json().catch(() => ({}));

      console.log('[Keyla CRM] Respuesta n8n:', JSON.stringify(data).substring(0, 300));

      if (data.status === 'connected' || data.connected || data.state === 'open' || data.message === 'connected' || (response.ok && !data.qrcode && !data.qr && !data.base64 && !data.error && data.status !== 'error')) {
        setWaStatus('connected');
        showNotification("¡WhatsApp vinculado exitosamente! 🟢");
      } else if (data.status === 'qrcode' || data.qrcode || data.qr || data.base64) {
        // Normalizar el base64: garantizar prefijo data URI para el <img src>
        let rawQr = data.qrcode || data.qr || data.base64 || '';
        if (rawQr && !rawQr.startsWith('data:')) {
          rawQr = `data:image/png;base64,${rawQr}`;
        }
        setQrCode(rawQr);
        setWaStatus('qr_ready');
        showNotification("Código QR generado. Escanéalo en tu celular.");
        startPollingStatus(instanceName);
      } else if (data.status === 'error' || data.error || !response.ok) {
        // devolvió un error explícito o el HTTP falló
        const detail = data.message || data.detail || data.error || `HTTP ${response.status}`;
        showNotification(`Error de aprovisionamiento: ${detail}`, 'error');
        setWaStatus('disconnected');
        console.error('[Sistema] Error:', data);
      } else {
        // Fallback absoluto por si el webhook devuelve algo rarísimo pero 200 OK
        console.warn('[Sistema] Respuesta atípica, asumiendo conexión o proceso:', data);
        setWaStatus('connected');
        showNotification("Procesado correctamente.", "success");
      }
    } catch (error) {
      // Error de red (CORS, timeout, servidor caído)
      console.error('[Sistema] Error de red al contactar servidor:', error);
      showNotification('No se pudo conectar con el servidor. Verifica tu conexión.', 'error');
      setWaStatus('disconnected');
    }
  };

  const startPollingStatus = (instanceName) => {
    if (pollingInterval) clearInterval(pollingInterval);

    // Sondeo de estado cada 5 segundos
    const interval = setInterval(async () => {
      try {
        const response = await fetch(WA_PROVISION_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'check_status',
            instance_name: instanceName
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.status === 'connected') {
            setWaStatus('connected');
            setQrCode('');
            clearInterval(interval);
            setPollingInterval(null);
            
            if (data.instance_token) {
              updateActiveBusinessField('instance_token', data.instance_token);
            }
            showNotification("¡WhatsApp conectado exitosamente! 🟢", "success");
          }
        }
      } catch (err) {
        console.warn("Error en polling de WhatsApp:", err);
      }
    }, 5000);

    setPollingInterval(interval);
  };

  const handleDisconnectWhatsApp = async () => {
    if (isDemoAccount) {
      showNotification("Por seguridad, la desvinculación de WhatsApp está bloqueada en cuentas Demo.", "error");
      return;
    }

    setWaStatus('loading');
    const instanceName = activeBusiness.instance_name;

    try {
      await fetch(WA_PROVISION_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete_instance',
          instance_name: instanceName
        })
      });
    } catch (err) {
      console.warn("Error enviando desconexión:", err);
    }

    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setWaStatus('disconnected');
    setQrCode('');
    updateActiveBusinessField('instance_name', '');
    updateActiveBusinessField('instance_token', '');
    showNotification("Instancia de WhatsApp desconectada con éxito.", "warning");
  };

  // --- MÉTODOS DEL NEGOCIO ACTIVO ---
  const updateActiveBusinessField = (field, value) => {
    setMasterDb(prev => prev.map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return { ...b, [field]: value };
      }
      return b;
    }));
  };

  // Guarda la identidad de marca del negocio (y la persiste de inmediato en Supabase Postgres).
  const saveBusinessSettings = async (customFields = {}) => {
    // Evitar que React SyntheticEvent (onClick={saveBusinessSettings}) se pase como customFields
    const cleanCustom = (customFields && typeof customFields === 'object' && !customFields.nativeEvent && !customFields.target && !customFields._reactName) ? customFields : {};

    const rawMenuUrl = cleanCustom.menu_pdf_url !== undefined ? cleanCustom.menu_pdf_url : activeBusiness.menu_pdf_url;
    const menuUrl = getDirectMediaUrl(rawMenuUrl);
    
    const rawLinkUrl = cleanCustom.menu_link_url !== undefined ? cleanCustom.menu_link_url : activeBusiness.menu_link_url;
    const rawTextList = cleanCustom.menu_text_list !== undefined ? cleanCustom.menu_text_list : activeBusiness.menu_text_list;

    if (menuUrl !== undefined) {
      setStoredFeatureOverride(activeBusinessId, 'menu_pdf_url', menuUrl);
    }
    if (rawLinkUrl !== undefined) {
      setStoredFeatureOverride(activeBusinessId, 'menu_link_url', rawLinkUrl);
    }
    if (rawTextList !== undefined) {
      setStoredFeatureOverride(activeBusinessId, 'menu_text_list', rawTextList);
    }

    const localOverrides = getStoredFeatureOverrides()[String(activeBusinessId)] || {};
    const currentMuted = Array.isArray(localOverrides.muted_phones) ? localOverrides.muted_phones : (Array.isArray(activeBusiness.muted_phones) ? activeBusiness.muted_phones : []);

    const currentFeatures = getAllBusinessFeatures(activeBusiness, { 
      menu_pdf_url: menuUrl,
      menu_link_url: rawLinkUrl,
      menu_text_list: rawTextList,
      muted_phones: currentMuted
    });
    // Prioridad: 1) campo explícito desde caller, 2) estilo_clean del negocio si no está vacío, 3) extraído del estilo completo
    let cleanEstiloInput = (cleanCustom.estilo_clean !== undefined && cleanCustom.estilo_clean !== null)
      ? cleanCustom.estilo_clean
      : (activeBusiness.estilo_clean && activeBusiness.estilo_clean.trim()
          ? activeBusiness.estilo_clean
          : getCleanEstilo(activeBusiness.estilo));

    if (cleanEstiloInput && !cleanEstiloInput.includes('REGLA UNIVERSAL DE TOMA MULTI-SELECCIÓN Y MULTI-CANTIDAD')) {
      cleanEstiloInput = `${cleanEstiloInput}\n\n${UNIVERSAL_MULTI_SELECTION_RULE}`;
    }

    const fullEstilo = buildEstiloWithFeatures(cleanEstiloInput, currentFeatures);

    const memoryState = {
      nombre_negocio: activeBusiness.nombre_negocio,
      tipo_negocio: activeBusiness.tipo_negocio,
      flujo_operativo: activeBusiness.flujo_operativo || 'citas',
      id_whatsapp: activeBusiness.id_whatsapp,
      google_calendar_id: activeBusiness.google_calendar_id,
      horario: activeBusiness.horario,
      tono: activeBusiness.tono,
      estilo: fullEstilo,
      estilo_clean: cleanEstiloInput,
      menu_pdf_url: menuUrl,
      menu_link_url: rawLinkUrl,
      menu_text_list: rawTextList,
      services: activeBusiness.services || [],
      staff: activeBusiness.staff || [],
      appointments: activeBusiness.appointments || [],
      orders: activeBusiness.orders || [],
      ...cleanCustom
    };

    // Actualizar estado en memoria local de inmediato
    setMasterDb(prev => prev.map(b => String(b.id) === String(activeBusinessId) ? { ...b, ...memoryState } : b));

    // Payload para Postgres
    const dbPayload = {
      nombre_negocio: cleanCustom.nombre_negocio || activeBusiness.nombre_negocio,
      tipo_negocio: cleanCustom.tipo_negocio || activeBusiness.tipo_negocio,
      flujo_operativo: cleanCustom.flujo_operativo || activeBusiness.flujo_operativo || 'citas',
      id_whatsapp: cleanCustom.id_whatsapp || activeBusiness.id_whatsapp,
      google_calendar_id: cleanCustom.google_calendar_id || activeBusiness.google_calendar_id,
      horario: cleanCustom.horario || activeBusiness.horario,
      tono: cleanCustom.tono || activeBusiness.tono,
      slug: cleanCustom.slug || activeBusiness.slug,
      estilo: fullEstilo,
      banner_url: cleanCustom.banner_url !== undefined ? cleanCustom.banner_url : activeBusiness.banner_url,
      logo_url: cleanCustom.logo_url !== undefined ? cleanCustom.logo_url : activeBusiness.logo_url
    };

    try {
      const { error } = await supabase.from('businesses').update(dbPayload).eq('id', activeBusinessId);
      if (error) {
        console.error("[Supabase Error - Guardar Identidad IA]", error.message);
        showNotification(`Error al guardar en la base de datos: ${error.message}`, "error");
      } else {
        showNotification(`¡${activeBusiness.nombre_negocio} actualizado en la base de datos!`);
      }
    } catch (err) {
      console.error("Error al guardar en Postgres:", err);
      showNotification(`Error al guardar: ${err.message || 'No se pudo conectar a la base de datos'}`, "error");
    }
  };

  // Guarda las características habilitadas para un negocio (solo editable desde el panel Master/Admin).
  const updateBusinessFeature = async (businessId, field, value) => {
    const targetBiz = masterDb.find(b => String(b.id) === String(businessId));
    if (!targetBiz) return;

    if (field === 'whatsapp_enabled' && value === false && targetBiz.instance_name) {
      try {
        showNotification("Desconectando bot de WhatsApp...", "info");
        fetch(WA_PROVISION_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'delete_instance',
            instance_name: targetBiz.instance_name
          })
        }).catch(err => console.warn("Error asíncrono al desconectar n8n:", err));
      } catch (e) {
        console.error(e);
      }
    }

    // 1. Guardar de forma infalible en localStorage para la sesión actual
    setStoredFeatureOverride(businessId, field, value);

    const currentFeatures = getAllBusinessFeatures(targetBiz, { [field]: value });

    const updatedEstilo = buildEstiloWithFeatures(targetBiz.estilo_clean || targetBiz.estilo, currentFeatures);

    // 2. Actualización optimista del estado local en React State
    setMasterDb(prev => prev.map(b => String(b.id) === String(businessId) ? { 
      ...b, 
      [field]: value, 
      estilo: updatedEstilo, 
      estilo_clean: getCleanEstilo(updatedEstilo) 
    } : b));

    showNotification(`Módulo ${value ? 'Habilitado' : 'Deshabilitado'} con éxito.`, "success");

    // 3. Persistir en Supabase Postgres (se guarda en la columna 'estilo' para garantizar persistencia global en BD)
    try {
      const { error } = await supabase.from('businesses').update({ estilo: updatedEstilo, [field]: value }).eq('id', businessId);
      if (error) {
        // Fallback en caso de que la columna individual [field] no exista aún en Postgres
        await supabase.from('businesses').update({ estilo: updatedEstilo }).eq('id', businessId);
      }
    } catch (err) {
      console.error("Error al guardar característica en Postgres:", err);
    }
  };

  const handleAddStaff = async () => {
    if (activeBusiness) {
      const limite = activeBusiness.limite_profesionales || 2;
      if (activeBusiness.staff && activeBusiness.staff.length >= limite) {
        showNotification(`Límite alcanzado. Tu plan permite máximo ${limite} profesional(es). Mejora tu plan para añadir más.`, "error");
        return;
      }
    }
    
    if (!newStaff.name.trim() || !newStaff.specialty.trim()) {
      showNotification("Por favor completa el nombre y especialidad del staff", "error");
      return;
    }
    const member = { ...newStaff, id: Date.now().toString(), business_id: activeBusinessId };
    
    try {
      const { error } = await supabase.from('staff').insert([member]);
      if (error) throw error;

      setMasterDb(prev => prev.map(b => {
        if (b.id === activeBusinessId) {
          return { ...b, staff: [...b.staff, member] };
        }
        return b;
      }));
      setNewStaff({ name: "", specialty: "", google_calendar_id: "", pin_code: "", telefono: "", comision_porcentaje: 50, available: true });
      showNotification("Especialista agregado");
    } catch (err) {
      console.error(err);
      showNotification("Error al guardar especialista en BD", "error");
    }
  };

  const toggleStaffStatus = async (staffId) => {
    const member = (activeBusiness.staff || []).find(s => String(s.id) === String(staffId));
    const newStatus = member ? !member.available : true;

    setMasterDb(prev => prev.map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          staff: (b.staff || []).map(s => String(s.id) === String(staffId) ? { ...s, available: newStatus } : s)
        };
      }
      return b;
    }));

    try {
      await supabase.from('staff').update({ available: newStatus }).eq('id', staffId);
      showNotification("Disponibilidad del personal modificada");
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (['9001', '9002', '9003'].includes(String(activeBusinessId)) && userRole !== 'master') {
      alert('Acción denegada: No se puede eliminar personal en las cuentas de demostración para visitantes.');
      return;
    }
    setMasterDb(prev => prev.map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          staff: (b.staff || []).filter(s => String(s.id) !== String(staffId))
        };
      }
      return b;
    }));

    try {
      await supabase.from('staff').delete().eq('id', staffId);
      showNotification("Especialista retirado del staff", "warning");
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditStaff = async (member) => {
    const newName = window.prompt("Editar Nombre del Especialista:", member.name);
    if (newName === null) return;
    const newSpecialty = window.prompt("Editar Especialidad:", member.specialty);
    if (newSpecialty === null) return;
    const newEmail = window.prompt("Editar Correo (Gmail para Calendario):", member.google_calendar_id || "");
    if (newEmail === null) return;
    const newPhone = window.prompt("Editar Teléfono (WhatsApp):", member.telefono || "");
    if (newPhone === null) return;
    const newPin = window.prompt("Editar PIN de Acceso (4 dígitos):", member.pin_code || "");
    if (newPin === null) return;
    const newComision = window.prompt("Editar Comisión (%):", member.comision_porcentaje || 50);
    if (newComision === null) return;

    const updatedMember = {
      ...member,
      name: newName.trim() || member.name,
      specialty: newSpecialty.trim() || member.specialty,
      google_calendar_id: newEmail.trim(),
      telefono: newPhone.trim(),
      pin_code: newPin.trim().substring(0, 4),
      comision_porcentaje: parseFloat(newComision) || 0
    };

    setMasterDb(prev => prev.map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          staff: (b.staff || []).map(s => String(s.id) === String(member.id) ? updatedMember : s)
        };
      }
      return b;
    }));

    try {
      await supabase.from('staff').update({
        name: updatedMember.name,
        specialty: updatedMember.specialty,
        google_calendar_id: updatedMember.google_calendar_id,
        telefono: updatedMember.telefono,
        pin_code: updatedMember.pin_code,
        comision_porcentaje: updatedMember.comision_porcentaje
      }).eq('id', member.id);
      showNotification("Especialista actualizado", "success");
    } catch (err) {
      console.error(err);
      showNotification("Error al actualizar especialista", "error");
    }
  };

  const handleAddService = async () => {
    if (!newService.nombre.trim() || !newService.precio) {
      showNotification(isRestaurant ? "Por favor completa el nombre y precio del producto" : "Por favor completa el nombre y precio del servicio", "error");
      return;
    }
    const service = { 
      ...newService, 
      id: Date.now().toString(), 
      business_id: activeBusinessId,
      duracion_minutos: isRestaurant ? null : (newService.duracion_minutos || "30")
    };
    
    try {
      const { error } = await supabase.from('services').insert([service]);
      if (error) {
        console.warn("[Supabase Warning]", error.message);
      }
      
      setMasterDb(prev => prev.map(b => {
        if (String(b.id) === String(activeBusinessId)) {
          return { ...b, services: [...(b.services || []), service] };
        }
        return b;
      }));
      setNewService({ nombre: "", precio: "", duracion_minutos: "30", activo: true });
      showNotification(isRestaurant ? "¡Producto agregado al menú!" : "¡Servicio agregado al catálogo!");
    } catch (err) {
      console.error(err);
      showNotification(isRestaurant ? "Producto agregado localmente." : "Servicio agregado localmente.", "info");
    }
  };

  // --- MÉTODOS DE GESTIÓN COMPLETA DE PEDIDOS ---
  const updateOrderStatus = async (orderId, newStatus, extraFields = {}) => {
    const payload = { status: newStatus, ...extraFields };
    setMasterDb(prev => prev.map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          orders: (b.orders || []).map(o => String(o.id) === String(orderId) ? { ...o, ...payload } : o)
        };
      }
      return b;
    }));

    try {
      const { error } = await supabase.from('orders').update(payload).eq('id', orderId);
      if (error) console.warn("[Supabase] error updating order status:", error.message);
      showNotification("Estado del pedido actualizado.");
    } catch (err) {
      console.error(err);
      showNotification("Estado del pedido guardado localmente.", "info");
    }
  };

  const handleCopyForDelivery = (order) => {
    let itemsText = '';
    try {
      const parsedItems = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
      if (Array.isArray(parsedItems)) {
        itemsText = parsedItems.map(i => `- ${i.cantidad || i.qty || i.quantity || 1}x ${i.nombre || i.name || i.producto || i.item || 'Producto'} ($${i.precio || i.price || i.precio_unitario || 0})`).join('\n');
      } else {
        itemsText = order.items;
      }
    } catch(e) {
      itemsText = order.items;
    }

    const isPickup = order.delivery_mode === 'recoger_local' || order.delivery_mode === 'pickup';

    const text = `🛵 *NUEVO PEDIDO*\n\n` +
      `👤 *Cliente:* ${order.client_name || 'Sin nombre'}\n` +
      `📞 *Teléfono:* ${order.client_phone || 'Sin teléfono'}\n` +
      `📍 *Dirección:* ${isPickup ? '🏪 RECOGE EN LOCAL' : (order.delivery_address || 'Sin dirección')}\n\n` +
      `📦 *Productos:*\n${itemsText}\n\n` +
      `💰 *Total a Cobrar:* $${order.total || 0}\n` +
      `💳 *Método de Pago:* ${order.payment_method || 'Efectivo'}` +
      `${order.driver_notes ? `\n\n📝 *Notas:* ${order.driver_notes}` : ''}`;
      
    navigator.clipboard.writeText(text).then(() => {
      showNotification("¡Datos del pedido copiados al portapapeles!");
    }).catch(err => {
      console.error('Error al copiar:', err);
      showNotification("Error al copiar los datos", "error");
    });
  };

  const handleDeleteOrder = async (orderId) => {
    if (['9001', '9002', '9003'].includes(String(activeBusinessId)) && userRole !== 'master') {
      alert('Acción denegada: No se pueden eliminar pedidos en las cuentas de demostración para visitantes.');
      return;
    }
    if (!window.confirm("¿Estás seguro de eliminar este pedido definitivamente? Esta acción eliminará el registro de la base de datos.")) return;
    setMasterDb(prev => prev.map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          orders: (b.orders || []).filter(o => String(o.id) !== String(orderId))
        };
      }
      return b;
    }));
    showNotification("Pedido eliminado.", "warning");

    try {
      const { error } = await supabase.from('orders').delete().eq('id', orderId);
      if (error) console.warn("[Supabase] error deleting order:", error.message);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveOrderDetails = async (orderId, updatedFields) => {
    setMasterDb(prev => prev.map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          orders: (b.orders || []).map(o => String(o.id) === String(orderId) ? { ...o, ...updatedFields } : o)
        };
      }
      return b;
    }));

    try {
      const { error } = await supabase.from('orders').update(updatedFields).eq('id', orderId);
      if (error) console.warn("[Supabase] error saving order details:", error.message);
      showNotification("¡Datos y anotaciones del pedido guardados!");
    } catch (err) {
      console.error(err);
      showNotification("Anotaciones guardadas en el panel local.", "info");
    }
  };

  const toggleServiceStatus = async (serviceId) => {
    const srv = (activeBusiness.services || []).find(s => String(s.id) === String(serviceId));
    const newActive = srv ? !srv.activo : true;

    setMasterDb(prev => prev.map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          services: (b.services || []).map(s => String(s.id) === String(serviceId) ? { ...s, activo: newActive } : s)
        };
      }
      return b;
    }));

    try {
      await supabase.from('services').update({ activo: newActive }).eq('id', serviceId);
      showNotification("Estado del servicio modificado");
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (['9001', '9002', '9003'].includes(String(activeBusinessId)) && userRole !== 'master') {
      alert('Acción denegada: No se pueden eliminar servicios en las cuentas de demostración para visitantes.');
      return;
    }
    setMasterDb(prev => prev.map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          services: (b.services || []).filter(s => String(s.id) !== String(serviceId))
        };
      }
      return b;
    }));

    try {
      const { error } = await supabase.from('services').delete().eq('id', serviceId);
      if (error) {
        console.error("Error al eliminar servicio en Supabase:", error);
      } else {
        showNotification(isRestaurant ? "Producto eliminado del menú" : "Servicio eliminado del catálogo", "warning");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddAppointment = async () => {
    if (!newAppt.client.trim() || !newAppt.phone.trim() || !newAppt.date || !newAppt.time) {
      showNotification("Por favor llena todos los campos de la cita", "error");
      return;
    }
    const appt = {
      ...newAppt,
      id: Date.now().toString(),
      status: "scheduled",
      business_id: activeBusinessId
    };

    try {
      const { error } = await supabase.from('appointments').insert([appt]);
      if (error) throw error;

      setMasterDb(prev => prev.map(b => {
        if (b.id === activeBusinessId) {
          return { ...b, appointments: [appt, ...b.appointments] };
        }
        return b;
      }));
      setNewAppt({ client: "", phone: "", service: "Corte de Cabello", time: "", date: "", staffAssigned: "" });
      showNotification("Cita agregada de forma manual en el CRM");
    } catch (err) {
      console.error(err);
      showNotification("Error al guardar la cita en BD", "error");
    }
  };

  const cancelAppointment = async (apptId) => {
    try {
      const { error } = await supabase.from('appointments').update({ status: 'cancelled' }).eq('id', apptId);
      if (error) throw error;

      setMasterDb(prev => prev.map(b => {
        if (b.id === activeBusinessId) {
          return {
            ...b,
            appointments: b.appointments.map(a => a.id === apptId ? { ...a, status: "cancelled" } : a)
          };
        }
        return b;
      }));
      showNotification("Cita cancelada correctamente en el sistema y calendario.", "warning");
    } catch (err) {
      console.error(err);
      showNotification("Error al cancelar la cita", "error");
    }
  };

  const syncWithN8N = async () => {
    setIsSyncing(true);
    try {
      const response = await fetch(activeBusiness.webhook_n8n, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(activeBusiness)
      });
      if (response.ok) {
        showNotification(`¡${activeBusiness.nombre_negocio} sincronizado!`);
      } else {
        throw new Error();
      }
    } catch (e) {
      setTimeout(() => {
        showNotification("Error de sincronización.", "error");
        setIsSyncing(false);
      }, 1500);
      return;
    }
    setIsSyncing(false);
  };

  // Calcular métricas SaaS globales
  const totalMRR = masterDb.filter(b => b.activo).reduce((acc, curr) => acc + (curr.monto_suscripcion || 0), 0);
  const totalCitas = masterDb.reduce((acc, curr) => acc + (curr.appointments || []).length, 0);

  // Lógica central para procesar el rol según la sesión de Supabase
  const processUserSession = async (session, connectingBusinessId = null) => {
    if (!session || !session.user) return false;
    const userEmail = (session.user.email || '').toLowerCase().trim();
    setCurrentUserEmail(userEmail);

    // 1. Verificar si es Administrador Master
    if (adminEmails.some(e => e.toLowerCase().trim() === userEmail)) {
      setUserRole(prev => prev === 'client' ? 'client' : 'master');
      setIsAuthenticated(true);
      return true;
    }

    // 2. Buscar si el email corresponde a un cliente registrado (o priorizar el negocio que estamos conectando)
    let businessMatch = null;
    
    if (connectingBusinessId) {
      businessMatch = masterDb.find(b => String(b.id) === String(connectingBusinessId));
      if (!businessMatch) {
        try {
          const { data } = await supabase.from('businesses').select('*').eq('id', connectingBusinessId).single();
          if (data) businessMatch = data;
        } catch(e) { console.error(e); }
      }
    }
    
    if (!businessMatch) {
      businessMatch = masterDb.find(b => b.google_calendar_id && b.google_calendar_id.toLowerCase().trim() === userEmail);
    }
    
    // Si no está en masterDb local (debido a caché stale de React en onAuthStateChange), lo buscamos en Supabase
    if (!businessMatch) {
      try {
        const { data, error } = await supabase.from('businesses').select('*').eq('google_calendar_id', userEmail).single();
        if (data && !error) {
          businessMatch = data;
          fetchDb(); // Refrescar el estado global ahora que tenemos token válido
        }
      } catch (err) {
        console.error("Error buscando negocio de usuario autenticado:", err);
      }
    }
    
    if (businessMatch) {
      if (!businessMatch.activo) {
        supabase.auth.signOut();
        showNotification("Tu cuenta se encuentra suspendida. Por favor contacta al administrador.", "error");
        return false;
      }

      // --- GUARDAR TOKENS OAUTH DE GOOGLE CALENDAR ---
      if (session.provider_token || session.provider_refresh_token) {
        const updates = {};
        if (session.provider_token) updates.google_access_token = session.provider_token;
        if (session.provider_refresh_token) updates.google_refresh_token = session.provider_refresh_token;
        
        // Actualizar silenciosamente en la base de datos
        supabase.from('businesses')
          .update(updates)
          .eq('id', businessMatch.id)
          .then(({error}) => {
            if(error) console.error("Error guardando tokens OAuth:", error);
          });
      }
      // -----------------------------------------------

      setUserRole('client');
      setActiveBusinessId(businessMatch.id);
      setIsAuthenticated(true);
      setActiveTab(current => {
        if (current.startsWith('master-')) return 'dashboard';
        return current;
      });
      return true;
    }

    // 3. Correo no autorizado
    supabase.auth.signOut();
    showNotification(`El correo "${userEmail}" no se encuentra registrado en el sistema. Por favor contacte con el administrador.`, "error");
    return false;
  };

  // Google OAuth Login
  const handleGoogleLogin = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.hostname === 'localhost' ? `${window.location.origin}/dashboard` : 'https://keyla.seryiads.com/dashboard'
        }
      });
      if (error) throw error;
    } catch (err) {
      console.error("Error en Google Login:", err);
      showNotification("Error al iniciar sesión con Google. Intenta de nuevo.", "error");
    }
  };

  const handleConnectCalendar = async () => {
    try {
      // Guardamos el ID del negocio activo para actualizar su token al regresar
      localStorage.setItem('keylacrm_connecting_calendar_id', activeBusiness.id);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.hostname === 'localhost' ? `${window.location.origin}/dashboard` : 'https://keyla.seryiads.com/dashboard',
          scopes: 'https://www.googleapis.com/auth/calendar.events',
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });
      if (error) throw error;
    } catch (err) {
      console.error("Error conectando calendario:", err);
      showNotification("Error al conectar Google Calendar.", "error");
    }
  };

  // Login por Correo y Contraseña
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPassword.trim()) {
      showNotification("Por favor ingresa tu correo y contraseña.", "error");
      return;
    }
    setIsLoggingIn(true);
    const emailClean = loginEmail.trim().toLowerCase();

    // 1. Si es correo de Administrador Master
    if (adminEmails.some(e => e.toLowerCase().trim() === emailClean)) {
      // Contraseña real (sesión de Supabase): escribir solo un correo de administrador no basta.
      const { error: adminError } = await supabase.auth.signInWithPassword({ email: emailClean, password: loginPassword });
      if (adminError) {
        showNotification("Credenciales inválidas. Si entras con tu cuenta de Google, usa «Continuar con Google».", "error");
        setIsLoggingIn(false);
        return;
      }
      setUserRole('master');
      setActiveTab('master-dashboard');
      setIsAuthenticated(true);
      showNotification("¡Bienvenido Administrador!", "success");
      setIsLoggingIn(false);
      return;
    }

    // 2. Buscamos en DB
    let businessMatch = masterDb.find(b => b.google_calendar_id && b.google_calendar_id.toLowerCase().trim() === emailClean);
    if (!businessMatch) {
       businessMatch = await lookupBusinessForLogin(supabase, emailClean); // sin sesión la tabla no es pública: solo id, nombre y activo
    }

    if (businessMatch) {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: emailClean,
        password: loginPassword,
      });

      if (error) {
        showNotification("Credenciales inválidas.", "error");
        setIsLoggingIn(false);
        return;
      }

      if (!businessMatch.activo) {
        showNotification("Tu cuenta se encuentra suspendida. Por favor contacta al administrador.", "error");
        await supabase.auth.signOut();
        setIsLoggingIn(false);
        return;
      }
      setUserRole('client');
      setActiveBusinessId(businessMatch.id);
      setActiveTab('dashboard');
      setIsAuthenticated(true);
      if (window.location.pathname !== '/dashboard') {
        window.history.pushState({}, '', '/dashboard');
      }
      showNotification(`¡Bienvenido a ${businessMatch.nombre_negocio}!`, "success");
      setIsLoggingIn(false);
      return;
    }

    showNotification(`El correo "${emailClean}" no está vinculado a ningún negocio.`, "error");
    setIsLoggingIn(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!signupData.acceptsTerms) {
      showNotification("Debes aceptar los Términos y Condiciones.", "error");
      return;
    }
    setIsSigningUp(true);
    
    // 1. Registrar en Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: signupData.email.trim(),
      password: signupData.password,
    });

    if (authError) {
      showNotification("Error en registro: " + authError.message, "error");
      setIsSigningUp(false);
      return;
    }

    // 2. Crear Negocio con estado Trial
    const businessId = Date.now().toString();
    const baseSlug = signupData.businessName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const randomSuffix = Math.floor(1000 + Math.random() * 9000);
    const finalSlug = `${baseSlug}${randomSuffix}`;
    const instanceName = `${finalSlug}inst`;
    
    const newBiz = {
      id: businessId,
      nombre_negocio: signupData.businessName,
      slug: finalSlug,
      instance_name: instanceName,
      google_calendar_id: signupData.email.trim().toLowerCase(),
      id_whatsapp: signupData.phone.replace(/\D/g, ''),
      plan: 'free',
      subscription_status: 'trial',
      accepts_commercial_emails: true,
      activo: true,
      tipo_negocio: 'servicios',
      reglas_oro: `1. Eres el asistente IA de ${signupData.businessName}. Sé amable y profesional.`
    };

    const { error: bizError } = await supabase.from('businesses').insert([newBiz]);

    if (bizError) {
      showNotification("Error creando el espacio de trabajo.", "error");
      console.error(bizError);
      setIsSigningUp(false);
      return;
    }

    await startInTestMode(supabase, businessId, signupData.phone);

    // 3. Webhook Opcional
    try {
      await fetch('https://hook.us2.make.com/placeholder-webhook-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Nunca se envía la contraseña a un servicio externo.
        body: JSON.stringify({ ...signupData, password: undefined })
      });
    } catch (err) {
      console.warn("Error en webhook opcional de registro:", err);
    }

    showNotification("¡Cuenta creada con éxito! Ingresando...", "success");
    setIsSigningUp(false);
    
    // Auto-login
    setLoginEmail(signupData.email);
    setLoginPassword(signupData.password);
    setIsSignUp(false);
    
    if (authData.session) {
      setCurrentUserEmail(signupData.email.trim().toLowerCase());
      setUserRole('client');
      setActiveBusinessId(businessId);
      setActiveTab('dashboard');
      setIsAuthenticated(true);
      if (window.location.pathname !== '/dashboard') {
        window.history.pushState({}, '', '/dashboard');
      }
      fetchDb(); // Refrescar masterDb
    }
  };

  // Verificar si el usuario ya está autenticado (al cargar la página)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (data?.session) {
          processUserSession(data.session);
        }
      } catch (err) {
        console.error("Error checking auth session:", err);
        // Limpieza agresiva de sesión corrupta
        try {
          localStorage.removeItem('supabase.auth.token');
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
              localStorage.removeItem(key);
            }
          }
          await supabase.auth.signOut();
        } catch (e) {}
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        
        // Interceptar si venimos del flujo de conectar calendario
        const connectingBusinessId = localStorage.getItem('keylacrm_connecting_calendar_id');
        if (connectingBusinessId && session.provider_refresh_token) {
          localStorage.removeItem('keylacrm_connecting_calendar_id');
          try {
            await supabase
              .from('businesses')
              .update({ google_refresh_token: session.provider_refresh_token })
              .eq('id', connectingBusinessId);
            console.log("[Auth] Refresh token de calendario guardado con éxito.");
            // Actualizamos la base local sutilmente
            setMasterDb(prev => prev.map(b => b.id === connectingBusinessId ? { ...b, google_refresh_token: session.provider_refresh_token } : b));
          } catch (e) {
            console.error("Error guardando refresh token:", e);
          }
        }

        processUserSession(session, connectingBusinessId);
      } else if (event === 'SIGNED_OUT') {
        console.warn("[Auth] Sesión cerrada.");
        setIsAuthenticated(false);
        setUserRole('master');
        setCurrentUserEmail('');
        setLoginEmail('');
        setLoginPassword('');
      }
    });

    return () => subscription?.unsubscribe();
  }, []);


  // Middleware de protección y redirección de rutas (/login ➔ /dashboard y viceversa)
  useEffect(() => {
    if (isCheckingAuth) return;

    const checkAndRedirectRoute = () => {
      const currentPath = window.location.pathname;
      const publicPaths = ['/', '/login', '/signup'];
      const protectedPrefixes = ['/dashboard', '/staff', '/delivery', '/cocina', '/soluciones'];
      if (isAuthenticated) {
        // Authenticated users: redirect public entry points to dashboard
        if (publicPaths.includes(currentPath) || currentPath.startsWith('/login') || currentPath.startsWith('/signup') || currentPath === '/') {
          if (currentPath !== '/dashboard') {
            window.history.pushState({}, '', '/dashboard');
          }
          setActiveTab('dashboard');
        }
      } else {
        // Unauthenticated users: protect routes
        if (protectedPrefixes.some(p => currentPath.startsWith(p))) {
          window.history.replaceState({}, '', '/login');
        }
      }
    };

    checkAndRedirectRoute();
    window.addEventListener('popstate', checkAndRedirectRoute);
    return () => window.removeEventListener('popstate', checkAndRedirectRoute);
  }, [isAuthenticated, isCheckingAuth]);

  const handleClearCache = async () => {
    if (window.confirm('¿Estás seguro que deseas borrar la caché local y forzar una actualización de la aplicación?')) {
      localStorage.removeItem('keylacrm_master_db');
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (let reg of regs) {
          await reg.unregister();
        }
      }
      window.location.reload(true);
    }
  };

  const handleLogout = async () => {
    // Restaurar Demo si aplica enviando señal a N8N
    if (['9001', '9002', '9003'].includes(String(activeBusinessId))) {
      try {
        fetch('https://flow.seryiads.com/webhook/reset-demo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ business_id: activeBusinessId }),
          keepalive: true
        }).catch(e => console.error("Error trigger reset:", e));
      } catch (e) {
        console.error("Fetch failed", e);
      }
    }

    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setUserRole('master');
    setActiveTab('master-dashboard');
    setLoginEmail('');
    window.history.pushState({}, '', '/login?logout=true');
    window.dispatchEvent(new PopStateEvent('popstate'));
    showNotification("Has cerrado sesión correctamente", "success");
  };

  // Auto-Logout por Inactividad (15 min)
  useEffect(() => {
    if (!isAuthenticated) return;
    
    let timeoutId;
    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.warn("[Inactividad] Cerrando sesión automáticamente...");
        handleLogout();
      }, 900000); 
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => document.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      events.forEach(event => document.removeEventListener(event, resetTimer));
    };
  }, [isAuthenticated, activeBusinessId]);

  // Paginación y Filtrado del Panel Maestro (Movido arriba para evitar Error 310)
  const MASTER_ITEMS_PER_PAGE = 15;
  const filteredMasterDb = masterDb.filter(b => {
    if (masterFilterStatus === 'activos' && !b.activo) return false;
    if (masterFilterStatus === 'suspendidos' && b.activo) return false;
    
    if (masterSearchQuery.trim() !== '') {
      const q = masterSearchQuery.toLowerCase();
      const matchName = (b.nombre_negocio || '').toLowerCase().includes(q);
      const matchPhone = String(b.id_whatsapp || '').includes(q);
      const matchId = String(b.id || '').includes(q);
      if (!matchName && !matchPhone && !matchId) return false;
    }
    return true;
  });

  const masterTotalPages = Math.max(1, Math.ceil(filteredMasterDb.length / MASTER_ITEMS_PER_PAGE));
  
  useEffect(() => {
    if (masterCurrentPage > masterTotalPages) {
      setMasterCurrentPage(1);
    }
  }, [masterCurrentPage, masterTotalPages]);

  const paginatedMasterDb = filteredMasterDb.slice((masterCurrentPage - 1) * MASTER_ITEMS_PER_PAGE, masterCurrentPage * MASTER_ITEMS_PER_PAGE);

  if (isLoadingDb || isCheckingAuth) {
    return (
      <div className="min-h-screen bg-seryi-bg-dark flex flex-col justify-center items-center p-4">
        <img src="/icono-seryiads.png" alt="Keyla CRM" className="w-20 h-20 mb-4 animate-pulse drop-shadow-lg" />
        <h1 className="font-black text-3xl text-seryi-heading tracking-tight mb-1">Keyla CRM</h1>
        <p className="text-seryi-text text-sm font-bold tracking-widest uppercase animate-pulse">Cargando Sistema...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-seryi-bg-dark flex flex-col justify-center items-center p-4 font-sans relative overflow-hidden">
        {/* Elementos decorativos de fondo */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-seryi-green/10 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600/20 rounded-full blur-[120px]"></div>
        </div>

        <div className="bg-seryi-bg/80 backdrop-blur-xl border border-seryi-bg-light p-8 md:p-12 rounded-3xl shadow-2xl max-w-md w-full relative z-10 text-center space-y-6">
          <div className="flex flex-col items-center gap-3">
            <img src="/logo-icon.png" alt="Keyla CRM" className="w-16 h-16 drop-shadow-lg" />
            <div>
              <h1 className="font-black text-3xl text-seryi-heading tracking-tight">Keyla CRM</h1>
              <p className="text-xs text-seryi-green font-bold tracking-wider uppercase mt-1">Multi-Tenant</p>
            </div>
          </div>

          {window.location.search.includes('logout=true') && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-2xl text-xs font-semibold flex items-center justify-center gap-2 animate-fadeIn">
              <CheckCircle size={16} />
              <span>Has cerrado sesión correctamente</span>
            </div>
          )}
          
          {isSignUp ? (
            <form onSubmit={handleSignup} className="space-y-4 text-left pt-2">
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Tu Nombre</label>
                <input type="text" required className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all"
                  value={signupData.name} onChange={e => setSignupData({...signupData, name: e.target.value})} />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Nombre del Negocio</label>
                <input type="text" required className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all"
                  value={signupData.businessName} onChange={e => setSignupData({...signupData, businessName: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Teléfono</label>
                  <input type="tel" required className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all"
                    value={signupData.phone} onChange={e => setSignupData({...signupData, phone: e.target.value})} />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Email</label>
                  <input type="email" required className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all"
                    value={signupData.email} onChange={e => setSignupData({...signupData, email: e.target.value})} />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Contraseña</label>
                <input type="password" required minLength="6" className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all"
                  value={signupData.password} onChange={e => setSignupData({...signupData, password: e.target.value})} />
              </div>
              
              <div className="flex items-start gap-2 pt-2 pb-1">
                <input type="checkbox" required className="mt-1 bg-seryi-bg border border-seryi-bg-light text-seryi-green rounded"
                  checked={signupData.acceptsTerms} onChange={e => setSignupData({...signupData, acceptsTerms: e.target.checked})} />
                <span className="text-[10px] text-seryi-text leading-tight">
                  Al hacer clic en 'Crear Cuenta', aceptas nuestros Términos y Condiciones y autorizas a Seryiads/Keyla CRM al envío de comunicaciones comerciales y novedades vía correo electrónico.
                </span>
              </div>

              <button type="submit" disabled={isSigningUp} className="w-full bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-50 font-bold py-3 px-4 rounded-xl text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20">
                {isSigningUp ? "Creando Cuenta..." : "Crear Cuenta"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleEmailLogin} className="space-y-4 text-left pt-2">
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Correo Electrónico</label>
                <input 
                  type="email" 
                  required
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all"
                  placeholder="usuario@contoso.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Contraseña</label>
                <input 
                  type="password" 
                  required
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all"
                  placeholder="••••••••"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                />
              </div>

              <button 
                type="submit"
                disabled={isLoggingIn}
                className="w-full bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 disabled:opacity-50 font-bold py-3 px-4 rounded-xl text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20"
              >
                {isLoggingIn ? "Accediendo..." : "Iniciar Sesión"}
              </button>
            </form>
          )}

          <button 
            type="button" 
            onClick={() => setIsSignUp(!isSignUp)} 
            className="text-xs font-bold text-seryi-text hover:text-seryi-green transition-colors mt-4"
          >
            {isSignUp ? "¿Ya tienes cuenta? Inicia sesión" : "¿No tienes cuenta? Regístrate"}
          </button>

          {/* Divisor */}
          <div className="relative flex items-center justify-center my-2">
            <div className="border-t border-seryi-bg-light w-full"></div>
            <span className="bg-seryi-bg px-3 text-[10px] uppercase font-bold text-seryi-text absolute">o</span>
          </div>

          {/* Botón Google OAuth */}
          <button 
            type="button"
            onClick={handleGoogleLogin}
            className="w-full bg-white hover:bg-slate-100 text-slate-900 font-bold py-3 px-4 rounded-xl text-sm transition-all flex items-center justify-center gap-3 shadow-lg"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              <path fill="none" d="M1 1h22v22H1z" />
            </svg>
            Continuar con Google
          </button>
        </div>

        {/* NOTIFICACIÓN TOAST */}
        {toast.show && (
          <div className={`fixed bottom-6 right-6 ${toast.type === 'error' ? 'bg-rose-600' : toast.type === 'warning' ? 'bg-amber-600' : 'bg-emerald-600'} text-seryi-heading py-3 px-5 rounded-xl shadow-xl flex items-center gap-2 text-xs font-bold z-50 animate-bounce`}>
            {toast.type === 'error' ? <X size={16} /> : <Check size={16} />}
            {toast.message}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-seryi-bg-dark text-slate-100 font-sans">
      
      {/* MOBILE HEADER */}
      <header className="md:hidden flex items-center justify-between p-4 bg-seryi-bg border-b border-seryi-bg-light z-50 relative">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-seryi-green rounded-lg flex items-center justify-center shadow-lg shadow-seryi-green/20">
            <BarChart2 className="text-seryi-bg-dark" size={16} />
          </div>
          <div>
            <h1 className="font-extrabold text-base leading-none tracking-tight">CRM</h1>
          </div>
        </div>
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 bg-seryi-bg-light/30 rounded-lg text-seryi-text hover:text-white transition-colors"
        >
          {isMobileMenuOpen ? <X size={20} /> : <div className="space-y-1"><div className="w-5 h-0.5 bg-current"></div><div className="w-5 h-0.5 bg-current"></div><div className="w-5 h-0.5 bg-current"></div></div>}
        </button>
      </header>

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden relative">
        
      {/* 1. SIDEBAR GLOBAL */}
      <aside className={`${isMobileMenuOpen ? 'flex absolute inset-0 z-50' : 'hidden'} md:flex md:static w-full md:w-64 bg-seryi-bg border-b md:border-b-0 md:border-r border-seryi-bg-light flex-col justify-between p-6 shrink-0 h-full md:h-screen overflow-y-auto transition-all`}>
        <div className="space-y-8">
          {/* Brand Header */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-seryi-green rounded-xl flex items-center justify-center shadow-lg shadow-seryi-green/20">
                <BarChart2 className="text-seryi-bg-dark" size={20} />
              </div>
              <div>
                <h1 className="font-extrabold text-lg leading-none tracking-tight">CRM</h1>
                <span className="text-[10px] text-seryi-green font-semibold uppercase tracking-wider">{userRole === 'master' ? 'Consola Master' : 'Consola de Negocio'}</span>
              </div>
            </div>
          </div>

          {/* Menú Dinámico según Rol */}
          <nav className="flex flex-col gap-1.5 pb-8 md:pb-0">
            {userRole === 'master' ? (
              <>
                <button 
                  onClick={() => { setActiveTab('master-dashboard'); setIsMobileMenuOpen(false); }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-xs md:text-sm font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'master-dashboard' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                >
                  <BarChart2 size={16} /> <span>Dashboard</span>
                </button>
                <button 
                  onClick={() => { setActiveTab('master-clients'); setIsMobileMenuOpen(false); }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-xs md:text-sm font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'master-clients' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                >
                  <Building size={16} /> <span>Administrar Clientes ({masterDb.length})</span>
                </button>
                <button 
                  onClick={() => { setActiveTab('master-plans'); setIsMobileMenuOpen(false); }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-xs md:text-sm font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'master-plans' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                >
                  <DollarSign size={16} /> <span>Planes y Precios</span>
                </button>
                <button 
                  onClick={() => { setActiveTab('master-profile'); setIsMobileMenuOpen(false); }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-xs md:text-sm font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'master-profile' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                >
                  <ShieldCheck size={16} /> <span>Perfil y Accesos</span>
                </button>
              </>
            ) : (
              <>
                {/* Selector de Cliente que está editando (Solo visible cuando se impersona al cliente) */}
                {isImpersonating ? (
                  <div className="pb-3 border-b border-indigo-900/50 hidden md:block bg-seryi-bg-light/20 p-2.5 rounded-xl border">
                    <label className="text-[10px] text-seryi-green font-bold uppercase tracking-wider block mb-1 flex items-center gap-1">
                      <ShieldCheck size={12} /> Impersonando:
                    </label>
                    <select 
                      className="w-full bg-seryi-bg-dark border border-indigo-500/40 rounded-lg px-2 py-1.5 text-xs text-seryi-heading focus:outline-none"
                      value={activeBusinessId}
                      onChange={(e) => setActiveBusinessId(e.target.value)}
                    >
                      {masterDb.map(b => (
                        <option key={b.id} value={b.id}>{(b.activo ? '✅ ' : '❌ ') + b.nombre_negocio}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="pb-3 border-b border-seryi-bg-light hidden md:block">
                    <span className="text-[10px] text-seryi-text font-bold uppercase tracking-wider block">Mi Comercio:</span>
                    <p className="text-xs font-black text-seryi-heading truncate">{activeBusiness.nombre_negocio}</p>
                  </div>
                )}

                <button 
                  onClick={() => { setActiveTab('dashboard'); setIsMobileMenuOpen(false); }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-xs md:text-sm font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'dashboard' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                >
                  <LayoutDashboard size={16} /> <span>Consola Local</span>
                </button>

                {/* GRUPO 1: PEDIDOS Y VENTAS */}
                <div className="pt-2">
                  <span className="text-[10px] uppercase font-extrabold text-slate-400 px-2 tracking-wider flex items-center gap-1.5 mb-1">
                    <ShoppingBag size={12} className="text-seryi-green" /> 🛍️ Pedidos y Ventas
                  </span>
                  <div className="space-y-1 pl-1">
                    {showOrdersTab && (
                      <button 
                        onClick={() => { setActiveTab('pedidos'); setIsMobileMenuOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'pedidos' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                      >
                        <Store size={15} /> <span>Historial Pedidos</span>
                      </button>
                    )}
                    {showServicesTab && (
                      <button 
                        onClick={() => { setActiveTab('services'); setIsMobileMenuOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'services' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                      >
                        <DollarSign size={15} /> <span>{isRestaurant ? 'Menú y Precios' : 'Catálogo y Precios'}</span>
                      </button>
                    )}
                    {showInventoryTab && (
                      <button 
                        onClick={() => { setActiveTab('inventario'); setIsMobileMenuOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'inventario' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                      >
                        <Package size={15} /> <span>Inventario & Stock</span>
                      </button>
                    )}
                    {showFinanzasTab && (
                      <button 
                        onClick={() => { setActiveTab('finances'); setIsMobileMenuOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'finances' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                      >
                        <BarChart2 size={15} /> <span>Finanzas y Reportes</span>
                      </button>
                    )}
                    {showOrdersTab && (
                      <button 
                        onClick={() => window.open(`/cocina?id=${isImpersonating ? activeBusinessId : activeBusiness.id}`, '_blank')}
                        className="flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50"
                      >
                        <Activity size={15} /> <span>KDS Cocina 👨‍🍳</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* GRUPO 2: CITAS Y AGENDA */}
                <div className="pt-2">
                  <span className="text-[10px] uppercase font-extrabold text-slate-400 px-2 tracking-wider flex items-center gap-1.5 mb-1">
                    <CalendarDays size={12} className="text-seryi-green" /> 📅 Citas y Agenda
                  </span>
                  <div className="space-y-1 pl-1">
                    {showAppointmentsTab && (
                      <button 
                        onClick={() => { setActiveTab('agenda'); setIsMobileMenuOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'agenda' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                      >
                        <Calendar size={15} /> <span>Agenda Inteligente</span>
                      </button>
                    )}
                    {showAppointmentsTab && (
                      <button 
                        onClick={() => { setActiveTab('kanban'); setIsMobileMenuOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'kanban' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                      >
                        <LayoutDashboard size={15} /> <span>CRM Kanban</span>
                      </button>
                    )}
                    {showCampaignsTab && (
                      <button 
                        onClick={() => { setActiveTab('campanas'); setIsMobileMenuOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'campanas' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                      >
                        <Megaphone size={15} /> <span>Campañas WhatsApp</span>
                      </button>
                    )}
                    {showLoyaltyTab && (
                      <button 
                        onClick={() => { setActiveTab('fidelizacion'); setIsMobileMenuOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'fidelizacion' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                      >
                        <Gift size={15} /> <span>Fidelización & Cupones</span>
                      </button>
                    )}
                    {showStaffTab && (
                      <button 
                        onClick={() => { setActiveTab('staff'); setIsMobileMenuOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'staff' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                      >
                        <Users size={15} /> <span>Equipo/Staff</span>
                      </button>
                    )}
                    {showCommissionsTab && (
                      <button 
                        onClick={() => { setActiveTab('comisiones'); setIsMobileMenuOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'comisiones' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                      >
                        <Award size={15} /> <span>Comisiones & Nómina</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* GRUPO 3: MI SITIO WEB */}
                <div className="pt-2">
                  <span className="text-[10px] uppercase font-extrabold text-slate-400 px-2 tracking-wider flex items-center gap-1.5 mb-1">
                    <Globe size={12} className="text-seryi-green" /> 🌐 Mi Sitio Web
                  </span>
                  <div className="space-y-1 pl-1">
                    <button 
                      onClick={() => { setActiveTab('minisitio'); setIsMobileMenuOpen(false); }}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'minisitio' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                    >
                      <Globe size={15} /> <span>Personalizar Sitio</span>
                    </button>
                    {showMenuMediaTab && (
                      <button 
                        onClick={() => { setActiveTab('menu-media'); setIsMobileMenuOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'menu-media' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                      >
                        <Smartphone size={15} /> <span>Menú Multimedia 💎</span>
                      </button>
                    )}
                    <button 
                      onClick={() => window.open(`/app/${activeBusiness.slug || activeBusiness.id}`, '_blank')}
                      className="flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10"
                    >
                      <LinkIcon size={15} /> <span>Ver Mi Sitio Web ↗</span>
                    </button>
                  </div>
                </div>

                {/* GRUPO 4: WHATSAPP E IA */}
                <div className="pt-2">
                  <span className="text-[10px] uppercase font-extrabold text-slate-400 px-2 tracking-wider flex items-center gap-1.5 mb-1">
                    <Bot size={12} className="text-seryi-green" /> 🤖 WhatsApp e IA
                  </span>
                  <div className="space-y-1 pl-1">
                    <button 
                      onClick={() => { setActiveTab(showIdentidadTab ? 'config' : 'locked-Identidad IA y WhatsApp'); setIsMobileMenuOpen(false); }}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'config' ? 'bg-seryi-green/10 text-seryi-green border border-seryi-green/30' : 'text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light/50'}`}
                    >
                      <QrCode size={15} /> <span>Identidad IA y WhatsApp</span> {!showIdentidadTab && <Lock size={14} className="text-amber-500 ml-auto" />}
                    </button>
                    <button 
                      onClick={() => { setActiveTab('manual'); setIsMobileMenuOpen(false); }}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap w-full ${activeTab === 'manual' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30 shadow-sm' : 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 border border-transparent'}`}
                    >
                      <BookOpen size={15} /> <span>Keyla Academy (Manual 📖)</span>
                    </button>
                  </div>
                </div>
              </>
            )}
            
            <a
              href="/v2/cuenta"
              className="flex md:hidden items-center gap-3 px-4 py-3 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap text-seryi-text bg-seryi-bg-light/30 mt-4"
            >
              <KeyRound size={16} /> Mi cuenta y contraseña
            </a>

            {/* Botones móviles para Caché y Logout */}
            <button 
              onClick={handleClearCache}
              className="flex md:hidden items-center gap-3 px-4 py-3 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap text-rose-400 hover:text-rose-300 bg-rose-500/10 mt-4"
            >
              <RefreshCw size={16} /> Resetear Caché
            </button>
            
            <button 
              onClick={handleLogout}
              className="flex md:hidden items-center gap-3 px-4 py-3 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap text-seryi-text bg-seryi-bg-light/30 mt-2"
            >
              <LogOut size={16} /> Cerrar Sesión
            </button>
          </nav>
        </div>

        {/* Indicador de Estado del Sistema */}
        <div className="hidden md:flex flex-col gap-2 p-4 bg-seryi-bg-dark/50 rounded-2xl border border-seryi-bg-light/50 mt-auto">
          <div className="flex items-center gap-2 text-[11px] font-bold text-emerald-400">
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
            <span>{userRole === 'master' ? `Negocios Activos (${masterDb.filter(b => b.activo).length})` : 'Bot de WhatsApp Activo'}</span>
          </div>
          <p className="text-[10px] text-seryi-text leading-relaxed">
            {userRole === 'master'
              ? 'Todos tus asistentes están funcionando.'
              : 'La IA está respondiendo consultas en tiempo real.'
            }
          </p>
          <a
            href="/v2/cuenta"
            className="mt-2 text-[10px] text-seryi-text border border-seryi-bg-light bg-seryi-bg hover:bg-seryi-bg-light hover:text-seryi-heading py-1.5 px-2 rounded-lg font-bold transition-all w-full flex justify-center items-center gap-1"
          >
            <KeyRound size={10} /> Mi cuenta y contraseña
          </a>
          <button 
            onClick={handleClearCache}
            className="mt-2 text-[10px] text-rose-400 border border-rose-500/20 bg-rose-500/10 hover:bg-rose-500/20 py-1.5 px-2 rounded-lg font-bold transition-all w-full flex justify-center items-center gap-1"
          >
            <RefreshCw size={10} /> Resetear Caché
          </button>
          
          <button 
            onClick={handleLogout}
            className="mt-2 text-[10px] text-seryi-text border border-seryi-bg-light bg-seryi-bg hover:bg-seryi-bg-light hover:text-seryi-heading py-1.5 px-2 rounded-lg font-bold transition-all w-full flex justify-center items-center gap-1"
          >
            <LogOut size={10} /> Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* 2. MAIN CONTAINER */}
      <main className="flex-1 p-6 md:p-10 space-y-8 overflow-y-auto w-full max-w-full">

        {/* ========================================== */}
        {/* ROL: PORTAL MASTER */}
        {/* ========================================== */}
        {userRole === 'master' && activeTab === 'master-dashboard' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <img src="/logo-claro.png" alt="Keyla Master" className="w-48 object-contain drop-shadow-md" />
              <div className="hidden sm:block border-l border-seryi-bg-light pl-4 ml-2">
                <h2 className="text-xl font-black text-seryi-heading">Dashboard Central</h2>
                <p className="text-[10px] text-seryi-text uppercase tracking-widest font-bold">Consola de control global</p>
              </div>
            </div>
            {/* Métricas Master */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-2xl space-y-2">
                <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">MRR (Ingreso Mensual)</span>
                <p className="text-3xl font-black text-seryi-green">${totalMRR} USD</p>
                <div className="text-[10px] text-emerald-400 font-bold flex items-center gap-1">
                  <DollarSign size={12} /> Facturación recurrente
                </div>
              </div>

              <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-2xl space-y-2">
                <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Locales Activos</span>
                <p className="text-3xl font-black text-seryi-heading">{masterDb.filter(b => b.activo).length} / {masterDb.length}</p>
                <div className="text-[10px] text-seryi-green font-bold">Clientes con Bot activo</div>
              </div>

              <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-2xl space-y-2">
                <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Citas Totales</span>
                <p className="text-3xl font-black text-seryi-heading">{totalCitas}</p>
                <div className="text-[10px] text-seryi-text font-bold">Reservas sincronizadas</div>
              </div>

              <div className="bg-seryi-bg border border-indigo-950 bg-gradient-to-tr from-slate-900 to-indigo-950/40 p-6 rounded-2xl space-y-2">
                <span className="text-[10px] uppercase font-bold text-seryi-green tracking-wider">Estado Sistema</span>
                <p className="text-3xl font-black text-emerald-400">Óptimo</p>
                <div className="text-[10px] text-seryi-text font-bold">Infraestructura al 98.5%</div>
              </div>
            </div>

            {/* Listado Rápido */}
            <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-4">
              <h3 className="font-bold text-sm text-seryi-heading">Instancias de Clientes</h3>
              <div className="divide-y divide-slate-800">
                {masterDb.map(b => (
                  <div key={b.id} className="py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-seryi-green/10 rounded-xl flex items-center justify-center font-bold text-seryi-green border border-indigo-500/20">
                        {b.nombre_negocio.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-seryi-heading">{b.nombre_negocio}</span>
                          <span className="text-[10px] bg-seryi-bg-light text-seryi-heading px-2 py-0.5 rounded font-mono">{b.plan}</span>
                        </div>
                        <p className="text-xs text-seryi-text">WhatsApp: +{b.id_whatsapp} | Calendar: {b.google_calendar_id || "No conectado"}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => { setActiveBusinessId(b.id); setUserRole('client'); setActiveTab('dashboard'); setIsImpersonating(true); }}
                        className="bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-1.5 px-3 rounded-lg text-xs flex items-center gap-1 transition-all active:scale-95"
                      >
                        Gestionar Bot <ArrowRight size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {userRole === 'master' && activeTab === 'master-clients' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-black text-seryi-heading">Administrar Clientes</h2>
              <p className="text-xs text-seryi-text">Administra, suspende o configura planes.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Formulario de Alta */}
              <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-4 self-start">
                <h3 className="font-bold text-xs uppercase text-seryi-heading tracking-wider flex items-center gap-2">
                  <Plus size={14} className="text-seryi-green" /> Nuevo Cliente
                </h3>
                
                <div className="space-y-1">
                  <label className="text-[10px] uppercase text-seryi-text">Nombre del Comercio</label>
                  <DebouncedInput 
                    type="text" 
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                    placeholder="Peluquería El Bronx"
                    value={newBusiness.nombre_negocio}
                    onValueChange={(val) => setNewBusiness(prev => ({ ...prev, nombre_negocio: val }))}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase text-seryi-text">Tipo de Negocio</label>
                    <select 
                      className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-2 py-2 text-xs text-seryi-heading focus:outline-none"
                      value={newBusiness.tipo_negocio}
                      onChange={(e) => setNewBusiness(prev => ({ ...prev, tipo_negocio: e.target.value, ...getDefaultFeatures(e.target.value) }))}
                    >
                      <option value="Barbería">✂️  Barbería</option>
                      <option value="Estética">💅 Estética</option>
                      <option value="Clínica">🏥 Clínica</option>
                      <option value="Restaurante">🍽️  Restaurante / Comida</option>
                      <option value="General">🦄 General</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase text-seryi-text">Plan</label>
                    <select 
                      className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-2 py-2 text-xs text-seryi-heading focus:outline-none"
                      value={newBusiness.plan}
                      onChange={(e) => {
                        const selectedPlan = plansDb.find(p => p.name === e.target.value);
                        const planFeatures = {};
                        if (selectedPlan) {
                          MODULE_LIST.filter(m => !m.internal).forEach(m => {
                            if (selectedPlan[m.key] !== undefined) planFeatures[m.key] = selectedPlan[m.key];
                          });
                        }
                        setNewBusiness(prev => ({ 
                          ...prev, 
                          plan: e.target.value, 
                          monto_suscripcion: selectedPlan ? selectedPlan.price : prev.monto_suscripcion,
                          ...planFeatures
                        }));
                      }}
                    >
                      {plansDb.map(p => (
                        <option key={p.id} value={p.name}>{p.name} - ${p.price}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase text-seryi-text">Precio (USD)</label>
                    <DebouncedInput 
                      type="number" 
                      className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-2 py-2 text-xs text-emerald-400 font-bold focus:outline-none"
                      value={newBusiness.monto_suscripcion || 29}
                      onChange={(e) => setNewBusiness(prev => ({ ...prev, monto_suscripcion: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] uppercase text-seryi-text">Teléfono</label>
                  <DebouncedInput 
                    type="text" 
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none font-mono"
                    placeholder="573170000000"
                    value={newBusiness.id_whatsapp}
                    onValueChange={(val) => setNewBusiness(prev => ({ ...prev, id_whatsapp: val }))}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] uppercase text-seryi-text">Correo/Calendario</label>
                  <DebouncedInput 
                    type="text" 
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none font-mono"
                    placeholder="mi-negocio@gmail.com"
                    value={newBusiness.google_calendar_id}
                    onValueChange={(val) => setNewBusiness(prev => ({ ...prev, google_calendar_id: val }))}
                  />
                  <p className="text-[10px] text-seryi-text/70">Puede ser cualquier correo. Si no tiene Gmail, ponle una contraseña abajo.</p>
                </div>

                <div className="space-y-1">
                  <label htmlFor="new-business-password" className="text-[10px] uppercase text-seryi-text">Contraseña de acceso (opcional)</label>
                  <input
                    id="new-business-password" type="text" autoComplete="off"
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none font-mono"
                    placeholder="mínimo 8 caracteres"
                    value={newBusinessPassword}
                    onChange={(e) => setNewBusinessPassword(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] uppercase text-seryi-text">Módulos</label>
                  <div className="grid grid-cols-2 gap-2">
                    {MODULE_LIST.filter(f => !f.internal).map(f => (
                      <label key={f.key} className="flex items-center gap-2 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!newBusiness[f.key]}
                          onChange={(e) => setNewBusiness(prev => ({ ...prev, [f.key]: e.target.checked }))}
                        />
                        {f.label}{f.premium ? ' 💎' : ''}
                      </label>
                    ))}
                  </div>
                </div>

                <button 
                  onClick={handleCreateBusiness}
                  className="w-full bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-2.5 rounded-xl text-xs transition-all flex items-center justify-center gap-2"
                >
                  <Building size={14} /> Registrar en base de datos
                </button>
              </div>

              {/* Listado Clientes con Buscador y Acordeón */}
              <div className="lg:col-span-2 space-y-3">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mb-2">
                  <h3 className="font-bold text-xs uppercase text-seryi-heading tracking-wider shrink-0">Cartera de Clientes ({filteredMasterDb.length})</h3>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:w-48">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-seryi-text" size={14} />
                      <input 
                        type="text" 
                        placeholder="Buscar cliente..." 
                        value={masterSearchQuery}
                        onChange={(e) => { setMasterSearchQuery(e.target.value); setMasterCurrentPage(1); }}
                        className="w-full bg-seryi-bg border border-seryi-bg-light rounded-xl pl-9 pr-3 py-2 text-xs text-seryi-heading focus:outline-none"
                      />
                    </div>
                    <div className="relative shrink-0">
                      <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-seryi-text" size={14} />
                      <select 
                        value={masterFilterStatus}
                        onChange={(e) => { setMasterFilterStatus(e.target.value); setMasterCurrentPage(1); }}
                        className="bg-seryi-bg border border-seryi-bg-light rounded-xl pl-9 pr-8 py-2 text-xs text-seryi-heading appearance-none focus:outline-none"
                      >
                        <option value="todos">Todos</option>
                        <option value="activos">Activos</option>
                        <option value="suspendidos">Suspendidos</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {paginatedMasterDb.map(b => (
                    <div key={b.id} className="bg-seryi-bg border border-seryi-bg-light rounded-2xl overflow-hidden transition-all">
                      {/* Compact Header (Always Visible) */}
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-seryi-bg-light/30 transition-colors"
                        onClick={() => setExpandedClientId(expandedClientId === b.id ? null : b.id)}
                      >
                        <div className="flex items-center gap-3 overflow-hidden">
                          <div className={`w-2 h-2 rounded-full shrink-0 ${b.activo ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400'}`}></div>
                          <div className="flex flex-col truncate">
                            <span className="font-bold text-seryi-heading text-sm truncate">{b.nombre_negocio}</span>
                            <span className="font-mono text-[10px] text-seryi-text">+{b.id_whatsapp}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 shrink-0">
                          <span className={`hidden sm:inline-block text-[9px] font-bold px-2 py-0.5 rounded-full ${b.activo ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}>
                            {b.activo ? "Activo" : "Suspendido"}
                          </span>
                          <span className="hidden sm:inline-block text-xs font-black text-seryi-green">${b.monto_suscripcion}</span>
                          <button className="text-seryi-text hover:text-seryi-heading transition-colors">
                            {expandedClientId === b.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                          </button>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {expandedClientId === b.id && (
                        <div className="px-4 pb-4 pt-2 border-t border-seryi-bg-light/50 space-y-4 bg-seryi-bg-dark/20">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-[11px] text-seryi-text">
                            <div>
                              <p className="text-seryi-text font-bold mb-1">PLAN Y PRECIO</p>
                              <p className="text-seryi-heading font-semibold">{b.tipo_negocio} • Plan {b.plan}</p>
                              <p className="text-seryi-green font-black">${b.monto_suscripcion} USD/mes</p>
                            </div>
                            <div>
                              <p className="text-seryi-text font-bold mb-1 font-mono">ESTADO DE PAGO</p>
                              <p className={`font-bold ${b.payment_status === 'aprobado' ? 'text-emerald-400' : 'text-amber-400'}`}>
                                {(b.payment_status || 'pendiente').toUpperCase()}
                              </p>
                              {b.payment_proof_url && (
                                <a href={b.payment_proof_url} target="_blank" rel="noreferrer" className="text-seryi-green underline hover:text-emerald-400 truncate block mt-1">
                                  Ver Comprobante
                                </a>
                              )}
                              {b.payment_approved_at && <p className="text-[9px] mt-1 text-seryi-text/70">Aprobado: {new Date(b.payment_approved_at).toLocaleDateString()}</p>}
                            </div>
                            <div>
                              <p className="text-seryi-text font-bold mb-1 font-mono">CORREO / CALENDARIO</p>
                              <p className="font-mono text-seryi-heading truncate">{b.google_calendar_id || "Sin conectar"}</p>
                            </div>
                          </div>

                          <BusinessLoginPanel
                            key={b.id}
                            business={b}
                            onSaved={(email) => setMasterDb(prev => prev.map(x => (x.id === b.id ? { ...x, google_calendar_id: email } : x)))}
                          />

                          <div className="space-y-1.5">
                            <p className="text-[10px] text-seryi-text font-bold uppercase">Módulos Habilitados</p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {MODULE_LIST.map(f => {
                                const defaults = getDefaultFeatures(b.tipo_negocio);
                                const value = b[f.key] !== undefined ? b[f.key] : defaults[f.key];
                                return (
                                  <label key={f.key} className="flex items-center gap-1.5 bg-seryi-bg-dark border border-seryi-bg-light rounded-lg px-2 py-1.5 text-[11px] text-seryi-heading cursor-pointer hover:border-seryi-green/50 transition-colors">
                                    <input
                                      type="checkbox"
                                      checked={!!value}
                                      onChange={(e) => updateBusinessFeature(b.id, f.key, e.target.checked)}
                                    />
                                    {f.label}{f.premium ? ' 💎' : ''}
                                  </label>
                                );
                              })}
                            </div>
                          </div>

                          <div className="flex justify-between items-center pt-2">
                            <div className="flex flex-wrap gap-2">
                              {b.payment_status !== 'aprobado' && (
                                <button
                                  onClick={() => handleApprovePayment(b.id)}
                                  className="bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/20 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all"
                                  title="Aprobar pago manualmente"
                                >
                                  <CheckCircle size={12} /> Aprobar Pago
                                </button>
                              )}
                              {/* Toggle WhatsApp Conexión (Req #2) */}
                              <button
                                onClick={() => updateBusinessFeature(b.id, 'whatsapp_enabled', !(b.whatsapp_enabled !== false))}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all border ${
                                  b.whatsapp_enabled !== false
                                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20'
                                    : 'bg-rose-500/10 text-rose-400 border-rose-500/20 hover:bg-rose-500/20'
                                }`}
                                title="Activar o desactivar la conexión WhatsApp de este cliente"
                              >
                                {b.whatsapp_enabled !== false ? '📱 WA Activo' : '📳 WA Inactivo'}
                              </button>
                              <button 
                                onClick={() => toggleBusinessStatus(b.id)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${b.activo ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20' : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'}`}
                              >
                                {b.activo ? "Suspender" : "Habilitar"}
                              </button>
                              <button 
                                onClick={() => { setActiveBusinessId(b.id); setUserRole('client'); setActiveTab('dashboard'); setIsImpersonating(true); }}
                                className="bg-seryi-green/10 hover:bg-seryi-green/20 text-seryi-green border border-seryi-green/20 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 transition-all"
                              >
                                <UserCheck size={12} /> Entrar a Panel
                              </button>
                              <button 
                                onClick={() => handleDeleteBusiness(b.id)}
                                className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 transition-all ml-auto sm:ml-0"
                              >
                                <Trash2 size={12} /> Eliminar
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {paginatedMasterDb.length === 0 && (
                    <div className="p-8 text-center bg-seryi-bg border border-seryi-bg-light rounded-2xl">
                      <p className="text-seryi-text text-sm">No se encontraron clientes que coincidan con la búsqueda.</p>
                    </div>
                  )}
                </div>

                {/* Controles de Paginación */}
                {masterTotalPages > 1 && (
                  <div className="flex items-center justify-between pt-4">
                    <p className="text-[10px] text-seryi-text font-medium">Mostrando página {masterCurrentPage} de {masterTotalPages}</p>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setMasterCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={masterCurrentPage === 1}
                        className="p-2 rounded-xl bg-seryi-bg border border-seryi-bg-light text-seryi-heading hover:bg-seryi-bg-light disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button 
                        onClick={() => setMasterCurrentPage(prev => Math.min(masterTotalPages, prev + 1))}
                        disabled={masterCurrentPage === masterTotalPages}
                        className="p-2 rounded-xl bg-seryi-bg border border-seryi-bg-light text-seryi-heading hover:bg-seryi-bg-light disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ========================================== */}
        {/* ROL: PORTAL MASTER - PLANES */}
        {/* ========================================== */}
        {userRole === 'master' && activeTab === 'master-plans' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-black text-seryi-heading">Planes y Suscripciones</h2>
                <p className="text-xs text-seryi-text">Configura niveles y características.</p>
              </div>
              <button 
                onClick={handleExportDB}
                className="bg-emerald-600 hover:bg-emerald-700 text-seryi-heading font-bold py-2 px-4 rounded-xl text-xs flex items-center gap-2 transition-all shadow-lg"
              >
                <Save size={14} /> Backup
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Formulario de Plan */}
              <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-4 self-start">
                <h3 className="font-bold text-xs uppercase text-seryi-heading tracking-wider flex items-center gap-2">
                  <Plus size={14} className="text-seryi-green" /> Nuevo Plan
                </h3>
                
                <div className="space-y-1">
                  <label className="text-[10px] uppercase text-seryi-text">Nombre del Plan</label>
                  <DebouncedInput 
                    type="text" 
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                    placeholder="Ej. Enterprise"
                    value={newPlan.name}
                    onValueChange={(val) => setNewPlan(prev => ({ ...prev, name: val }))}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase text-seryi-text">Precio (USD)</label>
                    <DebouncedInput 
                      type="number" 
                      className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-2 py-2 text-xs text-emerald-400 font-bold focus:outline-none"
                      value={newPlan.price}
                      onValueChange={(val) => setNewPlan(prev => ({ ...prev, price: val }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase text-seryi-text">Max Staff</label>
                    <DebouncedInput 
                      type="number" 
                      className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-2 py-2 text-xs text-seryi-heading focus:outline-none"
                      value={newPlan.max_specialists}
                      onValueChange={(val) => setNewPlan(prev => ({ ...prev, max_specialists: parseInt(val) || 0 }))}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] uppercase text-seryi-text">Características (Comas)</label>
                  <DebouncedInput 
                    type="text" 
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                    placeholder="Soporte 24/7, IA Avanzada..."
                    value={newPlan.features}
                    onValueChange={(val) => setNewPlan(prev => ({ ...prev, features: val }))}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] uppercase text-seryi-text">Descripción</label>
                  <DebouncedInput 
                    type="text" 
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                    placeholder="Ideal para cadenas grandes"
                    value={newPlan.description}
                    onValueChange={(val) => setNewPlan(prev => ({ ...prev, description: val }))}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] uppercase text-seryi-text">Módulos incluidos</label>
                  <div className="grid grid-cols-1 gap-2">
                    {MODULE_LIST.filter(f => !f.internal).map(f => (
                      <label key={f.key} className="flex items-center gap-2 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!newPlan[f.key]}
                          onChange={(e) => setNewPlan(prev => ({ ...prev, [f.key]: e.target.checked }))}
                        />
                        {f.label}{f.premium ? ' 💎' : ''}
                      </label>
                    ))}
                  </div>
                </div>

                <button 
                  onClick={handleCreatePlan}
                  className="w-full bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-2.5 rounded-xl text-xs transition-all flex items-center justify-center gap-2"
                >
                  <Database size={14} /> Guardar Plan
                </button>
              </div>

              {/* Lista de Planes */}
              <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                {(plansDb || []).map(p => (
                  <div key={p.id || p.name} className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl flex flex-col justify-between">
                    <div className="space-y-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-black text-xl text-seryi-heading">{p.name}</h4>
                          <p className="text-[10px] text-seryi-text mt-1">{p.description}</p>
                        </div>
                        <p className="text-2xl font-black text-emerald-400">${p.price}</p>
                      </div>
                      
                      <div className="space-y-2 bg-seryi-bg-dark/50 p-4 rounded-xl border border-seryi-bg-light/50">
                        <div className="flex justify-between text-xs">
                          <span className="text-seryi-text">Max Staff:</span>
                          <span className="text-seryi-heading font-bold">{p.max_specialists}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <span className="text-[10px] font-bold text-seryi-green uppercase">Módulos:</span>
                        <div className="grid grid-cols-1 gap-1.5">
                          {MODULE_LIST.filter(f => !f.internal).map(f => (
                            <label key={f.key} className="flex items-center gap-1.5 bg-seryi-bg-dark/50 border border-seryi-bg-light/50 rounded-lg px-2 py-1.5 text-[11px] text-seryi-heading cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!p[f.key]}
                                onChange={(e) => updatePlanFeature(p.id, f.key, e.target.checked)}
                              />
                              {f.label}{f.premium ? ' 💎' : ''}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    <button 
                      onClick={() => handleDeletePlan(p.id)}
                      className="mt-6 w-full py-2 rounded-xl text-xs font-bold text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 transition-all flex items-center justify-center gap-2"
                    >
                      <Trash2 size={14}/> Eliminar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ROL: PERFIL MASTER & BÓVEDA DE CREDENCIALES */}
        {userRole === 'master' && (activeTab === 'master-profile' || activeTab === 'master-vault') && (
          <AdminCredentialsVault />
        )}

        {/* ========================================== */}
        {/* ROL: CLIENTE */}
        {/* ========================================== */}
        {userRole === 'client' && (
          <div className="space-y-6">
            
            {/* Cabecera de Alerta */}
            {isImpersonating && (
              <div className="bg-seryi-bg-light/40 border border-indigo-900/40 p-4 rounded-2xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-seryi-green/10 rounded-full flex items-center justify-center text-seryi-green">
                    <ShieldCheck size={16} />
                  </div>
                  <div>
                    <p className="text-xs text-indigo-300 font-bold">Impersonando: <span className="text-seryi-heading font-black">{activeBusiness.nombre_negocio}</span></p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setUserRole('master');
                    setActiveTab('master-dashboard');
                    if (masterDb && masterDb.length > 0) {
                      setActiveBusinessId(String(masterDb[0].id));
                    }
                  }}
                  className="bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-1.5 px-3 rounded-lg text-xs transition-all shadow-md active:scale-95"
                >
                  Regresar a Master
                </button>
              </div>
            )}

            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                {activeBusiness.payment_status !== 'aprobado' && (
                  <div className="bg-amber-500/10 border border-amber-500/20 p-6 rounded-2xl flex flex-col gap-3 animate-fadeIn">
                    <h3 className="text-amber-400 font-bold text-lg flex items-center gap-2">
                      <ShieldAlert size={20} /> 
                      {activeBusiness.payment_status === 'pendiente' ? 'Pago en Revisión' : 'Suscripción Inactiva / Pago Requerido'}
                    </h3>
                    <p className="text-sm text-amber-200/80">
                      {activeBusiness.payment_status === 'pendiente' 
                        ? 'Hemos recibido tu comprobante y lo estamos verificando. Pronto activaremos tu cuenta.' 
                        : 'Para continuar usando los servicios de Keyla CRM (incluido el Bot de WhatsApp), por favor sube el comprobante de tu pago (ej. enlace de Drive, Imgur, etc).'}
                    </p>
                    
                    <div className="flex gap-2 max-w-lg mt-2">
                      <input 
                        type="url"
                        placeholder="Pega aquí el enlace a tu comprobante (URL)..."
                        className="flex-1 bg-seryi-bg-dark border border-amber-500/20 rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-amber-400"
                        value={paymentProofUrlInput}
                        onChange={(e) => setPaymentProofUrlInput(e.target.value)}
                      />
                      <button 
                        onClick={handleSubmitPaymentProof}
                        className="bg-amber-500 hover:bg-amber-400 text-seryi-bg-dark font-bold px-4 py-2.5 rounded-xl text-xs transition-colors shrink-0 shadow-lg"
                      >
                        Enviar Comprobante
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="text-2xl font-extrabold text-seryi-heading">Consola de Control • {activeBusiness.nombre_negocio}</h2>
                    <p className="text-xs text-seryi-text">Monitorea tu asistente en tiempo real.</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button 
                      onClick={() => setIsNotificationsOpen(true)}
                      className="relative bg-seryi-bg-light/60 text-seryi-heading border border-seryi-border hover:border-seryi-green/50 font-bold py-2.5 px-3.5 rounded-xl text-xs flex items-center gap-2 transition-all shadow-lg active:scale-95"
                      title="Centro de Alertas y Sonido"
                    >
                      {notifications.some(n => !n.read) ? (
                        <BellRing size={15} className="text-seryi-green animate-bounce" />
                      ) : (
                        <Bell size={15} className="text-seryi-text" />
                      )}
                      <span className="hidden sm:inline">Alertas</span>
                      {notifications.filter(n => !n.read).length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-extrabold animate-pulse">
                          {notifications.filter(n => !n.read).length}
                        </span>
                      )}
                    </button>
                    <button 
                      onClick={() => setActiveTab('manual')}
                      className="bg-blue-600/20 text-blue-300 border border-blue-500/30 hover:bg-blue-600/30 font-bold py-2.5 px-4 rounded-xl text-xs flex items-center gap-2 transition-all shadow-lg active:scale-95"
                    >
                      <BookOpen size={14} /> Keyla Academy (Manual)
                    </button>
                    <button 
                      onClick={syncWithN8N}
                      className="bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-2.5 px-5 rounded-xl text-xs flex items-center gap-2 transition-all shadow-lg active:scale-95"
                    >
                      <RefreshCw size={14} className={isSyncing ? "animate-spin" : ""} />
                      {isSyncing ? "Sincronizando..." : "Actualizar"}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-2xl space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Chats IA Consumidos</span>
                      <span className="px-2 py-0.5 rounded-full bg-seryi-bg-light text-[9px] font-bold text-seryi-green uppercase border border-seryi-green/20">
                        Plan {activeBusiness.plan_actual || 'Emprendedor'}
                      </span>
                    </div>
                    <p className="text-3xl font-black text-seryi-heading">
                      {activeBusiness.messages_used || 142} <span className="text-sm font-semibold text-seryi-text">/ {activeBusiness.message_limit || 500}</span>
                    </p>
                    <div className="w-full bg-seryi-bg-dark rounded-full h-1.5 mt-2 overflow-hidden border border-seryi-bg-light">
                      <div className={`h-1.5 rounded-full transition-all duration-500 ${((activeBusiness.messages_used || 142) / (activeBusiness.message_limit || 500)) > 0.9 ? 'bg-red-500' : 'bg-seryi-green'}`} style={{ width: `${Math.min(100, ((activeBusiness.messages_used || 142) / (activeBusiness.message_limit || 500)) * 100)}%` }}></div>
                    </div>
                  </div>

                  <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-2xl space-y-2">
                    <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Citas Registradas</span>
                    <p className="text-3xl font-black text-seryi-heading">{activeBusiness.appointments?.length > 0 ? activeBusiness.appointments.length : 12}</p>
                    <div className="text-[10px] text-emerald-400 font-bold flex items-center gap-1">
                      <Activity size={12} /> Agenda {(!activeBusiness.appointments || activeBusiness.appointments.length === 0) && <span className="text-emerald-500/80 ml-1">(+4 hoy)</span>}
                    </div>
                  </div>

                  <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-2xl space-y-2">
                    <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Staff Disponible</span>
                    <p className="text-3xl font-black text-seryi-heading">
                      {activeBusiness.staff?.length > 0 ? activeBusiness.staff.filter(s => s.available).length : 2} / {activeBusiness.staff?.length > 0 ? activeBusiness.staff.length : 2}
                    </p>
                    <div className="text-[10px] text-seryi-text font-bold">Personal activo</div>
                  </div>

                  <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-2xl space-y-2">
                    <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Instancia de WhatsApp</span>
                    <p className="text-lg font-mono font-bold text-seryi-green">+{activeBusiness.id_whatsapp}</p>
                    <div className="text-[10px] text-seryi-text font-bold">Línea conectada</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'minisitio' && (
              <div className="space-y-6 max-w-3xl animate-fadeIn">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-seryi-bg-light/80 pb-4">
                  <div>
                    <h2 className="text-xl font-black text-seryi-heading flex items-center gap-3">
                      <Globe className="text-seryi-green" /> Personalización de Mi Sitio Web
                    </h2>
                    <p className="text-xs text-seryi-text mt-1">
                      Configura el banner, logo, horarios y redes sociales de tu portal web en vivo.
                    </p>
                  </div>
                  <button 
                    onClick={() => window.open(`/app/${activeBusiness.slug || activeBusiness.id}`, '_blank')}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2.5 px-5 rounded-xl text-xs flex items-center gap-2 shadow-lg transition-all"
                  >
                    <LinkIcon size={14} /> Abrir Mi Sitio Web en Vivo ↗
                  </button>
                </div>

                <div className="bg-seryi-bg-light/20 border border-seryi-bg-light p-6 rounded-3xl space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-seryi-text">URL de Banner Principal (Portada)</label>
                      <input 
                        type="text"
                        placeholder="https://..."
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
                        value={activeBusiness.cover_image_url || ''}
                        onChange={(e) => updateActiveBusinessField('cover_image_url', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-seryi-text">URL de Logo del Negocio</label>
                      <input 
                        type="text"
                        placeholder="https://..."
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
                        value={activeBusiness.logo_url || ''}
                        onChange={(e) => updateActiveBusinessField('logo_url', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-seryi-text">Horario de Atención Público</label>
                      <input 
                        type="text"
                        placeholder="Ej: Lun-Sáb 8:00 AM - 8:00 PM"
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
                        value={activeBusiness.horario_atencion || activeBusiness.horario || ''}
                        onChange={(e) => {
                          updateActiveBusinessField('horario_atencion', e.target.value);
                          updateActiveBusinessField('horario', e.target.value);
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-seryi-text">Dirección / Ubicación del Local</label>
                      <input 
                        type="text"
                        placeholder="Ej: Cra 45 # 12-34, Bogotá"
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
                        value={activeBusiness.address || ''}
                        onChange={(e) => updateActiveBusinessField('address', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-seryi-text">Instagram</label>
                      <input 
                        type="text"
                        placeholder="@tucuenta o URL de Instagram"
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
                        value={activeBusiness.instagram || ''}
                        onChange={(e) => updateActiveBusinessField('instagram', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-seryi-text">Facebook</label>
                      <input 
                        type="text"
                        placeholder="URL de Facebook"
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
                        value={activeBusiness.facebook || ''}
                        onChange={(e) => updateActiveBusinessField('facebook', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="pt-2">
                    <button 
                      onClick={saveBusinessSettings}
                      className="w-full bg-seryi-green hover:bg-seryi-green/80 text-seryi-bg-dark font-black py-3.5 px-6 rounded-xl text-xs flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95"
                    >
                      <Save size={16} /> Guardar Personalización del Sitio Web
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'config' && (
              <div className="space-y-6 max-w-3xl">
                <div className="mt-8 border-t border-slate-100 pt-8">
                  <h2 className="text-xl font-black text-seryi-heading flex items-center gap-3">
                    <UserCheck className="text-seryi-green" /> Identidad del Asistente <TooltipIcon text="Define la personalidad, nombre y reglas de Keyla para este negocio." />
                  </h2>
                  <p className="text-xs text-seryi-text">Configura la personalidad y reglas que el sistema inyectará al robot de este local.</p>
                </div>
                  {/* Botón para abrir modal de Plantillas */}
                  {isImpersonating && (
                    <div className="p-5 bg-seryi-bg/20 border border-seryi-bg-light rounded-3xl space-y-4 flex flex-col items-center justify-center text-center">
                      <Sparkles className="text-seryi-green" size={32} />
                      <div>
                        <h3 className="font-extrabold text-sm text-seryi-heading uppercase tracking-wider">
                          Galería de Plantillas Integradas
                        </h3>
                        <p className="text-xs text-seryi-text mt-1">
                          (IA + Productos + Módulos) Exclusivo Master
                        </p>
                      </div>
                      <button 
                        onClick={() => setIsTemplatesModalOpen(true)}
                        className="bg-seryi-green hover:bg-seryi-green/80 text-seryi-bg-dark font-black py-3 px-6 rounded-xl transition-all shadow-lg active:scale-95 flex items-center gap-2"
                      >
                        <Sparkles size={16} />
                        Ver Plantillas
                      </button>
                    </div>
                  )}


                <div className={`p-5 rounded-3xl border flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all ${showBotEnabled ? 'bg-emerald-950/20 border-emerald-800/40' : 'bg-rose-950/20 border-rose-800/40'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center border shrink-0 ${showBotEnabled ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-rose-500/10 border-rose-500/20 text-rose-400'}`}>
                      <Smartphone size={22} />
                    </div>
                    <div>
                      <h4 className="font-extrabold text-xs text-seryi-heading uppercase tracking-wider">
                        Respuesta de WhatsApp {showBotEnabled ? <span className="text-emerald-400 font-black">ENCENDIDO</span> : <span className="text-rose-400 font-black">PAUSADO</span>} <TooltipIcon text="Apaga el bot globalmente para TODOS los chats (ej. fuera de horario). Para silenciar un solo chat, escribe #pausar en ese chat desde el WhatsApp del negocio (y #activar para devolverlo)." />
                      </h4>
                      <p className="text-[11px] text-seryi-heading mt-0.5">
                        {showBotEnabled ? "El asistente está respondiendo consultas." : "El asistente está en pausa para TODOS los chats."}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => updateBusinessFeature(activeBusinessId, 'bot_activo', !showBotEnabled)}
                    className={`px-4 py-2.5 rounded-xl font-bold text-xs shrink-0 transition-all shadow-md active:scale-95 ${showBotEnabled ? 'bg-rose-600 hover:bg-rose-700 text-seryi-heading' : 'bg-emerald-600 hover:bg-emerald-700 text-seryi-heading'}`}
                  >
                    {showBotEnabled ? "Pausar Bot" : "Encender Bot"}
                  </button>
                </div>

                {/* GESTOR DE CHATS PAUSADOS */}
                <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-4">
                  <div>
                    <h3 className="font-extrabold text-xs text-seryi-heading flex items-center gap-2 uppercase tracking-wider">
                      <Users className="text-seryi-green" size={16} /> Atención Humana Directa <TooltipIcon text="Chats silenciados. Un asesor puede silenciar al bot en el chat de un cliente escribiendo #pausar desde el WhatsApp del negocio, y devolverlo con #activar. El bot reacciona con un emoji y no le escribe nada al cliente." />
                    </h3>
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col sm:flex-row items-center gap-2">
                      <input
                        type="text"
                        placeholder="Nombre (opcional)"
                        value={manualMuteName}
                        onChange={(e) => setManualMuteName(e.target.value)}
                        className="w-full sm:w-1/3 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none placeholder:text-seryi-text"
                      />
                      <div className="flex w-full sm:w-2/3 items-center gap-2">
                        <select
                          value={selectedCountryPrefix}
                          onChange={(e) => setSelectedCountryPrefix(e.target.value)}
                          className="bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2.5 text-xs text-seryi-heading font-mono focus:outline-none shrink-0"
                        >
                          <option value="57">🇨🇴 +57</option>
                          <option value="52">🇲🇽 +52</option>
                          <option value="1">🇺🇸 +1</option>
                          <option value="54">🇦🇷 +54</option>
                          <option value="">🌎 Otro</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Número local"
                          value={manualMutePhone}
                          onChange={(e) => setManualMutePhone(e.target.value)}
                          className="flex-1 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading font-mono focus:outline-none placeholder:text-seryi-text"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => { handleToggleMutePhone(manualMutePhone, manualMuteName); setManualMutePhone(''); setManualMuteName(''); }}
                      className="bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 px-4 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all active:scale-95 w-full"
                    >
                      <Plus size={14} /> Silenciar
                    </button>
                  </div>

                  <div className="space-y-2 pt-1">
                    {activeBusiness.muted_phones?.length > 0 && (
                      <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-seryi-text" size={14} />
                        <input
                          type="text"
                          placeholder="🔍 Buscar por nombre o número de teléfono..."
                          value={mutedPhoneSearch}
                          onChange={(e) => setMutedPhoneSearch(e.target.value)}
                          className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl pl-9 pr-4 py-2.5 text-xs text-seryi-heading focus:outline-none placeholder:text-seryi-text transition-colors focus:border-seryi-green/50"
                        />
                      </div>
                    )}
                    {(!activeBusiness.muted_phones || activeBusiness.muted_phones.length === 0) ? (
                      <p className="text-xs text-seryi-text py-2">Aún no has silenciado ningún número. Cuando agregues uno, aparecerá aquí y el bot dejará de responderle.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {(() => {
                          const filteredMutedPhones = activeBusiness.muted_phones.filter(phone => {
                            if (!mutedPhoneSearch) return true;
                            const searchLower = mutedPhoneSearch.toLowerCase();
                            const name = activeBusiness.muted_phones_metadata?.[phone] || "Desconocido";
                            return phone.toLowerCase().includes(searchLower) || name.toLowerCase().includes(searchLower);
                          });
                          
                          if (filteredMutedPhones.length === 0) {
                            return <div className="col-span-full text-center py-4 text-xs text-seryi-text">No se encontraron contactos silenciados.</div>;
                          }
                          
                          return filteredMutedPhones.map(phone => {
                            const name = activeBusiness.muted_phones_metadata?.[phone] || "Desconocido";
                          return (
                          <div key={phone} className="bg-seryi-bg-dark border border-seryi-bg-light/80 p-3 rounded-2xl flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1">
                              <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse shrink-0"></span>
                              {editingMutedPhone === phone ? (
                                <div className="flex flex-col gap-1 w-full max-w-[160px]">
                                  <input
                                    type="text"
                                    value={editMutedPhoneName}
                                    onChange={(e) => setEditMutedPhoneName(e.target.value)}
                                    className="bg-seryi-bg border border-seryi-green/50 rounded-lg px-2 py-1 text-[10px] text-seryi-heading focus:outline-none"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleUpdateMutedPhoneName(phone, editMutedPhoneName);
                                      if (e.key === 'Escape') setEditingMutedPhone(null);
                                    }}
                                  />
                                  <div className="flex items-center gap-1">
                                    <button onClick={() => handleUpdateMutedPhoneName(phone, editMutedPhoneName)} className="text-seryi-green hover:bg-seryi-green/20 p-1 rounded"><Check size={12}/></button>
                                    <button onClick={() => setEditingMutedPhone(null)} className="text-rose-400 hover:bg-rose-400/20 p-1 rounded"><X size={12}/></button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex flex-col group relative overflow-hidden flex-1">
                                  <div className="flex items-center gap-1.5">
                                    <span 
                                      className={`text-[10px] font-bold truncate max-w-[120px] cursor-pointer hover:text-seryi-green transition-colors ${!activeBusiness.muted_phones_metadata?.[phone] ? 'text-seryi-text italic' : 'text-seryi-text'}`}
                                      onClick={() => { setEditingMutedPhone(phone); setEditMutedPhoneName(name === "Desconocido" ? "" : name); }}
                                    >
                                      {name}
                                    </span>
                                    <button 
                                      onClick={() => { setEditingMutedPhone(phone); setEditMutedPhoneName(name === "Desconocido" ? "" : name); }}
                                      className="opacity-0 group-hover:opacity-100 text-seryi-text hover:text-seryi-green transition-opacity shrink-0"
                                    >
                                      <Edit2 size={10} />
                                    </button>
                                  </div>
                                  <span className="font-mono text-[11px] text-seryi-heading">+{phone}</span>
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleToggleMutePhone(phone)}
                              className="text-[10px] bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-bold px-2.5 py-1 rounded-lg transition-all ml-2"
                            >
                              Reactivar
                            </button>
                          </div>
                          );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase tracking-wider font-bold text-seryi-text">Nombre del Local</label>
                      <input 
                        type="text" 
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none"
                        value={activeBusiness.nombre_negocio}
                        onChange={(e) => updateActiveBusinessField('nombre_negocio', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase tracking-wider font-bold text-seryi-text">Categoría</label>
                      <select 
                        disabled={!isImpersonating}
                        className={`w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none ${!isImpersonating ? 'opacity-60 cursor-not-allowed bg-seryi-bg-dark/40' : ''}`}
                        value={activeBusiness.tipo_negocio}
                        onChange={(e) => updateActiveBusinessField('tipo_negocio', e.target.value)}
                      >
                        <option value="Barbería">✂️  Barbería</option>
                        <option value="Estética">💅 Estética</option>
                        <option value="Clínica">🏥 Clínica</option>
                        <option value="Restaurante">🍽️  Restaurante</option>
                        <option value="General">🦄 General</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase tracking-wider font-bold text-seryi-text">URL / Slug Público</label>
                      <input 
                        type="text" 
                        placeholder="ej: mibusiness"
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading font-mono focus:outline-none"
                        value={activeBusiness.slug || ''}
                        onChange={(e) => updateActiveBusinessField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase tracking-wider font-bold text-seryi-text">ID WhatsApp</label>
                      <input 
                        type="text" 
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading font-mono focus:outline-none"
                        value={activeBusiness.id_whatsapp || ''}
                        onChange={(e) => updateActiveBusinessField('id_whatsapp', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase tracking-wider font-bold text-seryi-text">Correo / Calendario</label>
                      <input 
                        type="text" 
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading font-mono focus:outline-none"
                        value={activeBusiness.google_calendar_id || ''}
                        onChange={(e) => updateActiveBusinessField('google_calendar_id', e.target.value)}
                      />
                      {showAppointmentsTab && (
                        <div className="pt-2">
                          {activeBusiness.google_refresh_token ? (
                            <button
                              onClick={handleConnectCalendar}
                              className="w-full flex items-center justify-center gap-2 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 border border-emerald-500/20 px-3 py-2 rounded-xl text-xs font-bold transition-all"
                              title="Haz clic para forzar la reconexión si el token expiró"
                            >
                              <Check size={14} /> Calendario Vinculado (Reconectar)
                            </button>
                          ) : (
                            <button
                              onClick={handleConnectCalendar}
                              className="w-full flex items-center justify-center gap-2 bg-blue-600/20 text-blue-400 border border-blue-500/30 px-3 py-2 rounded-xl text-xs font-bold hover:bg-blue-600/30 transition-all"
                            >
                              <Calendar size={14} /> Conectar Google Calendar
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-seryi-text">Identidad Base (Eres Keyla...)</label>
                    <textarea 
                      rows={6}
                      className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-4 text-xs text-seryi-heading focus:outline-none font-sans leading-relaxed"
                      value={(activeBusiness.estilo_clean && activeBusiness.estilo_clean.trim()) ? activeBusiness.estilo_clean : getCleanEstilo(activeBusiness.estilo)}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateActiveBusinessField('estilo_clean', val);
                        updateActiveBusinessField('estilo', val);
                        setStoredFeatureOverride(activeBusinessId, 'estilo_clean', val);
                      }}
                    />
                  </div>

                  <div className="space-y-1 mt-4">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-emerald-400">Instrucciones Adicionales del Negocio (Promociones, Catálogo)</label>
                    <textarea 
                      rows={5}
                      className="w-full bg-slate-800/80 border border-emerald-500/30 rounded-xl p-4 text-xs text-slate-300 focus:outline-none focus:border-emerald-500/60 font-sans leading-relaxed"
                      placeholder="Ej: Todos los martes 2x1 en cortes. \nEj: No tenemos parqueadero disponible."
                      value={activeBusiness.custom_instructions || (activeBusiness.features && activeBusiness.features.custom_instructions) || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateActiveBusinessField('custom_instructions', val);
                        setStoredFeatureOverride(activeBusinessId, 'custom_instructions', val);
                      }}
                    />
                  </div>

                  <button 
                    onClick={saveBusinessSettings}
                    className="w-full bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-3 px-6 rounded-xl text-xs flex items-center justify-center gap-2 transition-all shadow-md active:scale-95"
                  >
                    <Save size={14} /> Guardar Ajustes
                  </button>
                </div>

                {/* TARJETA DE PROVISIÓN DE WHATSAPP (BLOQUEO POR ESTADO O PLAN - REQ #6) */}
                {!isSuperAdmin && (isTrialAccount || isDemoAccount || !activeBusiness.activo || activeBusiness.status === 'inactivo' || activeBusiness.status === 'pendiente_pago') ? (
                  <div className="bg-gradient-to-r from-orange-500/20 to-red-600/20 border border-orange-500/30 p-6 rounded-3xl mt-6 flex flex-col md:flex-row items-center gap-6 shadow-xl">
                    <div className="bg-orange-500/10 p-4 rounded-full text-orange-400 shrink-0 hidden md:block">
                      <Lock size={32} />
                    </div>
                    <div className="flex-1 text-center md:text-left space-y-2">
                      <h4 className="font-black text-lg text-seryi-heading">🚀 Conexión de WhatsApp Restringida</h4>
                      <p className="text-xs text-seryi-text leading-relaxed">
                        Tu cuenta está inactiva, pendiente de pago o en un plan gratuito que no incluye automatización con WhatsApp Evolution API.
                      </p>
                    </div>
                    <button 
                      onClick={() => window.open('/planes', '_blank')}
                      className="shrink-0 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-black py-3 px-6 rounded-xl text-xs transition-all shadow-lg shadow-orange-500/30 w-full md:w-auto text-center cursor-pointer"
                    >
                      🚀 Actualizar Plan Ahora
                    </button>
                  </div>
                ) : (
                <div className="bg-gradient-to-r from-slate-900 via-slate-900 to-indigo-950/40 border border-seryi-bg-light p-6 rounded-3xl space-y-6 mt-6">
                  <div className="flex items-center justify-between border-b border-seryi-bg-light/80 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-seryi-green/10 border border-indigo-500/20 rounded-xl flex items-center justify-center">
                        <Smartphone className="text-seryi-green" size={18} />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm text-seryi-heading">Agregar Trabajador 24/7 (WhatsApp)</h3>
                        <p className="text-xs text-seryi-text">Vincula tu número para que tu asistente virtual trabaje por ti 24/7.</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                    <div className="md:col-span-3 space-y-3">
                      <h4 className="text-xs font-bold text-seryi-heading uppercase tracking-wide">Estado</h4>
                      <p className="text-xs text-seryi-text leading-relaxed">
                        {waStatus === 'connected' 
                          ? "El código de conexión QR ha sido generado. Abre WhatsApp en tu celular > Dispositivos vinculados > Vincular un dispositivo y apunta tu cámara hacia la pantalla."
                          : waStatus === 'loading'
                          ? "Estableciendo conexión segura con el aprovisionador de Evolution API. Por favor espera unos segundos..."
                          : "Tu asistente de IA no está activa actualmente. Para iniciar la automatización de WhatsApp, haz clic en el botón para generar tu instancia e iniciar el lector de código QR."}
                      </p>

                      <div className="flex items-center gap-4 pt-2">
                        {waStatus === 'disconnected' && (
                          <button
                            onClick={handleConnectWhatsApp}
                            className="bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-2.5 px-5 rounded-xl text-xs flex items-center gap-2 transition-all shadow-md hover:scale-[1.02] active:scale-95"
                          >
                            <Sparkles size={14} /> Vincular mi WhatsApp
                          </button>
                        )}

                        {waStatus === 'qr_ready' && (
                          <div className="flex gap-2">
                            <button
                              onClick={handleConnectWhatsApp}
                              className="bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-2.5 px-5 rounded-xl text-xs flex items-center gap-2 transition-all"
                            >
                              <RefreshCw size={12} className="animate-spin" /> Regenerar QR
                            </button>
                            <button
                              onClick={handleDisconnectWhatsApp}
                              className="bg-seryi-bg-light hover:bg-slate-700 text-seryi-heading font-bold py-2.5 px-5 rounded-xl text-xs transition-all"
                            >
                              Cancelar
                            </button>
                          </div>
                        )}

                        {waStatus === 'connected' && (
                          <button
                            onClick={handleDisconnectWhatsApp}
                            className="bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-400 font-bold py-2.5 px-5 rounded-xl text-xs flex items-center gap-2 transition-all hover:scale-[1.02] active:scale-95"
                          >
                            <Trash2 size={14} /> Desconectar Instancia
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="md:col-span-2 flex flex-col items-center justify-center p-4 bg-seryi-bg-dark/60 border border-seryi-bg-light rounded-2xl min-h-[180px]">
                      {waStatus === 'loading' ? (
                        <div className="flex flex-col items-center gap-2 text-seryi-green">
                          <RefreshCw className="animate-spin" size={24} />
                          <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Creando Instancia...</span>
                        </div>
                      ) : waStatus === 'qr_ready' && qrCode ? (
                        <div className="space-y-3 flex flex-col items-center">
                          <div className="bg-white p-3 rounded-xl shadow-lg border border-slate-200">
                            <img 
                              src={qrCode} 
                              alt="Código QR de Vinculación" 
                              className="w-36 h-36 object-contain"
                            />
                          </div>
                          <span className="text-[9px] uppercase font-bold text-seryi-green tracking-wider animate-pulse flex items-center gap-1.5">
                            <RefreshCw size={10} className="animate-spin" /> Esperando escaneo...
                          </span>
                        </div>
                      ) : waStatus === 'connected' ? (
                        <div className="flex flex-col items-center gap-2 text-emerald-400">
                          <div className="w-12 h-12 bg-emerald-500/10 border border-emerald-500/20 rounded-full flex items-center justify-center animate-bounce">
                            <Check size={20} />
                          </div>
                          <span className="text-[10px] uppercase font-bold tracking-wider">Bot Activo</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-slate-600">
                          <Smartphone size={32} />
                          <span className="text-[9px] uppercase font-bold text-seryi-text tracking-wider text-center">WhatsApp Desvinculado</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                )}
              </div>
            )}

            {/* TABLA CLIENTE: EQUIPO STAFF */}
            {activeTab === 'staff' && (
              <div className="space-y-6 max-w-4xl">
                <div>
                  <h2 className="text-xl font-extrabold text-seryi-heading">Equipo de Trabajo</h2>
                  <p className="text-xs text-seryi-text">Controla quiénes están disponibles en la base de datos para recibir citas de la IA de WhatsApp.</p>
                </div>

                <div className="bg-gradient-to-r from-seryi-bg to-seryi-bg-dark border border-seryi-bg-light p-4 rounded-2xl flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-seryi-green/10 flex items-center justify-center text-seryi-green shrink-0">
                      <Award size={20} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-seryi-heading">¿Deseas liquidar comisiones y exportar nómina?</p>
                      <p className="text-[11px] text-seryi-text">Abre el nuevo módulo para calcular ganancias, márgenes y enviar desgloses por WhatsApp.</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveTab('comisiones')}
                    className="px-4 py-2 bg-seryi-green text-seryi-bg-dark hover:brightness-110 rounded-xl text-xs font-bold transition-all shrink-0 flex items-center gap-1.5"
                  >
                    <span>Ir a Nómina</span>
                    <ArrowRight size={13} />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Creador */}
                  <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-4 self-start">
                    <h3 className="font-bold text-xs uppercase text-seryi-heading tracking-wider">Nuevo Miembro</h3>
                    
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase text-seryi-text">Nombre Profesional</label>
                      <input 
                        type="text" 
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                        placeholder="Mateo"
                        value={newStaff.name}
                        onChange={(e) => setNewStaff(prev => ({ ...prev, name: e.target.value }))}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] uppercase text-seryi-text">Especialidad</label>
                      <input 
                        type="text" 
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                        placeholder="Diseños y Barba"
                        value={newStaff.specialty}
                        onChange={(e) => setNewStaff(prev => ({ ...prev, specialty: e.target.value }))}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] uppercase text-seryi-text">Correo (Gmail)</label>
                      <input 
                        type="email" 
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                        placeholder="empleado@gmail.com"
                        value={newStaff.google_calendar_id}
                        onChange={(e) => setNewStaff(prev => ({ ...prev, google_calendar_id: e.target.value }))}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] uppercase text-seryi-text">Teléfono (WhatsApp)</label>
                      <input 
                        type="text" 
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                        placeholder="57300..."
                        value={newStaff.telefono}
                        onChange={(e) => setNewStaff(prev => ({ ...prev, telefono: e.target.value }))}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] uppercase text-seryi-text">PIN de Acceso Portal Staff</label>
                      <input 
                        type="password" 
                        maxLength={4}
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none tracking-widest text-center font-bold"
                        placeholder="****"
                        value={newStaff.pin_code}
                        onChange={(e) => setNewStaff(prev => ({ ...prev, pin_code: e.target.value.replace(/\D/g, '') }))}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] uppercase text-seryi-text">Comisión (%)</label>
                      <input 
                        type="number" 
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                        placeholder="50"
                        value={newStaff.comision_porcentaje}
                        onChange={(e) => setNewStaff(prev => ({ ...prev, comision_porcentaje: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>

                    <button 
                      onClick={handleAddStaff}
                      className="w-full bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-2.5 rounded-xl text-xs transition-all flex items-center justify-center gap-2"
                    >
                      <Plus size={14} /> Registrar Especialista
                    </button>
                  </div>

                  {/* Listado */}
                  <div className="lg:col-span-2 space-y-3">
                    <h3 className="font-bold text-xs uppercase text-seryi-heading tracking-wider">Plantilla del Local ({activeBusiness.staff.length})</h3>
                    {activeBusiness.staff.length === 0 ? (
                      <p className="text-xs text-seryi-text font-mono">No hay especialistas registrados en este negocio aún.</p>
                    ) : (
                      activeBusiness.staff.map(member => (
                        <div key={member.id} className="bg-seryi-bg border border-seryi-bg-light p-4 rounded-2xl flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-seryi-green/10 rounded-full flex items-center justify-center font-bold text-seryi-green border border-indigo-500/20">
                              {member.name.charAt(0)}
                            </div>
                            <div>
                              <p className="font-bold text-sm text-seryi-heading">{member.name}</p>
                              <p className="text-xs text-seryi-text">{member.specialty}</p>
                              <p className="text-[10px] text-seryi-text font-mono mt-1">{member.google_calendar_id || 'Calendario Google Conectado'}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => toggleStaffStatus(member.id)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${member.available ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}
                            >
                              {member.available ? <Check size={12} /> : <X size={12} />}
                              {member.available ? "Disponible" : "Fuera"}
                            </button>
                            <button 
                              onClick={() => handleEditStaff(member)}
                              title="Editar Especialista"
                              className="p-2 text-seryi-text hover:text-seryi-green hover:bg-seryi-green/10 rounded-lg transition-all"
                            >
                              ✍️  
                            </button>
                            <button 
                              onClick={() => handleDeleteStaff(member.id)}
                              title="Eliminar Especialista"
                              className="p-2 text-seryi-text hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-all"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* TABLA CLIENTE: MENÚ MULTIMEDIA (PDF / FOTOS POR BOT) */}
            {activeTab === 'menu-media' && (
              <div className="space-y-6 max-w-4xl">
                <div>
                  <h2 className="text-xl font-extrabold text-seryi-heading">Menú Multimedia 💎</h2>
                  <p className="text-xs text-seryi-text">Sube directamente tu archivo PDF o Foto del Menú a la nube, o pega un enlace externo (Google Drive / Canva).</p>
                </div>

                {/* BANNER DESTACADO CON VISTA PREVIA VISUAL DE LA FOTO / DOCUMENTO */}
                {activeBusiness.menu_pdf_url ? (
                  <div className="bg-emerald-950/30 border border-emerald-500/40 p-5 rounded-3xl space-y-4 shadow-xl">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-emerald-800/40 pb-4">
                      <div className="flex items-center gap-4 overflow-hidden">
                        {/* Vista previa de imagen o documento */}
                        {activeBusiness.menu_pdf_url.startsWith('data:image/') ||
                         activeBusiness.menu_pdf_url.includes('lh3.googleusercontent.com') || 
                         activeBusiness.menu_pdf_url.match(/\.(png|jpg|jpeg|webp)($|\?)/i) || 
                         activeBusiness.menu_pdf_url.includes('menu-files') ? (
                          <div className="relative group shrink-0">
                            <img 
                              src={getDirectMediaUrl(activeBusiness.menu_pdf_url)} 
                              alt="Vista previa Menú" 
                              className="w-20 h-20 object-cover rounded-2xl border-2 border-emerald-500/40 shadow-lg bg-seryi-bg-dark"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        ) : (
                          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0 shadow-md">
                            <FileText size={28} />
                          </div>
                        )}

                        <div className="truncate space-y-1">
                          <span className="text-[10px] font-black text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                            <CheckCircle size={12} /> FOTO / CARTA DIGITAL CARGADA Y ACTIVA EN VIVO
                          </span>
                          <p className="text-xs text-seryi-heading font-mono truncate max-w-md bg-seryi-bg-dark/80 px-3 py-1.5 rounded-lg border border-seryi-bg-light">
                            {activeBusiness.menu_pdf_url}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button 
                          type="button"
                          onClick={() => openMediaUrl(activeBusiness.menu_pdf_url)}
                          className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-seryi-heading rounded-xl text-xs font-bold transition-all shadow-md flex items-center gap-1.5 active:scale-95"
                        >
                          👁️  Ver Archivo Cargado
                        </button>
                        <button 
                          type="button"
                          onClick={async () => {
                            updateActiveBusinessField('menu_pdf_url', '');
                            await saveBusinessSettings({ menu_pdf_url: '' });
                            showNotification("Archivo de menú removido.", "info");
                          }}
                          className="px-3 py-2.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 rounded-xl text-xs font-bold transition-all"
                          title="Eliminar archivo cargado"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="text-[11px] text-emerald-300/90 font-medium flex items-center gap-2">
                      <Sparkles size={14} className="text-emerald-400 shrink-0" />
                      <span>Keyla IA enviará directamente esta imagen o documento interactivo a los clientes que soliciten el menú en WhatsApp.</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-950/20 border border-amber-800/40 p-4 rounded-2xl flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 shrink-0">
                      <FileText size={16} />
                    </div>
                    <p className="text-xs text-amber-300 font-medium">
                      ⚠️  Aún no has subido la carta o foto de tu menú. Elige una de las 2 opciones a continuación para activarlo.
                    </p>
                  </div>
                )}

                <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-6">
                  {/* Opción 1: Subida de Foto de la Carta */}
                  <div className="space-y-2">
                    <span className="text-[11px] font-extrabold uppercase text-seryi-green tracking-wider flex items-center gap-1.5">
                      <Upload size={14} /> Opción 1: Subir Foto de la Carta (Recomendado)
                    </span>
                    
                    <div className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all space-y-3 cursor-pointer relative group ${activeBusiness.menu_pdf_url ? 'border-emerald-500/50 bg-emerald-950/10' : 'border-seryi-bg-light hover:border-indigo-500 bg-seryi-bg-dark/60'}`}>
                      <input 
                        type="file" 
                        accept="image/png,image/jpeg,image/webp"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handleMenuFileUpload(e.target.files[0]);
                          }
                        }}
                      />
                      
                      <div className="flex justify-center">
                        {activeBusiness.menu_pdf_url ? (
                          <div className="w-full flex justify-center">
                            {activeBusiness.menu_pdf_url.match(/\.(jpeg|jpg|gif|png|webp)/i) || activeBusiness.menu_pdf_url.includes('lh3.googleusercontent.com') || activeBusiness.menu_pdf_url.startsWith('data:image/') ? (
                              <img 
                                src={activeBusiness.menu_pdf_url} 
                                alt="Menú" 
                                className="max-h-32 object-contain rounded-xl border border-emerald-500/30 group-hover:scale-105 transition-transform shadow-lg" 
                              />
                            ) : (
                              <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                                <CheckCircle size={28} />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="w-12 h-12 rounded-2xl bg-seryi-green/10 text-seryi-green border border-indigo-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <FileText size={24} />
                          </div>
                        )}
                      </div>

                      <div className="space-y-1">
                        <p className="text-xs font-bold text-seryi-heading">
                          {isUploadingMenu 
                            ? "⏳ Optimizando y guardando foto..." 
                            : activeBusiness.menu_pdf_url
                            ? "✅ Foto cargada correctamente. Arrastra otra foto para REEMPLAZARLA"
                            : "Haz clic o arrastra aquí la FOTO de tu Menú"
                          }
                        </p>
                        <p className="text-[10px] text-seryi-text">Imágenes PNG, JPG o WEBP (Máximo 10 MB)</p>
                      </div>
                      
                      {activeBusiness.menu_pdf_url && (
                        <div className="pt-2 flex justify-center">
                          <button 
                            type="button"
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              updateActiveBusinessField('menu_pdf_url', '');
                              await saveBusinessSettings({ menu_pdf_url: '' });
                              showNotification("Archivo de menú removido.", "info");
                            }}
                            className="relative z-20 inline-flex items-center gap-1.5 px-4 py-1.5 bg-rose-500/10 hover:bg-rose-500/30 text-rose-400 border border-rose-500/30 rounded-lg text-[11px] font-bold transition-all"
                          >
                            <Trash2 size={14} /> Borrar Imagen/PDF
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="relative flex py-1 items-center">
                    <div className="flex-grow border-t border-seryi-bg-light"></div>
                    <span className="flex-shrink mx-4 text-[10px] font-bold text-seryi-text uppercase">o bien</span>
                    <div className="flex-grow border-t border-seryi-bg-light"></div>
                  </div>

                  {/* Opción 2: Enlace Externo (Google Drive / Canva) */}
                  <div className="space-y-3 p-5 bg-seryi-bg-dark/40 border border-seryi-bg-light rounded-2xl">
                    <span className="text-[11px] font-extrabold uppercase text-seryi-heading tracking-wider flex items-center gap-1.5">
                      <LinkIcon size={14} className="text-seryi-green" /> Opción 2: Pegar Enlace Externo (Google Drive / Canva)
                    </span>

                    <div className="space-y-1">
                      <input 
                        type="url" 
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 font-mono"
                        placeholder="https://drive.google.com/file/d/... o https://canva.com/..."
                        value={activeBusiness.menu_link_url || ""}
                        onChange={(e) => updateActiveBusinessField('menu_link_url', e.target.value)}
                      />
                      <p className="text-[10px] text-seryi-text">
                        💡 <strong>Nota:</strong> Si pegas un enlace de Google Drive o Canva, Keyla la IA enviará directamente este enlace cuando el cliente pida ver el menú interactivo web.
                      </p>
                    </div>

                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={async () => {
                          await saveBusinessSettings({ menu_link_url: activeBusiness.menu_link_url });
                          showNotification("¡Enlace del Menú guardado exitosamente!", "success");
                        }}
                        className="flex-1 bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-2.5 rounded-xl text-xs flex items-center justify-center gap-2 transition-all shadow-md active:scale-95"
                      >
                        <Save size={14} /> Guardar Enlace
                      </button>
                      
                      {activeBusiness.menu_link_url && (
                        <button 
                          type="button"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            updateActiveBusinessField('menu_link_url', '');
                            await saveBusinessSettings({ menu_link_url: '' });
                            showNotification("Enlace externo removido.", "info");
                          }}
                          className="px-4 py-2.5 bg-rose-500/10 hover:bg-rose-500/30 text-rose-400 border border-rose-500/30 rounded-xl text-xs font-bold transition-all"
                        >
                          <Trash2 size={14} /> Borrar
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="relative flex py-1 items-center">
                    <div className="flex-grow border-t border-seryi-bg-light"></div>
                    <span className="flex-shrink mx-4 text-[10px] font-bold text-seryi-text uppercase">o bien</span>
                    <div className="flex-grow border-t border-seryi-bg-light"></div>
                  </div>

                  {/* Opción 3: Lista de Precios en Texto Nativamente Redactada */}
                  <div className="space-y-3 p-5 bg-seryi-bg-dark/40 border border-seryi-bg-light rounded-2xl">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <span className="text-[11px] font-extrabold uppercase text-seryi-heading tracking-wider flex items-center gap-1.5">
                        <FileText size={14} className="text-amber-400" /> Opción 3: Lista de Precios en Texto (Redactada)
                      </span>
                      <button
                        type="button"
                        onClick={async () => {
                          const autoList = generateTextListFromServices(activeBusiness.services);
                          if (!autoList) {
                            showNotification("Aún no tienes platillos registrados en la pestaña Menú y Precios.", "warning");
                            return;
                          }
                          updateActiveBusinessField('menu_text_list', autoList);
                          await saveBusinessSettings({ menu_text_list: autoList });
                          showNotification("¡Lista cargada y sincronizada automáticamente desde tu menú de productos!", "success");
                        }}
                        className="px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-xl text-[11px] font-bold transition-all flex items-center gap-1.5 self-start sm:self-auto"
                      >
                        <Sparkles size={12} /> ⚠️¡ Generar Lista desde "Menú y Precios"
                      </button>
                    </div>

                    <div className="space-y-1">
                      <textarea
                        rows={4}
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-3 text-xs text-seryi-heading focus:outline-none focus:border-amber-500 font-mono leading-relaxed"
                        placeholder="Ejemplo:&#10;🍽️ Hamburguesa Sencilla - $18.900&#10;🍽️ Hamburguesa Doble - $25.000&#10;🥤 Coca-Cola 400ml - $8.500"
                        value={activeBusiness.menu_text_list || ""}
                        onChange={(e) => updateActiveBusinessField('menu_text_list', e.target.value)}
                      />
                      <p className="text-[10px] text-seryi-text">
                        ✨ Puedes presionar el botón de arriba para sincronizar la lista automáticamente desde tu menú de productos, o editarla libremente a tu gusto.
                      </p>
                    </div>

                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={async () => {
                          await saveBusinessSettings({ menu_text_list: activeBusiness.menu_text_list });
                          showNotification("¡Lista de precios en texto guardada exitosamente!", "success");
                        }}
                        className="flex-1 bg-amber-600 hover:bg-amber-700 text-seryi-heading font-bold py-2.5 rounded-xl text-xs flex items-center justify-center gap-2 transition-all shadow-md active:scale-95"
                      >
                        <Save size={14} /> Guardar Lista de Precios
                      </button>
                      
                      {activeBusiness.menu_text_list && (
                        <button 
                          type="button"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            updateActiveBusinessField('menu_text_list', '');
                            await saveBusinessSettings({ menu_text_list: '' });
                            showNotification("Lista de precios en texto removida.", "info");
                          }}
                          className="px-4 py-2.5 bg-rose-500/10 hover:bg-rose-500/30 text-rose-400 border border-rose-500/30 rounded-xl text-xs font-bold transition-all"
                        >
                          <Trash2 size={14} /> Borrar
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Explicación de la Interacción por Opciones en WhatsApp */}
                  <div className="p-5 bg-seryi-bg-light/30 border border-indigo-900/40 rounded-2xl space-y-3">
                    <span className="text-xs font-bold text-seryi-green flex items-center gap-1.5">
                      <Sparkles size={14} /> Interacción Inteligente en WhatsApp (¿Cómo responde el Bot?)
                    </span>
                    <p className="text-[11px] text-seryi-heading leading-relaxed">
                      Cuando un cliente pida el menú o la carta por WhatsApp, Keyla la IA le saludará y le preguntará primero cuál opción prefiere (1. Imagen / PDF, 2. Enlace interactivo web, 3. Lista de precios en texto). En cuanto el cliente elija 1, 2 o 3, Keyla entregará exactamente la opción solicitada.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* TABLA CLIENTE: CATÁLOGO Y PRECIOS */}
            {activeTab === 'services' && (
              <div className="space-y-6 max-w-4xl">
                <div>
                  <h2 className="text-xl font-extrabold text-seryi-heading">
                    {isRestaurant ? "Menú y Carta Digital (Productos)" : "Catálogo de Servicios"}
                  </h2>
                  <p className="text-xs text-seryi-text">
                    {isRestaurant 
                      ? "Define tus platillos, bebidas y precios para que Keyla IA los ofrezca en WhatsApp y tome pedidos." 
                      : "Define los precios y duraciones para que Keyla IA sepa qué ofrecer y cuánto cobrar al agendar."}
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Creador de Servicios / Productos */}
                  <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-4 self-start">
                    <h3 className="font-bold text-xs uppercase text-seryi-heading tracking-wider">
                      {isRestaurant ? "Nuevo Producto / Platillo" : "Nuevo Servicio"}
                    </h3>
                    
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase text-seryi-text">
                        {isRestaurant ? "Nombre del Producto / Platillo" : "Nombre del Servicio"}
                      </label>
                      <input 
                        type="text" 
                        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500"
                        placeholder={isRestaurant ? "Ej: Hamburguesa Especial" : "Ej: Corte Clásico"}
                        value={newService.nombre}
                        onChange={(e) => setNewService(prev => ({ ...prev, nombre: e.target.value }))}
                      />
                    </div>

                    <div className={`grid ${isRestaurant ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase text-seryi-text">Precio ($)</label>
                        <input 
                          type="number" 
                          className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-emerald-400 font-bold focus:outline-none focus:border-indigo-500"
                          placeholder="18900"
                          value={newService.precio}
                          onChange={(e) => setNewService(prev => ({ ...prev, precio: parseFloat(e.target.value) || "" }))}
                        />
                      </div>
                      {!isRestaurant && (
                        <div className="space-y-1">
                          <label className="text-[10px] uppercase text-seryi-text">Duración (Min)</label>
                          <input 
                            type="number" 
                            className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500"
                            placeholder="30"
                            value={newService.duracion_minutos}
                            onChange={(e) => setNewService(prev => ({ ...prev, duracion_minutos: e.target.value }))}
                          />
                        </div>
                      )}
                    </div>

                    <button 
                      onClick={handleAddService}
                      className="w-full bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-2.5 rounded-xl text-xs transition-all flex items-center justify-center gap-2 shadow-md active:scale-95"
                    >
                      <Plus size={14} /> {isRestaurant ? "Añadir al Menú" : "Añadir al Catálogo"}
                    </button>
                  </div>

                  {/* Listado de Servicios / Productos */}
                  <div className="lg:col-span-2 space-y-3">
                    <h3 className="font-bold text-xs uppercase text-seryi-heading tracking-wider">
                      {isRestaurant ? `Platillos en el Menú (${(activeBusiness.services || []).length})` : `Servicios Activos (${(activeBusiness.services || []).length})`}
                    </h3>
                    {!(activeBusiness.services && activeBusiness.services.length > 0) ? (
                      <p className="text-xs text-seryi-text font-mono">
                        {isRestaurant ? "No hay productos registrados en el menú aún." : "No hay servicios registrados en este negocio aún."}
                      </p>
                    ) : (
                      activeBusiness.services.map(service => (
                        <div key={service.id} className="bg-seryi-bg border border-seryi-bg-light p-4 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          {editingServiceId === service.id ? (
                            <div className="flex-1 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                              <input 
                                type="text"
                                className="flex-1 bg-seryi-bg-dark border border-indigo-500 rounded-xl px-3 py-1.5 text-xs text-seryi-heading focus:outline-none"
                                value={editServiceName}
                                onChange={(e) => setEditServiceName(e.target.value)}
                                placeholder="Nombre del producto"
                              />
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-emerald-400 font-bold">$</span>
                                <input 
                                  type="number"
                                  className="w-28 bg-seryi-bg-dark border border-emerald-500 rounded-xl px-3 py-1.5 text-xs text-emerald-400 font-bold focus:outline-none"
                                  value={editServicePrice}
                                  onChange={(e) => setEditServicePrice(e.target.value)}
                                  placeholder="Precio"
                                />
                              </div>
                              <div className="flex items-center gap-2 pt-1 sm:pt-0">
                                <button
                                  type="button"
                                  onClick={() => handleSaveEditService(service.id)}
                                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-seryi-heading rounded-lg text-xs font-bold transition-all shadow-md"
                                >
                                  💾 Guardar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingServiceId(null)}
                                  className="px-2.5 py-1.5 bg-seryi-bg-light hover:bg-slate-700 text-seryi-heading rounded-lg text-xs font-bold transition-all"
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center font-bold text-emerald-400 border border-emerald-500/20 shrink-0">
                                  <DollarSign size={16} />
                                </div>
                                <div>
                                  <p className="font-bold text-sm text-seryi-heading">{service.nombre}</p>
                                  {!isRestaurant && service.duracion_minutos && (
                                    <p className="text-xs text-seryi-text">{service.duracion_minutos} min</p>
                                  )}
                                  <p className="text-xs text-emerald-400 font-bold mt-0.5">${Number(service.precio || 0).toLocaleString('es-CO')}</p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 shrink-0">
                                <button 
                                  type="button"
                                  onClick={() => handleStartEditService(service)}
                                  className="px-3 py-1.5 bg-seryi-green/10 hover:bg-seryi-green/20 text-indigo-300 border border-indigo-500/20 rounded-lg text-xs font-bold transition-all"
                                >
                                  ✍️  Editar
                                </button>
                                <button 
                                  onClick={() => toggleServiceStatus(service.id)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${service.activo ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}
                                >
                                  {service.activo ? <Check size={12} /> : <X size={12} />}
                                  {service.activo ? "Activo" : "Inactivo"}
                                </button>
                                <button 
                                  onClick={() => handleDeleteService(service.id)}
                                  className="p-2 text-seryi-text hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-all"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* TABLA CLIENTE: INVENTARIO Y STOCK */}
            {activeTab === 'inventario' && (
              <InventoryManager
                activeBusiness={activeBusiness}
                onUpdateBusiness={(updatedBusiness) => {
                  setMasterDb(prev => prev.map(b => {
                    if (String(b.id) === String(updatedBusiness.id)) {
                      return { ...b, ...updatedBusiness };
                    }
                    return b;
                  }));
                }}
              />
            )}

            {/* TABLA CLIENTE: COMISIONES Y NÓMINA EMPLEADOS */}
            {activeTab === 'comisiones' && (
              <StaffCommissions
                activeBusiness={activeBusiness}
                onUpdateBusiness={(updatedBusiness) => {
                  setMasterDb(prev => prev.map(b => {
                    if (String(b.id) === String(updatedBusiness.id)) {
                      return { ...b, ...updatedBusiness };
                    }
                    return b;
                  }));
                }}
              />
            )}

            {/* TABLA CLIENTE: CAMPAÑAS WHATSAPP Y MARKETING */}
            {activeTab === 'campanas' && (
              <WhatsAppCampaigns
                activeBusiness={activeBusiness}
                onUpdateBusiness={(updatedBusiness) => {
                  setMasterDb(prev => prev.map(b => {
                    if (String(b.id) === String(updatedBusiness.id)) {
                      return { ...b, ...updatedBusiness };
                    }
                    return b;
                  }));
                }}
              />
            )}

            {/* TABLA CLIENTE: FIDELIZACIÓN VIP Y CUPONES DE DESCUENTO */}
            {activeTab === 'fidelizacion' && (
              <LoyaltyCoupons
                activeBusiness={activeBusiness}
                onUpdateBusiness={(updatedBusiness) => {
                  setMasterDb(prev => prev.map(b => {
                    if (String(b.id) === String(updatedBusiness.id)) {
                      return { ...b, ...updatedBusiness };
                    }
                    return b;
                  }));
                }}
              />
            )}

            {/* TABLA CLIENTE: CENTRO DE AYUDA (KEYLA ACADEMY) */}
            {activeTab === 'manual' && (
              <ClientHelpCenter
                activeBusiness={activeBusiness}
                onClose={() => setActiveTab('dashboard')}
              />
            )}

            {/* TABLA CLIENTE: FINANZAS Y REPORTES */}
            {activeTab === 'finances' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-extrabold text-seryi-heading">Dashboard Financiero y Reportes de Caja</h2>
                  <p className="text-xs text-seryi-text">Lleva el control de ingresos totales ingresados a caja por citas y pedidos pagados por WhatsApp.</p>
                </div>

                {/* Métricas Generales */}
                {(() => {
                  const apptsPaid = (activeBusiness.appointments || []).filter(a => (a.status === "Completada" || a.status === "Confirmada") && a.estado_pago === "Pagado").reduce((acc, a) => acc + (a.precio_cobrado || 0), 0);
                  const ordersPaid = (activeBusiness.orders || []).filter(isPaidOrder).reduce((acc, o) => acc + (o.total || 0), 0);
                  const totalPaid = apptsPaid + ordersPaid;

                  const apptsPending = (activeBusiness.appointments || []).filter(a => a.status !== "Cancelada" && a.estado_pago !== "Pagado").reduce((acc, a) => acc + (a.precio_cobrado || 0), 0);
                  const ordersPending = (activeBusiness.orders || []).filter(o => o.status === "pending" || o.status === "in_preparation" || o.status === "shipped").reduce((acc, o) => acc + (o.total || 0), 0);
                  const totalPending = apptsPending + ordersPending;

                  const displayApptsPaid = apptsPaid;
                  const displayOrdersPaid = ordersPaid;

                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-2xl space-y-2">
                        <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Ingresos Totales (Pagados)</span>
                        <p className="text-3xl font-black text-emerald-400">${totalPaid.toLocaleString('es-CO')}</p>
                        <div className="text-[10px] text-emerald-400 font-bold flex items-center gap-1">
                          Citas (${displayApptsPaid.toLocaleString('es-CO')}) + Pedidos (${displayOrdersPaid.toLocaleString('es-CO')})
                        </div>
                      </div>

                      <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-2xl space-y-2">
                        <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Pendientes por Cobrar / Entregar</span>
                        <p className="text-3xl font-black text-amber-400">${totalPending.toLocaleString('es-CO')}</p>
                        <div className="text-[10px] text-amber-400 font-bold">Órdenes y Citas en proceso</div>
                      </div>
                    </div>
                  );
                })()}

                {/* Desglose por Empleado */}
                {(!activeBusiness.feature_commissions) ? (
                  <div className="bg-seryi-bg border border-seryi-bg-light p-8 rounded-3xl space-y-4 max-w-4xl text-center">
                    <div className="mx-auto w-16 h-16 bg-seryi-green/10 flex justify-center items-center rounded-full mb-2">
                      <Sparkles size={32} className="text-seryi-green" />
                    </div>
                    <h3 className="font-bold text-lg text-seryi-heading">Módulo de Comisiones Bloqueado</h3>
                    <p className="text-sm text-seryi-text max-w-md mx-auto">
                      El cálculo automático de comisiones está deshabilitado en tu cuenta actual. Contacta a soporte para activar esta funcionalidad.
                    </p>
                    <a 
                      href="https://wa.me/573174701590?text=Hola,%20quiero%20activar%20el%20m%C3%B3dulo%20de%20comisiones%20en%20Keyla%20CRM"
                      target="_blank" rel="noopener noreferrer"
                      className="mt-4 inline-block px-6 py-2 rounded-xl bg-seryi-green text-seryi-bg-dark font-bold text-sm"
                    >
                      Contactar Soporte
                    </a>
                  </div>
                ) : (
                <div className="bg-gradient-to-r from-seryi-bg to-seryi-bg-dark border border-seryi-bg-light p-6 rounded-3xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 max-w-4xl shadow-lg">
                  <div>
                    <h3 className="font-extrabold text-base text-seryi-heading flex items-center gap-2">
                      <Award size={18} className="text-seryi-green" />
                      <span>Módulo de Comisiones y Nómina del Equipo</span>
                    </h3>
                    <p className="text-xs text-seryi-text mt-1">
                      Gestiona el desglose de pagos por especialista, ajusta acuerdos comerciales y exporta reportes.
                    </p>
                  </div>
                  <button
                    onClick={() => setActiveTab('comisiones')}
                    className="px-5 py-2.5 bg-seryi-green text-seryi-bg-dark hover:brightness-110 rounded-2xl text-xs font-bold transition-all shadow-md shrink-0 flex items-center gap-2"
                  >
                    <span>Abrir Nómina y Comisiones</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
                )}

                {/* Libro de Movimientos Contables y Exportación CSV */}
                <FinancialReports activeBusiness={activeBusiness} />
              </div>
            )}

            {/* TABLA CLIENTE: CITAS / AGENDA */}
            {activeTab === 'agenda' && (
              <AppointmentsView negocioId={isImpersonating ? activeBusinessId : activeBusiness.id} />
            )}

            {/* TABLA CLIENTE: CRM KANBAN */}
            {activeTab === 'kanban' && (
              <div className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-seryi-bg-light/80 pb-4">
                  <div>
                    <h2 className="text-xl font-extrabold text-seryi-heading">CRM Kanban</h2>
                    <p className="text-xs text-seryi-text">Gestiona visualmente el estado de tus leads y citas (Drag & Drop).</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => setActiveTab('campanas')}
                      className="px-4 py-2.5 bg-seryi-green text-seryi-bg-dark hover:brightness-110 rounded-2xl text-xs font-extrabold transition-all shadow-md flex items-center gap-2"
                    >
                      <Megaphone size={16} />
                      <span>Campañas WhatsApp</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('fidelizacion')}
                      className="px-4 py-2.5 bg-amber-500 text-slate-900 hover:brightness-110 rounded-2xl text-xs font-extrabold transition-all shadow-md flex items-center gap-2"
                    >
                      <Gift size={16} />
                      <span>Cupones & VIP</span>
                    </button>
                  </div>
                </div>
                <KanbanBoard 
                  appointments={activeBusiness?.appointments || []} 
                  onUpdateStatus={handleUpdateKanbanStatus} 
                />
              </div>
            )}

            {/* TABLA CLIENTE: GESTIÓN DE PEDIDOS RECIBIDOS POR WHATSAPP */}


            {activeTab === 'pedidos' && (
              <div className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-seryi-bg-light/80 pb-4">
                  <div>
                    <h2 className="text-xl font-extrabold text-seryi-heading">Consola de Pedidos y Órdenes</h2>
                    <p className="text-xs text-seryi-text">Administración de pedidos por WhatsApp, Sitio Web o Registro Manual en local.</p>
                  </div>
                  <button 
                    onClick={() => setShowManualOrderModal(true)}
                    className="bg-seryi-green hover:bg-seryi-green/80 text-seryi-bg-dark font-black py-2.5 px-5 rounded-xl text-xs flex items-center gap-2 shadow-lg transition-all"
                  >
                    <Plus size={16} /> + Nuevo Pedido Manual
                  </button>
                </div>

                {/* FILTROS DE PEDIDO (Req #7) */}
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { id: 'all', label: 'Todos los Pedidos' },
                    { id: 'whatsapp', label: '💬 WhatsApp' },
                    { id: 'website', label: '🌐 Sitio Web' },
                    { id: 'manual', label: '📝 Manual / Presencial' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setOrderFilterTab(tab.id)}
                      className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                        orderFilterTab === tab.id 
                          ? 'bg-seryi-green text-seryi-bg-dark shadow-md' 
                          : 'bg-seryi-bg-light/40 text-seryi-text hover:text-seryi-heading'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* MODAL NUEVO PEDIDO MANUAL */}
                {showManualOrderModal && (
                  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl max-w-md w-full space-y-4 shadow-2xl animate-fadeIn">
                      <div className="flex items-center justify-between border-b border-seryi-bg-light pb-3">
                        <h3 className="font-black text-base text-seryi-heading">Registrar Pedido Manual</h3>
                        <button onClick={() => setShowManualOrderModal(false)} className="text-slate-400 hover:text-white"><X size={18} /></button>
                      </div>
                      <form onSubmit={handleCreateManualOrder} className="space-y-3">
                        <div className="space-y-1">
                          <label className="text-[10px] uppercase font-bold text-seryi-text">Nombre del Cliente *</label>
                          <input 
                            type="text" required placeholder="Ej: Carlos Gómez"
                            className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5 text-xs text-seryi-heading"
                            value={manualOrderForm.customer_name}
                            onChange={(e) => setManualOrderForm({...manualOrderForm, customer_name: e.target.value})}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <label className="text-[10px] uppercase font-bold text-seryi-text">Teléfono</label>
                            <input 
                              type="text" placeholder="Ej: 310..."
                              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5 text-xs text-seryi-heading"
                              value={manualOrderForm.customer_phone}
                              onChange={(e) => setManualOrderForm({...manualOrderForm, customer_phone: e.target.value})}
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] uppercase font-bold text-seryi-text">Total ($)</label>
                            <input 
                              type="number" placeholder="0"
                              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5 text-xs text-seryi-heading"
                              value={manualOrderForm.total}
                              onChange={(e) => setManualOrderForm({...manualOrderForm, total: e.target.value})}
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] uppercase font-bold text-seryi-text">Dirección o Mesa</label>
                          <input 
                            type="text" placeholder="Ej: Mesa 4 / Cra 12 # 34-56"
                            className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5 text-xs text-seryi-heading"
                            value={manualOrderForm.customer_address}
                            onChange={(e) => setManualOrderForm({...manualOrderForm, customer_address: e.target.value})}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] uppercase font-bold text-seryi-text">Detalle de Productos *</label>
                          <textarea 
                            rows={3} required placeholder="Ej: 2 Hamburguesas de Res, 1 Coca-Cola"
                            className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5 text-xs text-seryi-heading"
                            value={manualOrderForm.items_text}
                            onChange={(e) => setManualOrderForm({...manualOrderForm, items_text: e.target.value})}
                          />
                        </div>
                        <div className="flex items-center gap-2 pt-2">
                          <button type="button" onClick={() => setShowManualOrderModal(false)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-2.5 rounded-xl text-xs">Cancelar</button>
                          <button type="submit" className="flex-1 bg-seryi-green hover:bg-seryi-green/80 text-seryi-bg-dark font-black py-2.5 rounded-xl text-xs">Guardar Pedido</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {(!activeBusiness.orders || activeBusiness.orders.length === 0) ? (
                    <div className="bg-seryi-bg border border-seryi-bg-light p-8 rounded-3xl text-center space-y-2">
                      <Store className="mx-auto text-slate-600" size={32} />
                      <p className="text-sm font-bold text-seryi-heading">No hay pedidos registrados todavía.</p>
                      <p className="text-xs text-seryi-text max-w-sm mx-auto">Cuando los clientes pidan por WhatsApp, Sitio Web o de forma Manual, aparecerán aquí.</p>
                    </div>
                  ) : (
                    [...activeBusiness.orders]
                      .filter(order => {
                        if (orderFilterTab === 'all') return true;
                        if (orderFilterTab === 'whatsapp') return order.source === 'whatsapp' || !order.source;
                        if (orderFilterTab === 'website') return order.source === 'web' || order.source === 'website';
                        if (orderFilterTab === 'manual') return order.source === 'manual';
                        return true;
                      })
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .map(order => {
                        const statusKey = order.status || 'pending';
                        const isPending = statusKey === 'pending';
                        const isInPrep = statusKey === 'in_preparation';
                        const isShipped = statusKey === 'shipped';
                        const isDelivered = statusKey === 'delivered';
                        const isCancelled = statusKey === 'cancelled';
                        const isCancelledByClient = statusKey === 'cancelled_by_client';

                        let parsedItems = [];
                        try {
                          if (Array.isArray(order.items)) {
                            parsedItems = order.items;
                          } else if (typeof order.items === 'string') {
                            parsedItems = JSON.parse(order.items);
                          }
                        } catch (e) {
                          parsedItems = [];
                        }

                        return (
                          <div 
                            key={order.id} 
                            className={`bg-seryi-bg border ${isCancelled || isCancelledByClient ? 'border-rose-900/40 opacity-70' : isDelivered ? 'border-emerald-800/40' : isShipped ? 'border-purple-800/40' : isInPrep ? 'border-blue-800/40' : 'border-amber-800/40'} p-6 rounded-3xl space-y-4 shadow-lg transition-all`}
                          >
                            {/* Cabecera del Pedido */}
                            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-seryi-bg-light/80 pb-3">
                              <div className="flex items-center gap-2.5">
                                <span className="font-extrabold text-base text-seryi-heading">
                                  {order.client_name && order.client_name !== 'Anónimo' ? order.client_name : 'Cliente WhatsApp'}
                                </span>
                                
                                {/* Insignia de Estado */}
                                <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider ${
                                  isDelivered ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 
                                  isShipped ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' : 
                                  isInPrep ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 
                                  isCancelledByClient ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' : 
                                  isCancelled ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' : 
                                  'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                }`}>
                                  {isDelivered ? 'Entregado' : isShipped ? 'Despachado / En Camino' : isInPrep ? 'En Preparación' : isCancelledByClient ? 'Usuario Canceló' : isCancelled ? 'Cancelado' : 'Pendiente'}
                                </span>

                                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider bg-seryi-green/10 text-seryi-green border border-indigo-500/20">
                                  {order.delivery_mode === 'domicilio' ? '🛵 Domicilio' : '🏪 Recoger en local'}
                                </span>
                              </div>

                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono text-seryi-text">
                                  {order.created_at ? new Date(order.created_at).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                                </span>
                              </div>
                            </div>

                            {/* Detalle de Productos e Info de Contacto */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                              <div className="lg:col-span-2 space-y-3">
                                {/* Calculadora y Desglose Detallado del Pedido */}
                                {(() => {
                                  let rawItemsList = [];
                                  try {
                                    if (Array.isArray(order.items)) rawItemsList = order.items;
                                    else if (typeof order.items === 'string') rawItemsList = JSON.parse(order.items);
                                  } catch (e) {
                                    if (typeof order.items === 'string' && order.items.trim()) rawItemsList = [order.items];
                                  }

                                  let calcSubtotal = 0;
                                  const normalized = rawItemsList.map(rawItem => {
                                    if (typeof rawItem === 'object' && rawItem !== null) {
                                      const name = rawItem.nombre || rawItem.name || rawItem.item || rawItem.producto || rawItem.product || 'Producto';
                                      const qty = parseInt(rawItem.cantidad || rawItem.quantity || rawItem.qty || 1, 10);
                                      const price = parseFloat(rawItem.precio || rawItem.price || rawItem.precio_unitario || 0);
                                      const sub = qty * price;
                                      calcSubtotal += sub;
                                      return { name, qty, price, sub };
                                    }

                                    if (typeof rawItem === 'string') {
                                      const parts = rawItem.split(',');
                                      if (parts.length >= 3 && !isNaN(parts[1]) && !isNaN(parts[2])) {
                                        const name = parts[0].trim();
                                        const qty = parseInt(parts[1].trim(), 10);
                                        const price = parseFloat(parts[2].trim());
                                        const sub = qty * price;
                                        calcSubtotal += sub;
                                        return { name, qty, price, sub };
                                      }
                                      
                                      const match = rawItem.match(/^(\d+)\s*x?\s*(.+)$/i);
                                      if (match) {
                                        return { name: match[2].trim(), qty: parseInt(match[1], 10), price: 0, sub: 0 };
                                      }
                                      return { name: rawItem, qty: 1, price: 0, sub: 0 };
                                    }
                                    return { name: String(rawItem), qty: 1, price: 0, sub: 0 };
                                  });

                                  const finalOrderTotal = order.total || calcSubtotal;
                                  const shipFee = order.delivery_cost || 0;

                                  return (
                                    <div className="bg-seryi-bg-dark p-4 rounded-2xl border border-seryi-bg-light space-y-2.5 shadow-inner">
                                      <div className="flex items-center justify-between border-b border-seryi-bg-light/80 pb-2">
                                        <span className="text-[10px] uppercase font-black text-seryi-green tracking-wider flex items-center gap-1.5">
                                          <Database size={13} /> Calculadora y Desglose de Productos
                                        </span>
                                        <span className="text-[10px] font-mono text-seryi-text">
                                          {normalized.length} {normalized.length === 1 ? 'ítem' : 'ítems'}
                                        </span>
                                      </div>

                                      <div className="divide-y divide-slate-800/60 text-xs">
                                        {normalized.map((it, idx) => (
                                          <div key={idx} className="py-1.5 flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                              <span className="bg-seryi-green/20 text-indigo-300 font-black px-2 py-0.5 rounded-md text-[11px] font-mono">
                                                {it.qty}x
                                              </span>
                                              <span className="font-bold text-seryi-heading">{it.name}</span>
                                            </div>

                                            <div className="text-right font-mono">
                                              {it.price > 0 ? (
                                                <div>
                                                  <span className="text-slate-200 font-bold">${it.sub.toLocaleString('es-CO')}</span>
                                                  <span className="text-[10px] text-seryi-text block">(${it.price.toLocaleString('es-CO')} c/u)</span>
                                                </div>
                                              ) : (
                                                <span className="text-seryi-text text-[11px]">Incluido</span>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>

                                      {/* Total e Info de Cobro para el Domiciliario */}
                                      <div className="border-t border-seryi-bg-light/80 pt-2 space-y-1">
                                        {shipFee > 0 && (
                                          <div className="flex justify-between items-center text-seryi-text text-[11px]">
                                            <span>Costo Envío / Domicilio:</span>
                                            <span className="font-mono">${shipFee.toLocaleString('es-CO')}</span>
                                          </div>
                                        )}
                                        <div className="flex justify-between items-center text-sm font-black pt-1 text-emerald-400">
                                          <span>TOTAL A COBRAR:</span>
                                          <span className="text-base font-mono font-black">${finalOrderTotal.toLocaleString('es-CO')}</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}

                                <div className="flex flex-wrap items-center gap-4 text-xs text-seryi-heading pt-1">
                                  <span className="flex items-center gap-1.5 font-mono text-seryi-heading">
                                    <Smartphone size={14} className="text-seryi-green" /> 
                                    {order.client_phone ? order.client_phone.replace('@s.whatsapp.net', '') : 'Sin teléfono'}
                                  </span>
                                  {order.delivery_address && (
                                    <span className="flex items-center gap-1.5 font-semibold text-slate-200">
                                      <Store size={14} className="text-seryi-green" /> 
                                      {order.delivery_address}
                                    </span>
                                  )}
                                  {order.payment_method && (
                                    <span className="bg-seryi-bg-light px-2.5 py-0.5 rounded-md text-[11px] font-bold text-seryi-heading">
                                      Pago: {order.payment_method}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Editar Datos Básicos y Anotaciones del Repartidor / Negocio */}
                              <div className="bg-seryi-bg-dark p-4 rounded-2xl border border-seryi-bg-light space-y-2.5">
                                <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider flex items-center gap-1">
                                  <Settings size={12} /> Editar Datos y Notas
                                </span>

                                <div className="space-y-1">
                                  <label className="text-[9px] uppercase text-seryi-text font-bold">Nombre del Cliente</label>
                                  <input 
                                    type="text" 
                                    className="w-full bg-seryi-bg border border-seryi-bg-light rounded-lg px-2.5 py-1.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 font-bold"
                                    value={order.client_name || ""}
                                    onChange={(e) => handleSaveOrderDetails(order.id, { client_name: e.target.value })}
                                  />
                                </div>

                                <div className="space-y-1">
                                  <label className="text-[9px] uppercase text-seryi-text font-bold">Dirección de Entrega</label>
                                  <input 
                                    type="text" 
                                    className="w-full bg-seryi-bg border border-seryi-bg-light rounded-lg px-2.5 py-1.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500"
                                    value={order.delivery_address || ""}
                                    onChange={(e) => handleSaveOrderDetails(order.id, { delivery_address: e.target.value })}
                                  />
                                </div>

                                <div className="space-y-1">
                                  <label className="text-[9px] uppercase text-seryi-text font-bold">Anotación del Repartidor / Negocio</label>
                                  <textarea 
                                    rows="2"
                                    className="w-full bg-seryi-bg border border-seryi-bg-light rounded-lg px-2.5 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                                    placeholder="Ej: Cliente canceló por demora / Entregado en portería..."
                                    value={order.driver_notes || ""}
                                    onChange={(e) => handleSaveOrderDetails(order.id, { driver_notes: e.target.value })}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Barra de Cambios de Estado y Acciones */}
                            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-seryi-bg-light/80 pt-3">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="text-[10px] uppercase font-bold text-seryi-text mr-1">Cambiar Estado:</span>
                                
                                <button
                                  onClick={() => updateOrderStatus(order.id, 'in_preparation')}
                                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${isInPrep ? 'bg-blue-600 text-seryi-heading' : 'bg-seryi-bg-light hover:bg-slate-700 text-seryi-heading'}`}
                                >
                                  En Preparación
                                </button>
                                
                                <button
                                  onClick={() => updateOrderStatus(order.id, 'shipped')}
                                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${isShipped ? 'bg-purple-600 text-seryi-heading' : 'bg-seryi-bg-light hover:bg-slate-700 text-seryi-heading'}`}
                                >
                                  Despachado
                                </button>

                                <button
                                  onClick={() => updateOrderStatus(order.id, 'delivered', { is_paid: true })}
                                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${isDelivered || order.is_paid ? 'bg-emerald-600 text-seryi-heading shadow-lg' : 'bg-seryi-bg-light hover:bg-emerald-900/30 text-emerald-300'}`}
                                >
                                  {isDelivered || order.is_paid ? '✓ Entregado y Pagado' : '💰 Marcar como Pagado'}
                                </button>

                                <button
                                  onClick={() => updateOrderStatus(order.id, 'cancelled_by_client')}
                                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${isCancelledByClient ? 'bg-rose-600 text-seryi-heading' : 'bg-seryi-bg-light hover:bg-rose-900/30 text-rose-300'}`}
                                >
                                  Usuario Canceló
                                </button>
                                
                                <button
                                  onClick={() => handleCopyForDelivery(order)}
                                  className="px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/20 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all ml-1"
                                  title="Copiar datos para enviar por WhatsApp al Domiciliario"
                                >
                                  <Copy size={13} /> Copiar Datos
                                </button>
                              </div>

                              <button
                                onClick={() => handleDeleteOrder(order.id)}
                                className="px-3 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all"
                                title="Eliminar Pedido Definitivamente"
                              >
                                <Trash2 size={13} /> Eliminar
                              </button>
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
              </div>
            )}

          </div>
        )}

        {/* TABLA CLIENTE: MANUAL DE USO */}
        {userRole !== 'master' && activeTab === 'manual' && (
          <div className="bg-seryi-bg border border-seryi-bg-light p-6 md:p-10 rounded-3xl w-full max-w-4xl mx-auto space-y-8 shadow-xl animate-in fade-in zoom-in-95 duration-500">
            <div className="flex justify-between items-start border-b border-seryi-bg-light pb-6">
              <div>
                <h3 className="text-3xl font-black text-seryi-heading flex items-center gap-3">
                  <FileText className="text-blue-400" size={32} /> Manual de Uso Keyla CRM
                </h3>
                <p className="text-seryi-text text-sm mt-2">Guía rápida para sacarle el máximo provecho a tu asistente virtual.</p>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="bg-seryi-bg-dark/50 p-6 rounded-2xl border border-seryi-bg-light/50">
                <h4 className="font-bold text-seryi-green flex items-center gap-2 text-xl mb-4">
                  <Smartphone size={24} /> 1. Atención Híbrida (Pausar a Keyla)
                </h4>
                <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                  Si notas que un cliente requiere atención personalizada o humana urgente, puedes pausar a Keyla <strong>específicamente para ese cliente</strong> sin afectar a los demás.
                </p>
                <ul className="list-disc list-inside text-sm text-slate-400 mt-3 space-y-2">
                  <li><strong>Rápido desde tu celular:</strong> Entra al chat de WhatsApp del cliente y escribe <span className="text-rose-400 font-mono bg-slate-900 px-1 py-0.5 rounded">#pausar</span> para que el bot deje de contestarle (cualquiera de tus asesores puede hacerlo desde la línea del negocio). Para devolverle el chat al bot escribe <span className="text-emerald-400 font-mono bg-slate-900 px-1 py-0.5 rounded">#activar</span>. Keyla reacciona con un emoji al mensaje y no le escribe nada al cliente.</li>
                  <li><strong>Desde este panel:</strong> Ve a la pestaña <span className="text-emerald-400 font-bold">Dashboard</span>, y en <span className="italic">Atención Humana Directa</span> verás la lista de números silenciados. Puedes agregar nuevos o presionar "Reactivar" para que Keyla vuelva a contestarles.</li>
                </ul>
              </div>

              <div className="bg-seryi-bg-dark/50 p-6 rounded-2xl border border-seryi-bg-light/50">
                <h4 className="font-bold text-seryi-green flex items-center gap-2 text-xl mb-4">
                  <Store size={24} /> 2. Gestión de Menús y Catálogos
                </h4>
                <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                  Keyla puede mostrar a tus clientes tu menú o catálogo de productos automáticamente. En la pestaña <span className="text-emerald-400 font-bold">Menú Multimedia</span> puedes subir fotos y PDFs. 
                </p>
                <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                  <strong>Tip de IA:</strong> Keyla lee estos archivos visuales, por lo que responderá preguntas de tus clientes sobre los precios que estén escritos ahí dentro.
                </p>
              </div>

              <div className="bg-seryi-bg-dark/50 p-6 rounded-2xl border border-seryi-bg-light/50">
                <h4 className="font-bold text-seryi-green flex items-center gap-2 text-xl mb-4">
                  <Activity size={24} /> 3. Monitor de Cocina (KDS)
                </h4>
                <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                  Haz clic en el botón <span className="text-emerald-400 font-bold">KDS Cocina (Nueva Pestaña)</span> en el menú principal. Esto abrirá una pantalla oscura y optimizada en una nueva pestaña.
                </p>
              </div>
            </div>
          </div>
        )}

      </main>

      {/* MODAL DE CONFIRMACIÓN DE PLANTILLA */}
      {templateConfirmModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-seryi-bg border border-seryi-bg-light p-8 rounded-3xl max-w-xl w-full space-y-6 shadow-2xl relative">
            <h2 className="text-xl font-black text-seryi-heading">⚠️ Confirmación de Seguridad</h2>
            
            <div className="space-y-4 text-sm text-seryi-heading">
              <p>
                El negocio <span className="font-bold text-seryi-heading">"{activeBusiness.nombre_negocio}"</span> ya tiene <span className="font-bold text-emerald-400">{templateConfirmModal.currentServicesCount} productos</span> en su catálogo.
              </p>
              <p>
                Estás a punto de aplicar la plantilla <span className="font-bold text-seryi-green">"{templateConfirmModal.tmpl.name}"</span>. ¿Qué deseas hacer con el catálogo actual?
              </p>
            </div>

            <div className="flex flex-col gap-3 mt-4">
              <button 
                onClick={() => executeTemplateApply(templateConfirmModal.tmpl, false)}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-seryi-heading font-bold py-3 px-4 rounded-xl transition-all shadow-md text-left flex flex-col items-start gap-1"
              >
                <span>✅ Actualizar solo las Reglas de IA (Recomendado)</span>
                <span className="text-[10px] font-normal text-emerald-100">Mantiene los productos/servicios del cliente intactos, solo actualiza las instrucciones del bot.</span>
              </button>
              
              {String(activeBusiness.nombre_negocio || '').toLowerCase().includes('total emprender') ? (
                <button 
                  onClick={() => {
                    if(window.confirm(`CONFIRMACIÓN FINAL: ¿Estás seguro de que deseas ELIMINAR los ${templateConfirmModal.currentServicesCount} productos actuales y reemplazarlos por los de la plantilla? Esta acción no se puede deshacer.`)) {
                      executeTemplateApply(templateConfirmModal.tmpl, true);
                    }
                  }}
                  className="w-full bg-rose-600 hover:bg-rose-500 text-seryi-heading font-bold py-3 px-4 rounded-xl transition-all shadow-md text-left flex flex-col items-start gap-1"
                >
                  <span>⚠️ Reemplazar TODO el Catálogo y Reglas</span>
                  <span className="text-[10px] font-normal text-rose-100">Borra permanentemente el catálogo actual y carga los productos genéricos de la plantilla.</span>
                </button>
              ) : (
                <div className="w-full bg-seryi-bg-light border border-seryi-bg-light p-3 rounded-xl text-[10px] text-seryi-text text-center">
                  🔒 El reemplazo completo de catálogo está deshabilitado para este negocio por seguridad. Solo permitido en "Total Emprender".
                </div>
              )}
              
              <button 
                onClick={() => setTemplateConfirmModal(null)}
                className="w-full mt-2 bg-seryi-bg-light hover:bg-slate-700 text-seryi-heading font-bold py-3 px-4 rounded-xl transition-all border border-seryi-bg-light flex justify-center"
              >
                Cancelar y no hacer nada
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 4. NOTIFICACIÓN TOAST */}
      {toast.show && (
        <div className={`fixed bottom-6 right-6 ${toast.type === 'error' ? 'bg-rose-600' : toast.type === 'warning' ? 'bg-amber-600' : 'bg-emerald-600'} text-seryi-heading py-3 px-5 rounded-xl shadow-xl flex items-center gap-2 text-xs font-bold z-50 animate-bounce`}>
          {toast.type === 'error' ? <X size={16} /> : <Check size={16} />}
          {toast.message}
        </div>
      )}




      {isTemplatesModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-seryi-bg-dark/80 backdrop-blur-sm">
          <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto relative shadow-2xl">
            
            <button 
              onClick={() => setIsTemplatesModalOpen(false)}
              className="absolute top-4 right-4 p-2 text-seryi-text hover:text-rose-400 hover:bg-rose-500/10 rounded-xl transition-all"
            >
              <X size={20} />
            </button>
            
            <div className="mb-6">
              <h3 className="font-extrabold text-xl text-seryi-heading flex items-center gap-2 uppercase tracking-wider">
                <Sparkles className="text-seryi-green" size={20} /> Plantillas IA
              </h3>
              <p className="text-xs text-seryi-text mt-1">
                Navega entre las plantillas y aplica la que mejor se adapte al nicho del cliente.
              </p>
            </div>

            <div className="flex items-center gap-4">
              <button 
                onClick={() => setCurrentTemplateIndex(prev => (prev > 0 ? prev - 1 : activeTemplates.length - 1))}
                className="p-3 text-seryi-heading bg-seryi-bg-dark hover:bg-seryi-green/20 hover:text-seryi-green rounded-full border border-seryi-bg-light transition-all flex-shrink-0"
              >
                <ChevronLeft size={24} />
              </button>

              <div className="flex-1">
                {(() => {
                  const tmpl = activeTemplates[currentTemplateIndex];
                  if (!tmpl) return null;
                  return (
                    <div className="bg-seryi-bg-dark border border-seryi-bg-light p-6 rounded-3xl flex flex-col justify-between space-y-4 shadow-inner">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-4xl">{tmpl.icon}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-extrabold uppercase px-3 py-1 rounded-lg bg-seryi-green/10 text-seryi-green border border-seryi-green/20">
                              {tmpl.badge}
                            </span>
                          </div>
                        </div>
                        <h4 className="font-black text-xl text-seryi-heading">{tmpl.category}</h4>
                        
                        <div className="flex flex-wrap gap-1.5">
                          {(tmpl.featuresSummary || []).map((feat, idx) => (
                            <span key={idx} className="text-[10px] bg-seryi-bg text-seryi-heading font-medium px-2 py-1 rounded border border-seryi-bg-light/80">
                              ⚠️¡ {feat}
                            </span>
                          ))}
                        </div>

                        <p className="text-xs text-seryi-text leading-relaxed whitespace-pre-line font-mono bg-seryi-bg/60 p-4 rounded-xl border border-seryi-bg-light/80 h-40 overflow-y-auto custom-scrollbar">
                          {typeof tmpl.getEstilo === 'function' ? tmpl.getEstilo(activeBusiness.nombre_negocio || 'Mi Negocio') : tmpl.estiloText}
                        </p>
                      </div>

                      <div className="flex flex-col gap-2 mt-4">
                        <p className="text-[10px] text-slate-400 font-semibold mb-1 text-center">Opciones de Aplicación:</p>
                        
                        <button
                          type="button"
                          onClick={async () => {
                            const currentServicesCount = (activeBusiness.services || []).length;
                            const nombre = activeBusiness.nombre_negocio || 'Mi Negocio';
                            const newTono = tmpl.tono;
                            const newEstilo = typeof tmpl.getEstilo === 'function' ? tmpl.getEstilo(nombre) : tmpl.estiloText;

                            setEstiloTextDraft(newEstilo);
                            const el = document.getElementById('reglas-de-oro-textarea');
                            if (el) el.value = newEstilo;

                            updateActiveBusinessField('tipo_negocio', tmpl.category);
                            updateActiveBusinessField('tono', newTono);
                            updateActiveBusinessField('estilo', newEstilo);
                            updateActiveBusinessField('estilo_clean', newEstilo);
                            setStoredFeatureOverride(activeBusinessId, 'estilo_clean', newEstilo);
                            setStoredFeatureOverride(activeBusinessId, 'tono', newTono);
                            
                            await saveBusinessSettings({ tono: newTono, estilo: newEstilo, estilo_clean: newEstilo, tipo_negocio: tmpl.category });
                            showNotification("✅ ¡Reglas de Keyla IA actualizadas exitosamente! (Catálogo intacto)", "success");
                            setIsTemplatesModalOpen(false);
                          }}
                          className="w-full bg-seryi-bg-light hover:bg-indigo-600/20 text-seryi-heading border border-indigo-500/30 py-2.5 rounded-xl text-xs transition-all flex flex-col items-center justify-center gap-1 shadow-sm active:scale-95"
                        >
                          <span className="flex items-center gap-2 font-bold"><Sparkles size={14} /> 1. Solo Actualizar Reglas de Keyla IA</span>
                          <span className="text-[9px] text-slate-400 font-normal">Recomendado. No altera tus productos actuales.</span>
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            const currentServicesCount = (activeBusiness.services || []).length;
                            
                            if (currentServicesCount > 0) {
                              const confirmFullReplace = window.confirm(`⚠️ CONFIRMACIÓN FINAL: ¿Seguro que deseas ANEXAR los productos semilla de la plantilla al catálogo de ${currentServicesCount} productos actuales?`);
                              if (!confirmFullReplace) {
                                showNotification("Operación cancelada.", "info");
                                return;
                              }
                            }

                            if (tmpl.sampleServices && Array.isArray(tmpl.sampleServices)) {
                              const existingServices = activeBusiness.services || [];
                              const existingNames = new Set(existingServices.map(s => s.nombre.trim().toLowerCase()));
                              
                              const servicesToInsert = tmpl.sampleServices
                                .filter(s => !existingNames.has(s.nombre.trim().toLowerCase()))
                                .map((s, sIdx) => ({
                                  id: String(Date.now() + sIdx + 1),
                                  business_id: activeBusinessId,
                                  nombre: s.nombre,
                                  precio: s.precio,
                                  duracion_minutos: s.duracion_minutos || '30',
                                  activo: true
                                }));

                              const combinedServices = [...existingServices, ...servicesToInsert];
                              updateActiveBusinessField('services', combinedServices);

                              if (servicesToInsert.length > 0) {
                                try {
                                  await supabase.from('services').insert(servicesToInsert);
                                } catch(e) {
                                  console.warn("Aviso al anexar servicios:", e);
                                }
                              }
                            }
                            showNotification("✅ ¡Catálogo semilla cargado exitosamente!", "success");
                            setIsTemplatesModalOpen(false);
                          }}
                          className="w-full bg-seryi-bg-light hover:bg-orange-600/20 text-seryi-heading border border-orange-500/30 py-2.5 rounded-xl text-xs transition-all flex flex-col items-center justify-center gap-1 shadow-sm active:scale-95"
                        >
                          <span className="flex items-center gap-2 font-bold"><Sparkles size={14} /> 2. Solo Cargar Productos Semilla</span>
                          <span className="text-[9px] text-slate-400 font-normal">Carga 15 productos básicos del nicho (sin alterar reglas).</span>
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            const currentServicesCount = (activeBusiness.services || []).length;
                            
                            if (currentServicesCount > 0) {
                              const confirmFullReplace = window.confirm(`⚠️  CONFIRMACIÓN FINAL: ¿Seguro que deseas actualizar TODA la plantilla (Reglas + Productos)?`);
                              if (!confirmFullReplace) {
                                showNotification("Operación cancelada.", "info");
                                return;
                              }
                            }

                            const nombre = activeBusiness.nombre_negocio || 'Mi Negocio';
                            const newTono = tmpl.tono;
                            const newEstilo = typeof tmpl.getEstilo === 'function' ? tmpl.getEstilo(nombre) : tmpl.estiloText;

                            setEstiloTextDraft(newEstilo);
                            const el = document.getElementById('reglas-de-oro-textarea');
                            if (el) el.value = newEstilo;

                            updateActiveBusinessField('tipo_negocio', tmpl.category);
                            updateActiveBusinessField('tono', newTono);
                            updateActiveBusinessField('estilo', newEstilo);
                            updateActiveBusinessField('estilo_clean', newEstilo);
                            setStoredFeatureOverride(activeBusinessId, 'estilo_clean', newEstilo);
                            setStoredFeatureOverride(activeBusinessId, 'tono', newTono);
                            
                            if (tmpl.sampleServices && Array.isArray(tmpl.sampleServices)) {
                              const existingServices = activeBusiness.services || [];
                              const existingNames = new Set(existingServices.map(s => s.nombre.trim().toLowerCase()));
                              
                              const servicesToInsert = tmpl.sampleServices
                                .filter(s => !existingNames.has(s.nombre.trim().toLowerCase()))
                                .map((s, sIdx) => ({
                                  id: String(Date.now() + sIdx + 1),
                                  business_id: activeBusinessId,
                                  nombre: s.nombre,
                                  precio: s.precio,
                                  duracion_minutos: s.duracion_minutos || '30',
                                  activo: true
                                }));

                              const combinedServices = [...existingServices, ...servicesToInsert];
                              updateActiveBusinessField('services', combinedServices);

                              if (servicesToInsert.length > 0) {
                                try {
                                  await supabase.from('services').insert(servicesToInsert);
                                } catch(e) {
                                  console.warn("Aviso al anexar servicios:", e);
                                }
                              }
                            }

                            await saveBusinessSettings({ tono: newTono, estilo: newEstilo, estilo_clean: newEstilo, tipo_negocio: tmpl.category });
                            showNotification("✅ ¡Plantilla Completa Aplicada (IA + Productos)!", "success");
                            setIsTemplatesModalOpen(false);
                          }}
                          className="w-full bg-seryi-green hover:bg-seryi-green/90 text-seryi-bg-dark border border-seryi-green font-black py-2.5 rounded-xl text-xs transition-all flex flex-col items-center justify-center gap-1 shadow-lg shadow-seryi-green/20 active:scale-95"
                        >
                          <span className="flex items-center gap-2 font-bold"><Sparkles size={14} /> 3. Aplicar Plantilla Completa</span>
                          <span className="text-[9px] text-seryi-bg-dark/80 font-normal">Aplica Reglas de IA + Catálogo Semilla.</span>
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <button 
                onClick={() => setCurrentTemplateIndex(prev => (prev < activeTemplates.length - 1 ? prev + 1 : 0))}
                className="p-3 text-seryi-heading bg-seryi-bg-dark hover:bg-seryi-green/20 hover:text-seryi-green rounded-full border border-seryi-bg-light transition-all flex-shrink-0"
              >
                <ChevronRight size={24} />
              </button>
            </div>
            
            <div className="flex justify-center mt-6 gap-2 flex-wrap max-w-md mx-auto">
              {activeTemplates.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentTemplateIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${idx === currentTemplateIndex ? 'bg-seryi-green w-6' : 'bg-seryi-bg-light hover:bg-seryi-text'}`}
                />
              ))}
            </div>

          </div>
        </div>
      )}

      {/* CENTRO DE ALERTAS Y NOTIFICACIONES PUSH */}
      <NotificationCenter
        isOpen={isNotificationsOpen}
        onClose={() => setIsNotificationsOpen(false)}
        notifications={notifications}
        onMarkAllRead={handleMarkAllRead}
        onClearAll={handleClearAllNotifications}
        onNotificationClick={handleNotificationClick}
        onAddDemoNotification={handleAddNotification}
        activeBusiness={activeBusiness}
      />

      </div>
    </div>
  );
}

